/* ---------------------------------------------------------------------------
   Application state.

   One object, one subscribe callback, full re-render on change. The register is
   a couple of dozen rows, so there is no reason to reach for anything cleverer —
   and a single render path means the screens can be pure functions of state.

   The subset of state listed in PERSIST_KEYS survives a reload via localStorage;
   everything else (search text, collapsed sections) is deliberately transient.

   One action here talks to the network — hydrate. It never rejects: a Cloudflare
   backend that cannot be reached leaves the app on the bundled seed with
   `backend: 'unreachable'` and a note explaining why.
   --------------------------------------------------------------------------- */

import type {
  BackendState,
  BridgeChange,
  DefectQuantity,
  ElementCondition,
  InspectionState,
  RegisterDefect,
  ComponentNode,
  DetailView,
  DetailStackView,
  Inspection,
  InspectionType,
  ListVariant,
  MapBase,
  MapMode,
  MapPaint,
  ModelIndex,
  Persisted,
  ProgressEntry,
  ProgressState,
  ProgressSummary,
  SiteDocument,
  SiteIncident,
  SiteItem,
  SiteReturn,
  SiteTest,
  PlantDay,
  PeriodReport,
  TradeCount,
  Screen,
  Shell,
  Theme,
  Vec3,
} from '../data/types.ts';
import { LOCALES, setLocale, type Locale } from '../data/locale.ts';
import { defaultGroupBy, isGroupBy, type GroupBy } from '../data/summary.ts';
import { getRegister, resetRegister, setRegister, updateOne } from '../data/register.ts';
import { active, buildTenant, byId, requestedTenant, resolveTenant, setActive } from '../tenant/registry.ts';
import { findBridge, scaleMax } from '../data/derive.ts';
import {
  BRIDGE_ELEMENTS,
  CUSTOM_ELEMENT_MAX,
  customElementId,
  elementLabel,
  isKnownElement,
} from '../data/elements.ts';
import { activityLabel, activityUnit } from '../data/activities.ts';
import {
  addDays,
  periodFor,
  isIsoDay,
  type Period,
  type PeriodKind,
} from '../data/periods.ts';
import { incidentOption, isWeather } from '../data/siteReturn.ts';
import { testFamily, type ItemAct } from '../data/siteQuality.ts';
import { isInspectionTrigger, isInspectionType, needsTrigger } from '../data/inspectionTypes.ts';
import { baselineFrom, baselineLinks, proposeMatch, readProgramme, type SerialisedSchedule } from '../data/programme.ts';
import { clearDraft, readDraft } from '../data/programmeDraft.ts';
import { headerOf, type Baseline, type BaselineHeader, type ScheduleMatch } from '../data/variance.ts';
import { EVERY_DAY, calendarLines, checkCalendar, parseCalendarLines, workingDaysBetween, type WorkCalendar } from '../data/calendar.ts';
import { programmeFromExport, worksProgramme, type ImportedProgramme, type ProgrammeComponent } from '../data/importedProgramme.ts';
import {
  checkContract,
  milestoneLines,
  parseMilestones,
  type ContractExtension,
  type ContractHeader,
} from '../data/contract.ts';
import { componentChoices } from '../data/componentTree.ts';
import { DEFECT_CODES } from '../data/defects.ts';
/* TEMPORARY — demonstration data for FWO. Delete with src/data/fwo-demo.ts. */
import {
  demoDefectsFor,
  demoFilledCount,
  demoHandoverFor,
  demoOwnsPhase,
  demoPhaseFor,
  demoProgressFor,
  demoProgressInPeriod,
  demoScheduleFor,
  demoSiteRecordFor,
  summariseWithDemo,
  withDemoBridges,
} from '../data/fwo-demo.ts';
import { fieldsFor, isEmptyQuantity, validateQuantity } from '../data/quantities.ts';
import { DOR_BAND_MAX } from '../data/ratingGuide.ts';
import { parseRateRows, validateRateTable, type RateTable } from '../data/rates.ts';
import type { StoredWording } from '../data/remedial.ts';
import {
  can as roleAllows,
  isGrantableIdentity,
  normaliseIdentity,
  rolesLabel,
  type Capability,
  type Person,
  type Role,
} from '../data/roles.ts';
import { scaleSourceNote, type PhotoPoint } from '../data/photoScale.ts';
import {
  CAPABILITY_APP,
  WORKS_KIND_LABEL,
  accessLine,
  isWorksKind,
  phaseOf,
  phasePermits,
  viewPhaseOf,
  type PhaseAct,
  type PhaseState,
} from '../data/phase.ts';
import { HANDOVER_FIELDS } from '../data/handover.ts';
import { scaleOf, traceResult, traceableFields } from '../data/photoTrace.ts';
import { idForLabel, pathsToReveal } from '../data/componentTree.ts';
import { buildEditPayload, draftFrom } from '../data/editFields.ts';
import { appById, appForShell, owns, readsSiteRecord, setRunningApp, type AppId } from './apps.ts';
import { deskSendsHome, probeShell, shellOverride, shellScreen } from './shell.ts';
import { STORAGE_KEY } from './prefs.ts';
import { showInstallPrompt } from './install.ts';
import { recallModelIndex, rememberModelIndex } from './modelIndexCache.ts';
import * as api from './api.ts';
import * as queue from './queue.ts';
import type { Screen as RatingScreen } from '../rating/store.ts';
import type { QueuedWrite } from './queue.ts';

/**
 * A template chosen in the picker, on its way to the modeller's frame.
 *
 * Held in state rather than passed straight to the overlay because the frame's URL
 * is built during a render, and because the loading cover names the template while
 * the workspace is still coming down the wire.
 */
/**
 * How far along an import is, for the record to say so.
 *
 * Each phase fails differently and takes a different amount of time, which is
 * why they are named rather than summed into a percentage: reading the export is
 * disk, creating a model is one request, booting a hidden modeller is the 1.6 MB
 * chunk and the state fetch, applying is fast and is where a bad file is found
 * out, and saving is the autosave debounce plus the thumbnail capture and their
 * flushes to Cloudflare.
 *
 * `reading` and `creating` were added because they were the SILENT ones. The
 * record said nothing until `opening`, and everything before it — reading a
 * two-thirds-of-a-megabyte export off disk, and a network round trip to make a
 * model to import into — happened with the button still saying "Import 3D
 * model". On a slow link that is seconds of a screen that looks like it ignored
 * the click.
 *
 * `geometry` is the step that makes an imported bridge exist for anybody but the
 * device that imported it. The modeller keeps a cloud model's triangles in
 * INDEXEDDB (see the vendored io/MeshStore.ts — a mesh is 236 KB for one span and
 * 6.5 MB for an 18-span flyover, well past the whole localStorage quota), and the
 * shim in src/modeler-host/boot.ts syncs localStorage and nothing else. So the
 * mesh never left the browser it was imported in: the model state carried
 * `hasImportedMesh: true`, which puts the modeller into viewer mode with its
 * parametric branches dropped, and on any other machine there was no mesh to draw
 * in their place. The bridge came up missing its abutments, its columns and
 * everything else the source had drawn.
 *
 * Exporting the geometry to R2 straight after the import is what fixes that —
 * `Canvas3D.Add` puts imported parts under `canvas3d.root`, which is exactly what
 * `ExportGeometryToGlb` walks, so the .glb carries them.
 */
export type ModelImportPhase =
  | 'reading'
  | 'creating'
  /**
   * The export document going to the server, BEFORE anything is applied.
   *
   * First because it is the only step whose failure can be made harmless. An
   * imported model's triangles live in the importing browser's IndexedDB and
   * nowhere else, so a model saved without its source on the server is a bridge
   * that exists on one laptop — and that is the state this whole path exists to
   * prevent. Failing here costs a model shell nobody has written a bridge into;
   * failing after the save would leave exactly the broken model, with the import
   * reporting success.
   */
  | 'storing'
  | 'opening'
  | 'importing'
  | 'saving'
  | 'geometry';

/** A CSiBridge export on its way into a saved model. */
export interface ModelImport {
  bridgeId: string;
  modelId: string;
  /**
   * The model being imported into, or '' until one exists.
   *
   * Empty for the first import on a structure: the model is created a moment
   * later, and naming it before it exists would put a name on screen that the
   * server had not agreed to yet.
   */
  modelName: string;
  phase: ModelImportPhase;
  /**
   * True when this run is the app repairing itself rather than a person importing.
   *
   * A restore replays an export the server already holds, because this device has
   * no mesh for a model whose state says it displays one — see
   * restoreImportedModel. The frame work is identical, so it shares this state; what
   * it must not share is the WORDING. "Importing…" over a record nobody touched
   * reads as the app having decided to change something, and the two are worth
   * distinguishing on screen even though they are the same code underneath.
   */
  restoring?: boolean;
  /**
   * The phases THIS run will pass through, in order.
   *
   * Stored rather than derived because the sequence varies: an import onto a
   * structure that already has a model skips `creating`. Knowing the real
   * sequence is what lets the record say "step 2 of 4" and have it be true —
   * a fixed list would either count a step that never runs or jump over it.
   */
  steps: readonly ModelImportPhase[];
}

/** What an import did, once it is over. */
export interface ModelImportResult {
  bridgeId: string;
  /**
   * The model it went into.
   *
   * By id as well as name, because the summary has to look the model up in the
   * re-read index to find out whether the import made it a displayed mesh — and
   * two models on one structure can share a name. Empty where the import failed
   * before a model existed to go into.
   */
  modelId: string;
  modelName: string;
  ok: boolean;
  /** The imported bridge's own name, as the export called it. */
  name: string;
  applied: string[];
  warnings: string[];
  error: string | null;
  /**
   * The import applied but the save was not confirmed before the deadline.
   *
   * Not a failure: the modeller writes through the same shim as an ordinary
   * edit and a flush was forced on the way out, so it has almost certainly
   * landed. It means the register did not SEE it land, which is worth saying
   * rather than claiming a save it cannot vouch for.
   */
  saveUnconfirmed: boolean;
  /**
   * Why the geometry could not be stored, or null.
   *
   * Not an import failure — the model is saved either way. What is lost is the
   * register's own device-independent 3D view of it, which matters because the
   * modeller keeps an imported mesh in IndexedDB and so on that device only. The
   * summary says so rather than letting the bridge come up incomplete elsewhere
   * with nothing having mentioned it.
   */
  geometryError: string | null;
  /**
   * What the import wrote into the inventory record (LC-83), where it wrote
   * anything: the fields it filled, which were blank on the record, and the
   * ones the record already states differently, which it left alone.
   */
  inventory?: { filled: string[]; differs: string[]; error: string | null };
}

/** Where the network map is centred, and how far in. */
export interface MapView {
  lat: number;
  lng: number;
  /**
   * Fractional Web Mercator zoom. Fractional because the home view is a fit to
   * an arbitrary box rather than a power of two — see fitBounds in
   * src/map/mercator.ts, and the tile layer that floors it and scales the rest.
   */
  zoom: number;
}

/**
 * A structure's position and deck axis, as being dragged on the map.
 *
 * `bearing` is null for a structure that has never had one — Girubari, whose
 * crossing is not mapped in OSM. The rotate handle then starts it at the axis of
 * the road it is nearest to nothing, so it starts at 0 and is dragged from there;
 * until it is touched, no footprint is drawn.
 */
export interface MapPlacement {
  bridgeId: string;
  lat: number;
  lng: number;
  bearing: number | null;
  /** What the record held when the drag started, so Cancel is exact and Save can tell nothing changed. */
  from: { lat: number | null; lng: number | null; bearing: number | null };
}

/** Draft values for the "Add inspection" dialog, one string per field. */
export interface InspectionDraft {
  /** ISO date (YYYY-MM-DD) — what an `<input type="date">` holds. */
  date: string;
  type: InspectionType;
  /** A special or post-event inspection's trigger, or '' for none chosen (LC-60). */
  trigger: string;
  rating: string;
  scale: string;
  note: string;
  /**
   * Condition typed against each element, keyed by element id, as typed.
   *
   * Strings because that is what a number input holds and because "" has to
   * mean *not assessed* — distinct from 0, which is a real and bad rating on
   * every scale the sheet uses. `'N'` is DoR's own *not applicable, or element
   * not accessible for inspection*, which is a third thing again. Only the
   * filled ones become `ElementCondition` entries; see buildElements.
   */
  elements: Record<string, string>;
  /**
   * Extent typed against each element — DoR's second box, `0`–`4`.
   *
   * A parallel map rather than a field on a per-element object, because that is
   * the shape the dialog's controls actually write: one control changes one
   * element's one box, and `setElementBox` is a single-key update either way.
   * Three maps also keep an untouched box absent rather than present-and-empty,
   * which is the distinction buildElements turns into *not assessed*.
   */
  extents: Record<string, string>;
  /** Urgency typed against each element — DoR's third box, `0`–`4`. */
  urgencies: Record<string, string>;
  /**
   * Items the inspector added beyond the form's own, as `custom:` ids in the
   * order they were added. Rated in the same three maps as the form's items.
   */
  customElements: string[];

  /**
   * Photographs picked so far, already downscaled, as base64 data URLs.
   *
   * Held here rather than in the imageSlot module's map because these have to
   * survive as far as the POST — imageSlot is a local preview whose object URLs
   * die with the page, which is exactly the gap this item closes. They go up on
   * the same request as the entry itself; see api.addInspection.
   */
  photos: string[];
}

function emptyInspectionDraft(): InspectionDraft {
  return {
    date: new Date().toISOString().slice(0, 10),
    type: 'routine',
    trigger: '',
    rating: '',
    scale: '0-4',
    note: '',
    elements: {},
    extents: {},
    urgencies: {},
    customElements: [],
    photos: [],
  };
}

/**
 * How many photographs one inspection may carry.
 *
 * Mirrors MAX_PHOTOS_PER_INSPECTION in functions/lib/photos.ts, which is the one
 * that actually refuses. Here so the dialog stops offering a slot rather than
 * letting someone attach a ninth and discover on submit that the whole
 * inspection was rejected. A check in scripts/smoke.ts asserts the two agree.
 */
export const MAX_INSPECTION_PHOTOS = 8;

/** How many photographs one defect may carry. Mirrors MAX_PHOTOS_PER_DEFECT in functions/lib/defects.ts. */
export const MAX_DEFECT_PHOTOS = 8;

/**
 * Draft values for the "Log defect" dialog, one string per field.
 *
 * There was an `element` here too — the coarse DoR-inventory location, asked for
 * beside the component. The dialog stopped asking when every structure gained a
 * 3D model: the component is the more precise of the two, and two fields for one
 * fact got two answers that could disagree. The column remains and the rows that
 * carry one still show it; it is the inspection form that rates per element now.
 */
export interface DefectDraft {
  code: string;
  severity: string;
  /** Component label from the model's own tree, or '' for "could not say where". */
  component: string;
  /** The model's id for that component, or '' where the model gave none (LC-14). */
  componentId: string;
  loggedAt: string;
  /** The inspection that found it, by id, or '' for none named (LC-62). */
  inspectionId: string;
  note: string;
  /**
   * Photographs of the defect, already downscaled, as base64 data URLs, in the
   * order they were taken — empty, which is the normal case.
   *
   * ON THE DRAFT, not in a module-level map like the elevation slots in
   * src/components/imageSlot.ts. That was the finding the inspection dialog
   * turned up: a picture the submit path cannot reach is a picture that never
   * leaves the device, and object URLs die with the page either way.
   *
   * A list, like an inspection's `photos`, capped at the same number — see
   * MAX_DEFECT_PHOTOS. A defect is one finding in one place, but a close-up with
   * a gauge in frame and a wider shot showing which member it is on are both
   * the evidence for it. The first is the one the modeller shows — migration 0033.
   */
  photos: string[];
  /**
   * The measurements typed into the quantity boxes, keyed by `QuantityKey`.
   *
   * Strings, as typed, for the same reason the element ratings are: "" has to
   * mean *this box was not filled in*, and a number input holds a string
   * anyway. Which boxes are offered depends on the category — see
   * `fieldsFor` in src/data/quantities.ts — and switching category does NOT
   * clear this map. A width typed against CRK and then re-categorised to SPA is
   * simply not sent, because SPA does not take one; clearing it would lose the
   * number if the inspector switched back, which on a phone happens by accident.
   */
  quantity: Record<string, string>;
  /**
   * How the measurements were obtained. '' until the inspector says.
   *
   * Deliberately not defaulted to `measured`. A pre-selected method is a claim
   * the application makes on the inspector's behalf, and it is the one claim
   * this whole item exists to stop it making — see validateQuantity, which
   * refuses a measurement with no method rather than assuming the commonest one.
   */
  method: string;
  /** Tape, crack gauge, rod, level. Only meaningful where the method is `measured`. */
  instrument: string;
  /**
   * Where on the geometry, when the defect was placed by clicking the record's 3D
   * view — or null, which is the dialog's ordinary case.
   *
   * Carried on the draft rather than handed to the save separately because the
   * dialog is what opens after the click: the point is the one fact about this
   * defect that nothing on the form can re-enter, so it has to ride with the rest.
   * Picking a different component in the dialog drops it — see setDefectField.
   */
  pin: Vec3 | null;
  /** Surface normal at the pin. Meaningless without one, and dropped with it. */
  normal: Vec3 | null;
}

/**
 * What is being recorded on site, before it is sent.
 *
 * THE BRIEF IS THREE TAPS — element, activity, camera — and everything in this
 * shape past those three is optional and looks it on screen. A foreman standing
 * on a riverbank with one hand free is the person this draft exists for.
 *
 * Every field is a string for the reason `DefectDraft`'s are: '' has to mean
 * *this was not filled in*, distinct from a zero somebody typed, and an input
 * holds a string anyway.
 */
/**
 * The Take / Revise baseline form (LC-40, LC-42, LC-43): the baseline's name,
 * the reason for a revision, the contract's calendar, and the contract header.
 * Strings throughout, as every draft here is.
 */
export interface BaselineForm {
  name: string;
  /** On a bridge built with no works open, the works the baseline programmes (LC-81). */
  worksKind: string;
  reason: string;
  calendarId: string;
  contractor: string;
  contractNo: string;
  value: string;
  commencement: string;
  completionDays: string;
  /** One per line: `2026-08-15 Substructure complete`. */
  milestones: string;
}

/** A working calendar as the administrator edits it (LC-43). */
export interface CalendarDraft {
  /** Null for a new calendar. */
  id: string | null;
  name: string;
  week: string;
  /** One per line: `2026-12-25 Christmas`. */
  holidays: string;
  /** One per line: `2026-07-01 2026-08-31 Monsoon`. */
  windows: string;
}

export interface ProgressDraft {
  /** Activity id from src/data/activities.ts. '' until one is chosen. */
  activity: string;
  /** Element id from src/data/elements.ts, or '' for "did not say". */
  element: string;
  /** Component label from the model's own tree, or ''. */
  component: string;
  /** The model's id for that component, or '' where the model gave none (LC-14). */
  componentId: string;
  /**
   * How much, as typed. Only offered where the library gives the activity a
   * unit, and optional even then: a crew that poured "the north half of the
   * bent cap" and did not measure it should still be able to say the pour
   * happened.
   *
   * THE UNIT IS NOT IN THE DRAFT. It is the library's for the activity and the
   * endpoint looks it up; carrying a copy here would be a second answer to a
   * question that has one.
   */
  quantity: string;
  note: string;
  /**
   * ISO date the work happened.
   *
   * DEFAULTS TO TODAY AND IS EDITABLE, which is not a nicety. The whole reason
   * this application queues is that a site has no signal; a pour on Tuesday
   * reaching the register on Thursday is the normal case, and a form that
   * stamped the send time would misdate most of the record.
   */
  occurredOn: string;
  /** One photograph, already downscaled, as a base64 data URL. */
  photo: string | null;
}

function emptyProgressDraft(): ProgressDraft {
  return {
    /* NOTHING PRE-SELECTED. The library's first entry is "Setting out", and a
       form that arrives with it chosen is a form that records setting out every
       time somebody taps save too quickly. Same reasoning as the defect
       dialog's measurement method, and the save path refuses an empty one. */
    activity: '',
    element: '',
    component: '',
    componentId: '',
    quantity: '',
    note: '',
    occurredOn: new Date().toISOString().slice(0, 10),
    photo: null,
  };
}

/**
 * A day's return, before it is sent.
 *
 * Strings throughout, as every other draft here is: '' has to mean *this box
 * was not filled in*, which for the two numbers is genuinely different from
 * zero. A site with nobody on it is an idle day and is one of the most useful
 * rows in the book; a return that did not count heads is not making that
 * claim.
 */
export interface ReturnDraft {
  /** The day this is about. Defaults to today and is editable, as a pour's is. */
  returnedOn: string;
  /** An id from WEATHER in src/data/siteReturn.ts; '' until somebody picks one. */
  weather: string;
  labour: string;
  labourNote: string;
  plant: string;
  hours: string;
  note: string;
  /** People by trade, as typed (LC-56). Where there are any, the total is their sum. */
  trades: { trade: string; count: string }[];
  /** Each item of plant with its hours working and idle, as typed (LC-56). */
  plantRows: { plant: string; working: string; idle: string }[];
  /** Rain in millimetres, as typed. Empty is not measured. */
  rainfall: string;
  /** One photograph of the day, downscaled, as a data URL — or null. */
  photo: string | null;
}

/** A day's return as the form holds it, for correcting it. */
function draftFromReturn(r: SiteReturn): ReturnDraft {
  return {
    returnedOn: r.returnedOn,
    weather: r.weather,
    labour: r.labour === null ? '' : String(r.labour),
    labourNote: r.labourNote,
    plant: r.plant,
    hours: r.hours === null ? '' : String(r.hours),
    note: r.note,
    trades: (r.labourByTrade ?? []).map((t) => ({ trade: t.trade, count: String(t.count) })),
    plantRows: (r.plantRows ?? []).map((p) => ({
      plant: p.plant,
      working: p.workingHours === null ? '' : String(p.workingHours),
      idle: p.idleHours === null ? '' : String(p.idleHours),
    })),
    rainfall: r.rainfallMm == null ? '' : String(r.rainfallMm),
    /* The stored photograph stays where it is; the form holds only a new one. */
    photo: null,
  };
}

/**
 * A typed number that may legitimately be absent.
 *
 * Three answers, not two: the number, `null` for an empty box, and `'bad'` for
 * something that is not a number at all. Returning null for both would send a
 * typing slip to the register as "nobody counted", which is a different and
 * quite wrong claim about the day.
 */
function optionalCount(raw: string): number | null | 'bad' {
  const text = raw.trim();
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return 'bad';
  return n;
}

function emptyReturnDraft(): ReturnDraft {
  return {
    returnedOn: new Date().toISOString().slice(0, 10),
    /* NOTHING, and the return is not recorded until somebody picks. Not
       `clear`: a default would let a return written for the labour figures
       claim the weather was fine without anybody looking at the sky — and that
       claim is the evidence an extension-of-time argument is tested against.
       Whoever writes the day's return is on site and says what it did. */
    weather: '',
    labour: '',
    labourNote: '',
    plant: '',
    hours: '',
    note: '',
    trades: [],
    plantRows: [],
    rainfall: '',
    photo: null,
  };
}

/** An incident, before it is reported. */
export interface IncidentDraft {
  /** An id from INCIDENT_KINDS. '' until somebody says which. */
  kind: string;
  occurredOn: string;
  element: string;
  /** What happened. The one field the endpoint will not accept empty. */
  description: string;
  action: string;
  /** Who it involved and the days lost, as typed (LC-56). Empty is not recorded. */
  peopleInvolved: string;
  peopleNames: string;
  daysLost: string;
  /** One photograph, downscaled, as a data URL — or null. */
  photo: string | null;
}

/** Opening one of the site-record forms closes the others: one sheet at a time. */
const ONE_SITE_FORM = {
  progressBridgeId: null,
  returnBridgeId: null,
  incidentBridgeId: null,
} as const;

function emptyIncidentDraft(): IncidentDraft {
  return {
    /* Nothing pre-selected, for the reason the activity picker has nothing
       pre-selected — and more sharply here, because the default would be the
       first entry in the list and the first entry is "Near miss". */
    kind: '',
    occurredOn: new Date().toISOString().slice(0, 10),
    element: '',
    description: '',
    action: '',
    peopleInvolved: '',
    peopleNames: '',
    daysLost: '',
    photo: null,
  };
}

/** A document, before it is filed. */
export interface DocumentDraft {
  kind: string;
  title: string;
  /** The date on the document, which is not today. Often genuinely absent. */
  datedOn: string;
  note: string;
  /** The progress entry this evidences, or '' for none. */
  eventId: string;
  /** The file itself, as a base64 data URL, and its name as chosen. */
  file: string | null;
  filename: string;
  /** Size of the decoded file, for the sheet to print before it is sent. */
  bytes: number;
}

function emptyDocumentDraft(): DocumentDraft {
  return {
    kind: '',
    title: '',
    datedOn: '',
    note: '',
    eventId: '',
    file: null,
    filename: '',
    bytes: 0,
  };
}

/**
 * A test result, before it is recorded (LC-53). Every figure a kind could need,
 * as typed; the kind decides which are sent. `component` is a choice key — see
 * `splitChoice`.
 */
export interface TestDraft {
  kind: string;
  testedOn: string;
  eventId: string;
  component: string;
  documentId: string;
  mix: string;
  ageDays: string;
  strengthMpa: string;
  requiredMpa: string;
  method: string;
  resultClass: string;
  testLoadKn: string;
  settlementMm: string;
  allowedMm: string;
  lot: string;
  grade: string;
  /** A certificate's stated compliance: 'yes', 'no', or '' for not yet said. */
  complies: string;
  note: string;
}

function emptyTestDraft(): TestDraft {
  return {
    kind: '',
    testedOn: new Date().toISOString().slice(0, 10),
    eventId: '',
    component: '',
    documentId: '',
    mix: '',
    ageDays: '28',
    strengthMpa: '',
    requiredMpa: '',
    method: '',
    resultClass: '',
    testLoadKn: '',
    settlementMm: '',
    allowedMm: '',
    lot: '',
    grade: '',
    complies: '',
    note: '',
  };
}

/** What a missing figure is called when the form asks for it. */
const TEST_FIELD_WORDS: Readonly<Record<string, string>> = {
  mix: 'mix or grade',
  ageDays: 'age of the specimens in days',
  strengthMpa: 'strength in MPa',
  requiredMpa: 'strength required in MPa',
  method: 'method',
  resultClass: 'result class',
  testLoadKn: 'test load in kN',
  settlementMm: 'settlement in mm',
  allowedMm: 'settlement allowed in mm',
  lot: 'heat or lot',
  grade: 'grade',
  complies: 'certificate’s statement of compliance',
};

/** A hold point, NCR or RFI, before it is raised (LC-54). */
export interface ItemDraft {
  kind: string;
  title: string;
  detail: string;
  owner: string;
  dueOn: string;
  /** The baseline activity a hold point holds, or '' for none. */
  activityId: string;
  /** A choice key, as the test's. */
  component: string;
  eventId: string;
}

function emptyItemDraft(kind = ''): ItemDraft {
  return { kind, title: '', detail: '', owner: '', dueOn: '', activityId: '', component: '', eventId: '' };
}

/**
 * A component picker's value: the id and the label joined by a control
 * character, or the label alone where the model has no id — and back.
 */
export function choiceKey(component: string | null, componentId: string | null): string {
  if (!component) return '';
  return componentId ? `${componentId}\u0001${component}` : component;
}

export function splitChoice(key: string): { component: string | null; componentId: string | null } {
  if (key === '') return { component: null, componentId: null };
  const at = key.indexOf('\u0001');
  if (at < 0) return { component: key, componentId: null };
  return { component: key.slice(at + 1), componentId: key.slice(0, at) || null };
}

function emptyDefectDraft(): DefectDraft {
  return {
    code: DEFECT_CODES[0]?.code ?? 'CRK',
    severity: 'Moderate',
    component: '',
    componentId: '',
    loggedAt: new Date().toISOString().slice(0, 10),
    inspectionId: '',
    note: '',
    photos: [],
    quantity: {},
    method: '',
    instrument: '',
    pin: null,
    normal: null,
  };
}

/**
 * The draft's measurements as a `DefectQuantity`, or null where there are none.
 *
 * Throws a message fit for the dialog, the same way buildInspection does, and
 * refuses through the SAME function the API refuses through — `validateQuantity`
 * — so the two cannot come to different conclusions about what a valid
 * measurement is.
 *
 * Null rather than `{ method: 'not-measured' }` for an untouched form. A defect
 * nobody measured should be byte-identical to one recorded before this form had
 * the boxes, which means no `quantity` key on the wire at all.
 */
export function buildDefectQuantity(d: DefectDraft): DefectQuantity | null {
  const typed: DefectQuantity = { method: 'not-measured' };
  let any = false;

  for (const field of fieldsFor(d.code)) {
    const raw = (d.quantity[field.key] ?? '').trim();
    if (raw === '') continue;
    const v = Number(raw);
    if (!Number.isFinite(v)) throw new Error(`"${field.label}" must be a number.`);
    (typed as unknown as Record<string, unknown>)[field.key] = v;
    any = true;
  }

  const method = d.method.trim();
  const instrument = d.instrument.trim();

  /* Nothing typed and nothing said: no quantity. This is the ordinary case and
     it must not become a record claiming the inspector declared anything. */
  if (!any && method === '' && instrument === '') return null;

  if (method === '') {
    throw new Error(
      'Say how the measurement was obtained — measured, from the model, estimated, or not measured.',
    );
  }
  typed.method = method as DefectQuantity['method'];
  if (instrument) typed.instrument = instrument;

  const fault = validateQuantity(d.code, typed);
  if (fault) throw new Error(fault);

  return isEmptyQuantity(typed) ? null : typed;
}

/** Must match SCALE_OPTIONS in src/screens/inspectionDialog.ts, the only place that offers a choice. */
const SCALE_OPTIONS: readonly string[] = ['0-4', '0-10', '0-100'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The first day a to-date total reads from: before any record this register holds. */
const FIRST_DAY = '1900-01-01';

/** Draft strings back into an Inspection, or throws a message fit for the dialog. */
function buildInspection(draft: InspectionDraft): Inspection {
  const date = draft.date.trim();
  if (!ISO_DATE.test(date) || Number.isNaN(Date.parse(date))) throw new Error('"Date" is required.');

  if (!isInspectionType(draft.type)) throw new Error('"Type" is required.');
  /* A special or post-event inspection names what caused it (LC-60). */
  const trigger = needsTrigger(draft.type) ? draft.trigger : '';
  if (needsTrigger(draft.type) && !isInspectionTrigger(trigger)) throw new Error('"Trigger" is required.');

  if (!SCALE_OPTIONS.includes(draft.scale)) throw new Error('"Scale" is required.');

  const rating = Number(draft.rating.trim());
  if (!Number.isFinite(rating) || rating < 0) throw new Error('"Rating" must be a number, zero or greater.');

  const scale = draft.scale;

  const note = draft.note.trim();
  if (note === '') throw new Error('"Note" is required.');

  const elements = buildElements(draft, scale);

  return {
    date,
    type: draft.type,
    ...(isInspectionTrigger(trigger) ? { trigger } : {}),
    rating,
    scale,
    note,
    // Absent rather than empty, so a visit that recorded only an overall
    // judgement is byte-identical to one recorded before elements existed.
    ...(elements.length ? { elements } : {}),
  };
}

/**
 * The element ratings that were actually filled in, on the inspection's scale.
 *
 * A blank is skipped rather than sent as zero: an element nobody looked at has
 * no entry, which is a different and more useful claim than a bottom score. The
 * ceiling is the inspection's own scale, so a 7 typed against a 0-4 sheet is
 * caught here rather than being stored and drawn as seven quarters of a star.
 */
function buildElements(draft: InspectionDraft, scale: string): ElementCondition[] {
  const max = scaleMax(scale);
  const out: ElementCondition[] = [];

  const custom = draft.customElements.map((id) => ({ id, label: elementLabel(id) }));
  for (const element of [...BRIDGE_ELEMENTS, ...custom]) {
    const typed = (draft.elements[element.id] ?? '').trim();
    const extent = (draft.extents[element.id] ?? '').trim();
    const urgency = (draft.urgencies[element.id] ?? '').trim();

    /* An element with no condition and nothing else typed was not assessed and
       has no entry. But extent or urgency typed against no condition is a
       half-filled row, and storing it would put a "5 to 20% of something"
       against an element the same visit says nothing about. Refused rather than
       dropped: dropping it loses what somebody typed without telling them. */
    if (typed === '') {
      if (extent !== '' || urgency !== '') {
        throw new Error(
          `"${element.label}" has an extent or urgency but no condition. Rate the condition, or clear the other boxes.`,
        );
      }
      continue;
    }

    /* DoR's own value, and its own rule: where the element is not applicable or
       could not be reached, "the extent and urgency ratings should be left
       blank". Not zeroed — a zero extent is a positive claim that nothing is
       wrong with an element nobody could see. */
    if (typed.toUpperCase() === 'N') {
      if (extent !== '' || urgency !== '') {
        throw new Error(
          `"${element.label}" is marked N — not applicable or not accessible — so extent and urgency must be left blank.`,
        );
      }
      out.push({ element: element.id, rating: 'N' });
      continue;
    }

    const rating = Number(typed);
    if (!Number.isFinite(rating) || rating < 0) {
      throw new Error(`"${element.label}" must be a number, zero or greater, or N.`);
    }
    if (max !== null && rating > max) {
      throw new Error(`"${element.label}" is above the ${scale} scale this inspection uses.`);
    }

    out.push({
      element: element.id,
      rating,
      ...band(extent, `${element.label} extent`),
      ...band(urgency, `${element.label} urgency`, 'urgency'),
    });
  }

  return out;
}

/**
 * One of DoR's fixed 0–4 boxes, as a spreadable fragment.
 *
 * Fixed at 0–4 whatever scale the inspection's condition is on. Extent and
 * urgency have one published definition each and DoR has never issued another,
 * so a 0-100 condition sheet does not get a 0-100 extent — it gets the same five
 * bands everything else does. Scaling them would invent values nobody defined.
 */
function band(typed: string, label: string, key: 'extent' | 'urgency' = 'extent'): Partial<ElementCondition> {
  if (typed === '') return {};
  const v = Number(typed);
  if (!Number.isInteger(v) || v < 0 || v > DOR_BAND_MAX) {
    throw new Error(`"${label}" must be a whole number from 0 to ${DOR_BAND_MAX}.`);
  }
  return { [key]: v };
}

/**
 * What the photo-measuring assist is holding — `QC-08`.
 *
 * The two phases are separate fields rather than a discriminated union because
 * going back to the scale must not discard the trace: an inspector who realises
 * the reference was marked wrong should be able to fix it and keep the outline
 * they spent a minute tapping out.
 */
export interface MeasureState {
  /** The photograph being measured, as the base64 data URL on the defect draft. */
  photo: string;
  /** Its natural pixel dimensions. Every distance is computed in this frame. */
  width: number;
  height: number;
  /** The defect category, which decides what can be measured. */
  code: string;
  phase: 'scale' | 'trace';
  /** The two ends of the reference of known size. */
  scalePoints: PhotoPoint[];
  /** How long that reference is, in millimetres, as typed. */
  referenceMm: string;
  /** Which measurement is being taken — a `QuantityKey`. */
  target: string | null;
  /** The trace. A path for a length, a polygon for an area, two ends for a width. */
  points: PhotoPoint[];
}

/** The district rates form, one string per field — `QC-06`. */
export interface RatesDraft {
  district: string;
  /** Bikram Sambat, as written on the approval. Never computed — see src/data/rates.ts. */
  fiscalYear: string;
  approvedOn: string;
  approvedBy: string;
  contingencyPct: string;
  vatPct: string;
  /** The rates, as pasted out of a spreadsheet. */
  rows: string;
}

/**
 * The grant being typed on the People screen.
 *
 * Strings for the text inputs, like every other draft in this file; the roles
 * and the suspension are the checkboxes' own values.
 */
export interface PersonDraft {
  email: string;
  roles: Role[];
  suspended: boolean;
  fullName: string;
  division: string;
  note: string;
}

function emptyPersonDraft(): PersonDraft {
  /* Seeded with no roles rather than with any. A form that opens with a box
     ticked grants that role to anybody who fills in an address and presses
     Save without reading it. */
  return { email: '', roles: [], suspended: false, fullName: '', division: '', note: '' };
}

/** The rewording being typed in the recommended-work dialog. */
export interface TreatmentEdit {
  id: string;
  label: string;
  note: string;
}

function emptyRatesDraft(): RatesDraft {
  return {
    district: '',
    fiscalYear: '',
    approvedOn: '',
    approvedBy: '',
    contingencyPct: '',
    vatPct: '',
    rows: '',
  };
}

/** The desk's flyouts: the title bar's two, the ribbon's mode, the register's filters. */
export type MenuId = 'account' | 'settings' | 'ribbon' | 'district' | 'road' | 'defect-code';

/** How Monitoring's progress photographs are laid out: by month, along the days, or as a grid. */
export type PhotoLayout = 'calendar' | 'timeline' | 'grid';

/** How Monitoring's daily returns are laid out: a month of skies, or the table. */
export type ReturnLayout = 'calendar' | 'list';
export type WorksLayout = 'table' | 'calendar';

export interface State extends Persisted {
  screen: Screen;
  /**
   * The app on screen: the start page (`home`) or one of the four products.
   * Mirrors `running` in src/core/apps.ts, which is what `currentApp()` reads —
   * this copy is what makes a change of app repaint and reach the address bar.
   * Set only by `actions.openApp` and the router, which move both together.
   */
  app: AppId;
  /**
   * Which ribbon tab is showing when it is not the app's own: `view` is the
   * View tab, which carries the theme, the ribbon's own layout and the
   * language, and belongs to no app; `help` is the Help tab, which carries the
   * guide. Not persisted and not in the address — a ribbon tab is a place to
   * find a command, not a place in the register.
   */
  ribbonTab: 'app' | 'view' | 'help';
  /**
   * The desk's one open flyout, or null — see src/components/flyout.ts. Not
   * persisted and not in the address: a menu is open for one decision.
   */
  menu: MenuId | null;

  /* backend */
  backend: BackendState;
  /** Human-readable reason the backend is unreachable, if it is. */
  backendNote: string;
  /**
   * Who Cloudflare Access says is signed in, from /api/health.
   *
   * Null both before the check answers and where the deployment does not enforce
   * Access at all — the header distinguishes the two using `backend`.
   */
  identity: api.Identity | null;

  /**
   * Whether this browser still has a session the register accepts.
   *
   * `ok` until something says otherwise, and `lost` once a call has been refused
   * with `reason: 'access'` — no Access session, or an expired one.
   *
   * IT IS NOT INFERRED FROM A FAILED REQUEST, and that is the whole care in this
   * field. A device with no signal fails every call too, and an application that
   * threw up a sign-in screen for that would be worse than useless in a gorge:
   * signing in is the one thing you cannot do with no connection. Only the
   * server saying so sets this — see onSessionLost in src/core/api.ts.
   */
  session: 'ok' | 'lost';
  /** Where to send somebody to sign in, from the denial. Null falls back to the site root. */
  loginUrl: string | null;
  /** What the register said when it refused. Shown on the sign-in screen. */
  sessionNote: string | null;

  /* --------------------------- the sign-in form --------------------------- */

  /** What is typed into the username and password boxes. */
  signInUser: string;
  signInPassword: string;
  signInBusy: boolean;
  /** Whether the password box shows what is typed. Off again once it has been used. */
  signInReveal: boolean;
  /** Why the last attempt was refused, in the register's own words. */
  signInError: string | null;

  /**
   * True where this browser is signed in with a password an administrator
   * issued, and may do nothing else until it is replaced.
   *
   * ITS OWN STATE RATHER THAN `session: 'lost'`, because it is the opposite
   * situation: that one means the register does not know who you are, this one
   * means it knows exactly and is waiting for one specific act. Showing the
   * sign-in screen for it would ask somebody to do the thing they have just
   * done. Enforced by the server either way — see functions/lib/sessionGuard.ts.
   */
  mustChangePassword: boolean;
  /** The change-password form, used for the forced change and the voluntary one. */
  passwordCurrent: string;
  passwordNext: string;
  passwordBusy: boolean;
  passwordError: string | null;
  passwordDone: string | null;

  /**
   * A password an administrator has just issued, shown once.
   *
   * Held only until they close it. The register keeps a hash and cannot show it
   * again, which the panel says in as many words — an administrator who assumes
   * they can come back for it has the wrong model of what was stored.
   */
  issuedPassword: { email: string; password: string; note: string } | null;

  /* register */
  q: string;
  district: string;
  /** The carrying road, by its route code — `ALL_ROADS` for every road. */
  road: string;
  sortKey: string;
  sortDir: 'asc' | 'desc';
  /**
   * Bumped whenever the register's records change under src/data/register.ts.
   *
   * The records themselves are not in this object — every screen reads them
   * through getRegister() — so adopting an edited bridge or a new inspection
   * moves nothing here, and a render pass comparing state to state would
   * conclude that nothing needs redrawing. src/main.ts does exactly that in
   * order to leave the screen alone while a dialog is being typed into, so the
   * one thing those dialogs change on the way out has to be visible to it.
   */
  registerRevision: number;
  /**
   * Rows ticked on the register, for its bulk action: recording bridges
   * already built as in service (LC-80). Cleared when the action is done.
   */
  registerPicked: string[];
  /** What the last bulk action did, or why it failed. */
  registerNote: string | null;

  /* detail — which inventory-sheet sections are collapsed */
  collapsedGroups: string[];

  /* detail — editing the inventory record */
  /**
   * Bridge whose edit dialog is open, or null when it is closed. The empty
   * string while `editCreating`: the dialog is open on a record that does not
   * exist yet, and its asset id is the draft's `id`.
   */
  editingId: string | null;
  /** The dialog is New bridge — a blank record, sent as a POST — not an edit. */
  editCreating: boolean;
  /** Draft field values, keyed by Bridge field name, one string per input. */
  editDraft: Record<string, string>;
  /** An edit is on its way to the backend. */
  editSaving: boolean;
  /** Why the last save attempt failed, or null. */
  editError: string | null;
  /**
   * What *Fill from model* put in the draft (LC-83): the model, and each value
   * as it filled it — so the form can mark the ones still the model's, and the
   * save can name the model. Null where nothing was filled.
   */
  editFill: { modelId: string; values: Record<string, string>; notes: string[] } | null;
  /** Fields with a dropdown that are being typed instead — "Other…" was picked. */
  editOther: string[];

  /**
   * Defect whose photograph is open full size, or null when none is.
   *
   * The id alone: the photo belongs to a defect on `selectedId`, and the bytes are
   * fetched by the browser from the same URL the thumbnail used — so opening one is
   * not a load, and the enlarged view needs nothing the record does not have.
   */
  photoDefectId: number | null;
  /** Which of that defect's photographs, from 1 — see `RegisterDefect.photoCount`. */
  photoDefectN: number;

  /**
   * Defect whose details the record's Defects page is showing in place of the
   * list, or null for the list — set by clicking its pin on the 3D model or its
   * row. The register's own id; a defect not on the selected structure is ignored.
   */
  defectFocusId: string | null;
  /** The tab under the model on the record's Defects page. */
  defectsTab: 'defects' | 'photos';
  /** Text the Inspection tab's table is narrowed to; '' for every row. */
  inspectionFilter: string;
  /** Whether the Inspection tab lists only the components with something logged on them. */
  inspectedOnly: boolean;

  /**
   * The open defect's history (LC-62): its status moves and every later visit
   * that saw it, fetched when the defect is opened. Null until asked for.
   */
  defectHistory: { id: string; history: api.DefectHistory | null; error: string | null } | null;
  /**
   * A status move being written on the open defect: a repair's date and
   * reference, a verification, or a reopen's reason. Null when none is open.
   */
  defectMove: { id: string; to: string; note: string; repairedOn: string; repairRef: string } | null;
  defectMoveError: string | null;
  /** A later inspection seeing the open defect again, being written (LC-62). */
  defectSight: {
    id: string;
    inspectionId: string;
    severity: string;
    /** Figures as typed, by measurement key. */
    values: Record<string, string>;
    note: string;
  } | null;

  /**
   * Photograph from an inspection currently enlarged, or null.
   *
   * Separate from photoDefectId rather than one "open photo" field, because the
   * two are looked up in different places — a defect photograph comes out of the
   * modeller's log for the selected structure, an inspection photograph out of
   * the register's own history — and a single id would not say which.
   */
  inspectionPhotoId: string | null;

  /** Monitoring's progress photographs: how they are laid out, and in what order. */
  photoLayout: PhotoLayout;
  photoNewestFirst: boolean;
  /** The month the photographs' calendar is on, as `yyyy-mm`; null for the latest with a photograph. */
  photoMonth: string | null;
  /** The construction entry whose photograph is enlarged, or null. */
  progressPhotoId: string | null;

  /** Monitoring's daily returns: as a calendar or a table, and the calendar's month (`yyyy-mm`; null for the latest). */
  returnLayout: ReturnLayout;
  returnMonth: string | null;

  /** Monitoring's works: as a table or a calendar, and the calendar's month (`yyyy-mm`; null for the latest). */
  worksLayout: WorksLayout;
  worksMonth: string | null;
  /** The works entry open in its dialog, with the model, or null. */
  worksEntryId: string | null;

  /* detail — adding an inspection history entry */
  /** Bridge whose "Add inspection" dialog is open, or null when it is closed. */
  addingInspectionId: string | null;
  inspectionDraft: InspectionDraft;
  /** An inspection is on its way to the backend. */
  inspectionSaving: boolean;
  /** Why the last save attempt failed, or null. */
  inspectionError: string | null;

  /* ---- the 3D model ----

     THE MODELLER IS NOT A DESTINATION and has no state here any more. There used
     to be eight fields: which bridge the template picker was open on, which model
     the full-screen frame was showing, whether it had booted, whether it was
     dirty, when it last saved, why it last failed. All of it described a modeller
     the user was looking at.

     Nobody looks at it now. A model is only ever imported, and an imported model
     is displayed rather than opened — so the vendored app is two pieces of
     machinery and neither is a screen: the parser the import runs in a hidden
     frame (src/modeler/importModel.ts), and the canvas the record renders its
     model in (src/modeler/viewport.ts). Each owns its own frame and its own
     lifecycle, and `modelImport` below is what the record shows while the first
     of them is working. */
  /** Models indexed against `selectedId`, read back from D1. */
  models: ModelIndex;
  /**
   * Whether that index has actually been read.
   *
   * THREE STATES AND NOT A BOOLEAN, and the reason is the one `progressRead`
   * gives below: an empty index means either "nothing has been modelled here" or
   * "this device could not reach the register", and the record drew the first of
   * those for both. It was not a cosmetic difference. A failed read replaced a
   * good index with an empty one, `heroModel` then returned null, and
   * src/modeler/meshView.ts tore down a canvas that was drawing the bridge
   * correctly — so a structure with a model in the bucket showed it for two
   * seconds and then reverted to "No 3D model built". The read that did the
   * damage was a second one, arriving after the first had succeeded.
   *
   * So a failure now says so and CHANGES NOTHING ELSE: whatever index is held is
   * still the last thing the register actually said, and it goes on being drawn.
   */
  modelsRead: 'unknown' | 'read' | 'unreachable';
  /**
   * Whether the index in hand came off THIS DEVICE rather than off the register.
   *
   * src/core/modelIndexCache.ts remembers the last index the register gave for a
   * structure, and the selection seeds the store with it so the record's 3D view
   * can mount on the geometry already in IndexedDB instead of waiting out the
   * slowest read the record makes. That index is the truth as of the last time
   * this device heard any — which is worth drawing and worth saying.
   *
   * Cleared by a successful read, and NOT by a failed one: a read that never
   * arrived has not made what is on screen any less remembered. So the pair to
   * read is this and `modelsRead` — remembered and 'unknown' is the ordinary
   * second before the answer lands, remembered and 'unreachable' is the state the
   * model card has to be honest about.
   */
  modelsRemembered: boolean;
  /**
   * Bridge whose "Delete 3D model" confirmation is open, or null when it is
   * closed. See src/modeler/deleteDialog.ts.
   *
   * Always the selected structure — the dialog is only reachable from that
   * structure's record, and the re-read afterwards goes through refreshModels,
   * which reads `selectedId`.
   */
  modelDeleteBridgeId: string | null;
  /**
   * A delete is in flight. Removing a model is several round trips per model and
   * cannot be undone, so the dialog is neither dismissible nor re-clickable while
   * this is set.
   */
  modelDeleteBusy: boolean;
  /** Why the last delete failed, or null. */
  modelDeleteError: string | null;
  /**
   * A CSiBridge export being imported into a model, or null.
   *
   * The import runs the modeller in a frame the user never sees — nothing out
   * here can apply a CSiBridge export or serialise the modeller's state, so the
   * modeller has to do both, but the point of the action is that it happens on
   * the record rather than by sending the engineer into the workspace. See
   * src/modeler/importModel.ts.
   *
   * While it is set the record shows the import's progress in place of the live
   * viewport, and the viewport is deliberately NOT mounted: two frames on one
   * model both write `bim_modeler_model_state_<id>`, and the idle one's autosave
   * would put the pre-import state back over the imported one.
   */
  modelImport: ModelImport | null;
  /**
   * What the last import did, for the summary dialog, or null when it is closed.
   *
   * Kept after the import finishes because the warnings are the part that
   * matters: every shipped bundle approximates or drops something, and an
   * import that says nothing looks like one that took everything.
   */
  modelImportResult: ModelImportResult | null;
  /**
   * Bumped whenever a model's thumbnail is known to have changed under it.
   *
   * The thumbnail URL is keyed by bridge and model, so it is the same string
   * before and after an import and the browser has every right to reuse the
   * image it already decoded. This rides along in the query string to say
   * "that one is stale" — see api.modelThumbUrl.
   */
  modelThumbNonce: number;
  /**
   * Whether THIS browser holds the triangles for one imported model.
   *
   * TRI-STATE ON PURPOSE, and the missing third state was a bug. `null` is "not
   * asked yet" and it is not the same as "no": the check is an IndexedDB round trip
   * and the viewport is mounted from a paint, so for the first frames after a
   * record opens the honest answer is that nobody knows.
   *
   * WHY IT HAS TO BE KNOWN BEFORE THE VIEWPORT MOUNTS. The modeller does not fail
   * visibly when the mesh is absent. `App.LoadImportedCloudMesh` sets
   * `importedCloudMesh = null` and REBUILDS THE BRIDGE FROM ITS IMPORTED
   * PARAMETERS — its own alert says the result "will not match the original
   * exactly" — and that alert never arrives, because the frame is sandboxed without
   * `allow-modals`. So the failure mode is not an empty box that reads as broken; it
   * is a plausible, wrong bridge drawn over the correct still, with nothing on
   * screen saying it is an approximation. Mounting the frame before this is known
   * therefore guarantees showing the wrong bridge first, every time.
   *
   * The index cannot answer it. `imported: true` says the model DISPLAYS an imported
   * mesh; it says nothing about whether the triangles are on this device, because
   * they live in this origin's IndexedDB and the index is a fact about the server.
   */
  modelMeshState: { modelId: string; present: boolean } | null;
  /**
   * Where the network map is looking, or null for its home view.
   *
   * In state rather than inside the map module because the screens here are pure
   * functions of state and the map is not going to be the exception: a pan or a
   * zoom is a `store.set` like any other, and the render that follows redraws the
   * tiles from it. Null means "wherever the map decides is home" — the extent of
   * the located structures — which is deliberately not resolved to numbers here,
   * since it depends on the size of a box only the DOM knows.
   *
   * Not persisted. A reload lands on the home view, which is the one that
   * answers "where is the network" rather than wherever someone last dragged to.
   */
  mapView: MapView | null;
  /**
   * Which stretch of a road its route card shows, where the road is drawn as
   * several — keyed by road, holding the stretch's first chainage and the
   * structure selected when it was picked. A pick holds until the selection
   * moves; after that the card follows the selection to whichever stretch has
   * it. See `stretchShown` in src/screens/network.ts. Not persisted.
   */
  routeStretch: Readonly<Record<string, { from: number; forId: string | null }>>;
  /**
   * The map's box in CSS pixels, as measured after layout.
   *
   * The tile grid and the fitted zoom both depend on it, so it has to be state
   * for the render to stay a function of state — see mapResized. Starts at a
   * sensible guess so the first paint draws something reasonable before any
   * measurement has happened, and in jsdom, where nothing has a size at all.
   */
  mapSize: { width: number; height: number };
  /**
   * A structure being placed by hand on the map, or null.
   *
   * A DRAFT, not the record: dragging a bridge around changes this, and only
   * `Save` writes it through `PATCH /api/bridges/:id` like any other edit. That
   * separation is what makes the gesture cancellable, and it is why the map draws
   * the footprint from this while it is set instead of from the record.
   *
   * It exists because the positions in this register are Google Maps pins and one
   * of them is probably on the wrong bridge — see the COORDINATES and DECK
   * BEARINGS notes in src/data/seed.ts. Someone looking at the crossing on screen
   * can see that better than any import could.
   */
  mapPlacing: MapPlacement | null;
  /** A placement is being saved. The map stays put and the bar says so. */
  mapPlaceSaving: boolean;
  /** Why the last placement failed to save, or null. */
  mapPlaceError: string | null;
  /**
   * A model's geometry being exported for the 3D view, or null.
   *
   * The export drives a hidden modeller and takes seconds — see
   * src/modeler/geometry.ts — so the view says so rather than appearing to hang.
   * Only one runs at a time; the button is gone while this is set.
   */
  geometryBuild: { bridgeId: string; modelId: string } | null;
  /** Why the last export failed, or null. */
  geometryError: string | null;
  /**
   * Whether the OpenStreetMap tiles are reachable.
   *
   * `failed` is set by the first tile that errors and turns the map into the
   * bundled outline (src/data/nepal.ts) with a note saying so. Sticky until the
   * user asks to try again: retrying per tile on a dead connection is a hundred
   * requests to be told the same thing.
   */
  mapTiles: 'unknown' | 'failed';
  /**
   * Whether the live 3D viewport in the detail screen's hero panel has posted
   * `ready` for whatever it is currently showing. Distinct from `modelerReady`
   * (the full-screen overlay's own flag): the two frames never coexist, but
   * each needs its own reset-per-load lifecycle — see src/modeler/viewport.ts.
   */
  heroViewportReady: boolean;
  /**
   * Every defect in the register, network-wide, from one request.
   *
   * There used to be two more fields beside this one — `defects` for the
   * modeller's own log on the selected structure and `defectsByBridge` for a
   * capped fan-out of the same over the whole register. Migration 0015 made the
   * `defects` table the single store, so there is one list, one read, and no
   * second answer to how many defects a structure has.
   *
   * Held for the whole network rather than per structure because the overview
   * counts across it and one query can answer for it. The detail screen filters
   * to the structure it is showing.
   */
  registerDefects: RegisterDefect[];

  /**
   * The selected structure's construction record, newest first.
   *
   * PER STRUCTURE, unlike the defects above, and the asymmetry is deliberate.
   * The overview counts defects across the whole network, so one request for
   * all of them pays for itself; nothing counts construction entries across a
   * department except the period reports, which ask for a date range and not
   * for everything. A register of fifty bridges under a five-year programme
   * would be tens of thousands of rows, and holding them all to draw one card
   * is the fan-out mistake `registerDefects` was created to undo, in reverse.
   */
  progress: ProgressEntry[];
  /** What `progress` adds up to, as the server rolled it up. Per unit, never across. */
  progressSummary: ProgressSummary;
  /**
   * Whether the record above has actually been read.
   *
   * THREE STATES AND NOT A BOOLEAN, for the reason `mapTiles` has three. An
   * empty list means one of two quite different things — a structure nobody has
   * started work on, or a register this device could not reach — and a card
   * that printed "No work recorded" for the second would be stating a fact
   * about a bridge on the strength of a failed request.
   */
  progressRead: 'unknown' | 'read' | 'unreachable';
  /**
   * The structure the site record on hand was read for. A selection moved where
   * nothing draws the record leaves the last one in place, and the bridge
   * dashboard (LC-30) must not read it as this structure's.
   */
  progressOf: string | null;

  /**
   * The rest of the selected structure's site record.
   *
   * Read in the same pass as `progress` and covered by the same
   * `progressRead`, because all four come from one structure being selected
   * and a card that said the pours were read and the diary was not would be
   * reporting on the shape of the code rather than on the register.
   */
  siteReturns: SiteReturn[];
  incidents: SiteIncident[];
  documents: SiteDocument[];
  /** Test results, and the register of hold points, NCRs and RFIs (LC-53, LC-54). */
  tests: SiteTest[];
  items: SiteItem[];
  /**
   * The handover of the selected structure, what holds it and what it would
   * carry over (LC-11), or null where it has not been read. Read beside the
   * site record, not inside its group: a handover that could not be read must
   * not empty the works.
   */
  handover: api.HandoverView | null;
  /**
   * What the structure's model — the model of record, else the newest — says
   * about its inventory, which Bridge info's Structure & levels shows in place
   * of the record's values. Null where there is no model or it was not read.
   */
  modelInventory: {
    bridgeId: string;
    modelId: string;
    fill: NonNullable<Awaited<ReturnType<typeof api.fetchModelInventory>>['fill']>;
  } | null;
  handoverBusy: boolean;
  handoverError: string | null;
  /** Whether the handover is open as a dialog, from Monitoring's ribbon. */
  handoverOpen: boolean;

  /**
   * The programme this structure is measured against, and the join to it.
   *
   * `baseline` is null for every bridge already in service, which is most of
   * them, and that is the ordinary case rather than a missing value: a
   * structure nobody is building has no plan to be late against, and the
   * screens say so rather than showing an empty comparison.
   */
  baseline: Baseline | null;
  matches: ScheduleMatch[];
  /** True while a baseline is being solved and frozen. */
  baselineBusy: boolean;
  /** Why the last attempt to take one came to nothing, in words. */
  baselineNote: string | null;
  /**
   * The revision a planner has taken and no approver has accepted yet (LC-40).
   * Never measured against: every figure is `baseline`'s.
   */
  proposedBaseline: Baseline | null;
  /** Every baseline taken of the structure, newest first, with reasons and approvers. */
  baselineHistory: BaselineHeader[];
  /** Extensions of time on the structure's contract (LC-42). */
  extensions: ContractExtension[];
  /** The department's working calendars (LC-43). */
  calendars: WorkCalendar[];
  /** The Take / Revise baseline form on the Scheduler's Baseline page, or null while it is shut. */
  baselineForm: BaselineForm | null;
  /** An extension of time being raised, or null. */
  eotDraft: { reason: string; days: string } | null;
  /** The calendar the administrator is editing, or null. */
  calendarEdit: CalendarDraft | null;
  calendarNote: string | null;
  /** The planned activity whose component picker is open (LC-41), or null. */
  matchPick: string | null;
  /**
   * Whether the baseline is being shown as a Gantt, on the cards that fold it:
   * section (h) of the construction report. Read it through `ganttShown`,
   * which answers for the Scheduler from `scheduleView` instead.
   *
   * FOLDED BY DEFAULT. The card shares the page with everything else, and the
   * bars — a few hundred rows behind a chunk that has to be fetched — stay
   * shut until the percentages and the late list have raised a question.
   *
   * Not persisted and not in the address. It is a fold, not a destination.
   */
  ganttOpen: boolean;

  /**
   * Which of its two views Construction Scheduler's programme screen shows,
   * each filling the page: the frozen baseline as a Gantt, or the table of
   * every planned activity against what the site has recorded.
   *
   * Not persisted. In the address since LC-31 — `#/schedule/<id>/activities` —
   * because each view is the whole page, and so a place rather than a fold.
   */
  scheduleView: 'gantt' | 'activities';

  /**
   * Whether a page of the Scheduler's explorer is open beside the programme.
   * Off, the programme has the width to itself; picking Baseline or Variance
   * opens the property panel beside it, and its pin closes it again.
   *
   * Not persisted: the programme is what the Scheduler opens on.
   */
  schedulePanel: boolean;

  /**
   * The month the calendar shows, "2026-05", and the structure it was picked
   * on; null, or another structure, opens on this month or the programme's first.
   */
  scheduleMonth: { bridgeId: string; month: string } | null;

  /**
   * Bridge Rating's screen, and the rating open on it, as the address names
   * them (LC-31). Bridge Rating keeps both in its own store; these are the
   * copy the router encodes, followed from it (`ratingMoved`) and pushed back
   * into it when an address is read — see `followRatingAddress`.
   */
  ratingScreen: RatingScreen;
  ratingOpen: string | null;

  /**
   * And whether those bars have been thrown across the whole window.
   *
   * A CARD IS THE WRONG SIZE FOR A PROGRAMME AND ALWAYS WILL BE. The fold
   * above gives the Gantt a 460px box inside a column that is itself sharing
   * the record with everything else, which is enough to see where an activity
   * sits and not enough to read a programme. So the box is not the only
   * offer: this takes the same mounted view — the same slot, the same scroll
   * position, the same collapsed rows — and lets it fill the viewport.
   *
   * SEPARATE FROM `ganttOpen`, because they answer different questions and a
   * single tri-state would make "shut" and "shut, but it was full last time"
   * the same thing. Closing the fold clears this; see `toggleGantt`.
   *
   * Not persisted and not in the address, for the reason the fold is not.
   */
  ganttFull: boolean;

  /* --- the Works screen: a whole department's record over a period --- */

  /**
   * Which cadence the period reports are being read at.
   *
   * Proposal 1 §4's three, plus the range somebody typed. Not persisted: which
   * report you were last reading is a question about a document rather than a
   * preference, and it travels in the address instead — see src/core/router.ts.
   */
  worksPeriod: PeriodKind;
  /** The days the report covers, inclusive at both ends. */
  worksRange: Period;
  /** The department's entries over that period, newest first. */
  worksEvents: ProgressEntry[];
  worksSummary: ProgressSummary;
  /** Same three states, same reason, as `progressRead` above. */
  worksRead: 'unknown' | 'read' | 'unreachable';
  /** The same three records over the report's period. */
  worksReturns: SiteReturn[];
  worksIncidents: SiteIncident[];
  worksDocuments: SiteDocument[];
  worksTests: SiteTest[];
  worksItems: SiteItem[];
  /**
   * Every entry of the department's from the first to the period's last day,
   * for the quantities to date beside the period's (LC-55). Empty where they
   * could not be read, which the section then says.
   */
  worksToDate: ProgressEntry[] | null;
  /** The structure in hand's report for the period: its summary and photographs (LC-55). */
  worksReport: PeriodReport | null;
  /** The executive summary being written, or null when it is not being edited. */
  worksSummaryDraft: string | null;
  /** The photographs being chosen, by entry id, or null when nobody is choosing. */
  worksPhotoPick: string[] | null;
  worksReportError: string | null;
  /** Whether a read is in flight. The screen dims rather than emptying. */
  worksLoading: boolean;

  /**
   * Every approved district rate table the register holds.
   *
   * Empty is the ordinary state and is not a failure: this application ships
   * with no rates, because there is no honest way for it to invent a Nepali
   * unit rate — see src/data/rates.ts. An empty list means every estimate on
   * every structure reports that nothing is priced, which is the correct answer
   * until a Division loads its own approved table.
   *
   * Held for the whole register rather than per structure, like the defects
   * above and for the same reason: one request answers for the network, and the
   * screen that needs one filters to its district.
   */
  rateTables: api.StoredRateTable[];

  /* --------------------------- people and roles --------------------------- */

  /**
   * Everybody who holds a role, or null before the People screen has asked.
   *
   * Null and empty are different answers and both are ordinary: null is "nobody
   * has looked yet" and `[]` is a register where every role is the default. The
   * screen says which of the two it is looking at rather than showing an empty
   * table for both — the rule the whole application follows about a dash and a
   * nil return.
   *
   * Fetched by the screen rather than on boot, unlike the rate tables: it is
   * administration data that only an administrator may read, and asking for it on
   * every load would mean a 403 in the console of every inspector's phone.
   */
  people: Person[] | null;
  /** How many administrators come from ADMIN_EMAILS rather than from the table. */
  peopleDeploymentAdmins: number;
  peopleLoading: boolean;
  /** Why the last read or write failed, or null. A 403 arrives here too. */
  peopleError: string | null;
  /** What the endpoint said after a change — including what removing a row did NOT do. */
  peopleNote: string | null;
  /** The grant being typed, or null when the form is closed. */
  personDraft: PersonDraft | null;
  /**
   * The address the open form is editing, or null when it is granting a new one.
   *
   * Held separately from the draft's own `email` so that changing the address in
   * an edit is impossible rather than merely discouraged: an identity IS the key,
   * and letting it be retyped would silently create a second row and leave the
   * first one holding the role somebody thought they had changed.
   */
  personEditing: string | null;
  personSaving: boolean;
  /** The row whose removal is being confirmed, or null. */
  personRemoving: string | null;

  /** What has been done to roles and access lately. Empty until the screen asks. */
  adminLog: api.AdminLogEntry[];

  /**
   * The photo-measuring assist, or null when it is closed.
   *
   * `QC-08`. Held on the store rather than inside the dialog for the reason
   * every other draft in this file is: the result has to reach the defect draft,
   * and a value living in a module-level map is a value the submit path cannot
   * see — which is the finding that produced `FA-11` and the inspection form's
   * photo strip before it.
   */
  measure: MeasureState | null;

  /** Whether the district rates dialog is open — `QC-06`. */
  ratesOpen: boolean;
  ratesSaving: boolean;
  /**
   * Every fault in the table as pasted, not the first.
   *
   * A list because this is what a Division sees after pasting a spreadsheet, and
   * reporting one problem per attempt turns a ten-minute job into ten round
   * trips for somebody who did not write this code.
   */
  ratesFaults: string[];
  ratesDraft: RatesDraft;

  /** Every treatment the administrator has reworded — migration 0047. */
  treatmentWording: StoredWording[];
  /** Whether the recommended-work dialog is open. */
  treatmentsOpen: boolean;
  /** The treatment being reworded in it, or null. */
  treatmentEdit: TreatmentEdit | null;
  treatmentSaving: boolean;
  treatmentError: string | null;

  /**
   * The selected structure's change log, or null before it has been read.
   *
   * Null and empty are different answers: nothing has been asked yet, versus
   * this record has never been edited. The panel says which.
   */
  changes: BridgeChange[] | null;
  /** Whether the history panel is expanded. Transient — it is a look, not a preference. */
  changesOpen: boolean;

  /**
   * The selected structure's phase history (LC-10), or null before it has been
   * read. Read when Bridge info's State group is shown; the current state
   * itself comes with the record and needs no read.
   */
  phaseHistory: api.PhaseTransition[] | null;
  /** A phase decision or a model-of-record change in flight. */
  phaseBusy: boolean;
  /** What the last one said, where it did not land. */
  phaseNote: string | null;
  /** The Edit state dialog, open on the selected structure. */
  stateEditOpen: boolean;
  /** What is typed into it: `inService`, `act`, `reason`, `months`. */
  stateDraft: Record<string, string>;

  /* --------------------------- the offline queue --------------------------- */

  /**
   * Writes recorded on this device that have not reached the register.
   *
   * Read from IndexedDB, so it survives the tab being closed — an inspector who
   * records three inspections in a valley and drives home with the app shut has
   * three inspections waiting, not none.
   */
  queued: QueuedWrite[];
  /** A drain is in flight. */
  syncing: boolean;
  /**
   * What the last drain did, if it is worth saying: how many landed, or that the
   * register refused something. Null the rest of the time.
   */
  syncNote: string | null;

  /* --------------------- installing on this device --------------------- */

  /**
   * The browser has offered an install prompt and it is being held.
   *
   * The whole condition for drawing the Install button, and the reason it is a
   * field rather than a call: the offer arrives as an event, at a moment nothing
   * else is happening, so the screen has to be repainted when it lands. See
   * src/core/install.ts for why nothing may be drawn without it.
   */
  canInstall: boolean;
  /** This device is running the installed copy, or has just installed it. */
  installed: boolean;
  /**
   * What the last press of Install came to, in the browser's own terms, or null
   * before there has been one.
   *
   * Worth a field of its own because a dismissed prompt leaves no trace on
   * screen otherwise — the button disappears, the event being spent, and an
   * inspector who tapped Cancel by accident would be left with no button and no
   * explanation.
   */
  installNote: string | null;

  /* bulk import */
  /** Whether the import dialog is open. */
  importOpen: boolean;
  /** The file's text, as pasted or read from disk. */
  importCsv: string;
  /** The last plan the backend returned, or null before one has been asked for. */
  importPlan: api.ImportPlan | null;
  importBusy: boolean;
  importError: string | null;

  /** Bridge whose "Log defect" dialog is open, or null. */
  defectBridgeId: string | null;
  /**
   * Nodes expanded in that dialog's component picker, by tree path.
   *
   * OPEN paths, where `componentTreeCollapsed` below holds CLOSED ones. The two
   * defaults are opposites on purpose. The Defects card is pruned to branches
   * that have a defect under them, so every group still on screen is there
   * because something is wrong beneath it and hiding it by default would hide
   * the finding. The picker offers the whole model — hundreds of components —
   * and showing all of it at once is the flat list this replaced.
   */
  defectTreeOpen: string[];
  /**
   * What is typed into the picker's find box. Non-empty swaps the tree for a
   * flat list of matching components — see searchComponents. Reset on open, for
   * the reason `defectTreeOpen` is.
   */
  defectTreeFilter: string;
  defectDraft: DefectDraft;
  defectSaving: boolean;
  defectError: string | null;

  /** Bridge whose "Record work" sheet is open, or null. */
  progressBridgeId: string | null;
  progressDraft: ProgressDraft;
  progressSaving: boolean;
  progressError: string | null;

  /** Bridge whose daily-return sheet is open, or null. */
  returnBridgeId: string | null;
  returnDraft: ReturnDraft;
  returnSaving: boolean;
  returnError: string | null;
  /**
   * Set where the register declined the write because the day is already
   * signed off.
   *
   * ITS OWN FIELD RATHER THAN A MESSAGE IN `returnError`, because it is not an
   * error: nothing was wrong with what was typed, the day has simply been
   * accepted and frozen. A refusal and a "too late" read differently and have
   * different answers — one is corrected, the other is taken to whoever
   * accepted the day.
   */
  returnFrozen: boolean;

  /** Bridge whose incident form is open, or null. */
  incidentBridgeId: string | null;
  incidentDraft: IncidentDraft;
  incidentSaving: boolean;
  incidentError: string | null;
  /**
   * The incident being closed out on the site record, or null (LC-50): what was
   * done, as it will be stored, and the day. One at a time.
   */
  incidentClose: { id: string; action: string; closedOn: string } | null;

  /** Bridge whose document upload is open, or null. */
  documentBridgeId: string | null;
  documentDraft: DocumentDraft;
  documentSaving: boolean;
  documentError: string | null;

  /** Bridge whose test-result form is open, or null (LC-53). */
  testBridgeId: string | null;
  testDraft: TestDraft;
  testSaving: boolean;
  testError: string | null;
  /** Bridge whose form raising a hold point, NCR or RFI is open, or null (LC-54). */
  itemBridgeId: string | null;
  itemDraft: ItemDraft;
  itemSaving: boolean;
  itemError: string | null;
  /** The item being answered, closed or released on the site record, or null. */
  itemAct: { id: string; act: ItemAct; note: string } | null;
  itemActError: string | null;
  /**
   * A review the register refused — an entry, a return, an inspection or a
   * defect moved on a picker — keyed by the row's id, with the reason (QA
   * 2026-09-30). The picker goes back to what is stored and the row says why;
   * it used to keep showing the refused state until a reload.
   */
  refused: { id: string; message: string } | null;
  /**
   * The hero viewport's own component tree, reported live once its frame is
   * ready — null before that, and whenever no viewport is showing at all.
   * Never persisted; see src/data/types.ts's ComponentNode.
   */
  heroComponentTree: ComponentNode[] | null;
  /**
   * Which nodes of the Components tree the user has folded away, keyed by
   * label path (e.g. "Spans>Span-1>Deck-1") rather than by index — index
   * would drift if a re-request ever reordered the tree.
   *
   * Membership means COLLAPSED, so an empty array is "everything open" — the
   * default a freshly loaded tree starts at. That is the way round it has to
   * be now the table is pruned to defect-bearing branches only: every group
   * still on screen exists solely because a defect sits under it, so starting
   * folded would show a list of headings and hide the only rows anyone came
   * for.
   */
  componentTreeCollapsed: string[];
}


const PERSIST_KEYS = [
  'theme',
  'listVariant',
  'navOpen',
  'guideOpen',
  'ribbonMode',
  'rowHeight',
  'selectedId',
  'detailPanelWidth',
  'ganttTableWidth',
  'scheduleLayout',
  'homeView',
  'detailPanelCollapsed',
  'detailView',
  'detailExplorerCollapsed',
  'detailStackView',
  'openBlocks',
  'showEmptyFields',
  'mapBase',
  'mapMode',
  'mapPaint',
  'summaryBy',
  'locale',
  'shell',
] as const satisfies readonly (keyof Persisted)[];

/**
 * "Nothing on file", shared rather than rebuilt per caller.
 *
 * State is read-only by convention — no screen writes through it — and one
 * instance means `store.set` can see that clearing an already-clear model index
 * changes nothing, and skip the repaint.
 */
const NO_MODELS: ModelIndex = { models: [], lastSavedAt: null, totalBytes: 0 };

/**
 * What a structure's models are, before the register has said anything.
 *
 * AN EMPTY INDEX USED TO BE THE ONLY ANSWER, and it made the record's 3D view a
 * coin toss. `heroModel` reads the model id out of this, src/core/geometryCache.ts
 * keys the `.glb` it holds by that id, and src/modeler/meshView.ts mounts on
 * neither until the index arrives — so a device holding the current geometry,
 * verified by ETag, still drew "No 3D model built" whenever the slowest read the
 * record makes ran past the 12-second budget in ./api.ts. See the header of
 * src/core/modelIndexCache.ts for the measurement and the trade.
 *
 * So the last index this device was given is the starting point, marked as such.
 * The read still goes out, and its answer still wins.
 */
function seedModels(bridgeId: string): { models: ModelIndex; modelsRemembered: boolean } {
  const remembered = recallModelIndex(bridgeId);
  return remembered
    ? { models: remembered, modelsRemembered: true }
    : { models: NO_MODELS, modelsRemembered: false };
}

/* An empty construction record. The same object `summariseProgress([])` builds
   on the server, so a structure with no entries and a read that failed produce
   identically shaped state and the card has one less branch to get wrong. */
const NO_PROGRESS_SUMMARY: ProgressSummary = {
  count: 0,
  draft: 0,
  structures: 0,
  quantities: [],
};

function defaults(): State {
  return {
    /* persisted */
    theme: 'light',
    listVariant: 'grid',
    navOpen: true,
    guideOpen: false,
    ribbonMode: 'classic',
    // One of the three heights the register's S / M / L control offers.
    rowHeight: 34,
    selectedId: 'BR-CHT-107',
    detailPanelWidth: 360,
    ganttTableWidth: null,
    scheduleLayout: 'gantt',
    homeView: 'both',
    detailPanelCollapsed: false,
    detailView: 'inventory',
    detailExplorerCollapsed: false,
    detailStackView: 'both',
    mapBase: 'street',
    mapMode: '2d',
    mapPaint: 'plain',
    summaryBy: 'district',
    locale: 'en',
    /* Seeded by measurement rather than by a constant: a phone opening this for
       the first time has no stored choice, and defaulting to the desk shell
       would show it the register's table. Overridden by a stored choice and by
       `?shell=` — see init() below. */
    shell: probeShell(),

    /* transient */
    screen: 'dashboard',
    app: 'home',
    ribbonTab: 'app',
    menu: null,

    backend: 'unknown',
    backendNote: '',
    identity: null,
    session: 'ok',
    loginUrl: null,
    sessionNote: null,
    signInUser: '',
    signInPassword: '',
    signInBusy: false,
    signInReveal: false,
    signInError: null,
    mustChangePassword: false,
    passwordCurrent: '',
    passwordNext: '',
    passwordBusy: false,
    passwordError: null,
    passwordDone: null,
    issuedPassword: null,

    q: '',
    district: 'All districts',
    road: 'All roads',
    sortKey: 'name',
    sortDir: 'asc',
    registerRevision: 0,
    registerPicked: [],
    registerNote: null,

    collapsedGroups: [],
    /* Empty, and that is the feature: every block on the record starts closed.
       See `openBlocks` in src/data/types.ts. */
    openBlocks: [],
    showEmptyFields: false,

    editingId: null,
    editCreating: false,
    editDraft: {},
    editSaving: false,
    editError: null,
    editFill: null,
    editOther: [],

    photoDefectId: null,
    photoDefectN: 1,
    defectFocusId: null,
    defectsTab: 'defects',
    inspectionFilter: '',
    inspectedOnly: false,
    defectHistory: null,
    defectMove: null,
    defectMoveError: null,
    defectSight: null,
    inspectionPhotoId: null,
    photoLayout: 'calendar',
    photoNewestFirst: true,
    photoMonth: null,
    returnLayout: 'calendar',
    returnMonth: null,
    worksLayout: 'table',
    worksMonth: null,
    worksEntryId: null,
    progressPhotoId: null,

    addingInspectionId: null,
    inspectionDraft: emptyInspectionDraft(),
    inspectionSaving: false,
    inspectionError: null,

    // Not persisted. Reopening the modeller on reload would spin up a WebGL
    // context and pull a model down before the user has asked for either.
    models: NO_MODELS,
    modelsRead: 'unknown',
    modelsRemembered: false,
    modelDeleteBridgeId: null,
    modelDeleteBusy: false,
    modelDeleteError: null,
    modelImport: null,
    modelImportResult: null,
    modelThumbNonce: 0,
    modelMeshState: null,
    mapView: null,
    routeStretch: {},
    // Roughly the card the network screen gives it on a laptop. Replaced by a
    // real measurement on the first paint that has a box to measure.
    mapSize: { width: 900, height: 420 },
    mapTiles: 'unknown',
    mapPlacing: null,
    mapPlaceSaving: false,
    mapPlaceError: null,
    geometryBuild: null,
    geometryError: null,
    heroViewportReady: false,
    registerDefects: [],
    progress: [],
    progressSummary: { count: 0, draft: 0, structures: 0, quantities: [] },
    progressRead: 'unknown',
    progressOf: null,
    siteReturns: [],
    incidents: [],
    documents: [],
    tests: [],
    items: [],
    handover: null,
    modelInventory: null,
    handoverBusy: false,
    handoverError: null,
    handoverOpen: false,
    baseline: null,
    matches: [],
    baselineBusy: false,
    baselineNote: null,
    proposedBaseline: null,
    baselineHistory: [],
    extensions: [],
    calendars: [],
    baselineForm: null,
    eotDraft: null,
    calendarEdit: null,
    calendarNote: null,
    matchPick: null,
    ganttOpen: false,
    scheduleView: 'gantt',
    schedulePanel: false,
    scheduleMonth: null,
    ratingScreen: 'ratings',
    ratingOpen: null,
    ganttFull: false,
    worksPeriod: 'month',
    /* Built from today at construction time. `periodFor` never reads the clock
       itself — see its header — so the one place the clock is consulted for a
       period is here and in the action that changes it. */
    worksRange: periodFor('month', new Date().toISOString().slice(0, 10)),
    worksEvents: [],
    worksSummary: { count: 0, draft: 0, structures: 0, quantities: [] },
    worksRead: 'unknown',
    worksReturns: [],
    worksIncidents: [],
    worksDocuments: [],
    worksTests: [],
    worksItems: [],
    worksLoading: false,
    rateTables: [],
    worksToDate: null,
    worksReport: null,
    worksSummaryDraft: null,
    worksPhotoPick: null,
    worksReportError: null,
    people: null,
    peopleDeploymentAdmins: 0,
    peopleLoading: false,
    peopleError: null,
    peopleNote: null,
    personDraft: null,
    personEditing: null,
    personSaving: false,
    personRemoving: null,
    adminLog: [],
    measure: null,
    ratesOpen: false,
    ratesSaving: false,
    ratesFaults: [],
    ratesDraft: emptyRatesDraft(),
    treatmentWording: [],
    treatmentsOpen: false,
    treatmentEdit: null,
    treatmentSaving: false,
    treatmentError: null,
    queued: [],
    syncing: false,
    syncNote: null,
    /* Measured, not guessed: watchInstall reports on the first tick of the boot
       sequence and again whenever the browser changes its mind. */
    canInstall: false,
    installed: false,
    installNote: null,
    importOpen: false,
    importCsv: '',
    importPlan: null,
    importBusy: false,
    importError: null,
    changes: null,
    changesOpen: false,
    phaseHistory: null,
    phaseBusy: false,
    phaseNote: null,
    stateEditOpen: false,
    stateDraft: {},
    defectBridgeId: null,
    defectTreeOpen: [],
    defectTreeFilter: '',
    defectDraft: emptyDefectDraft(),
    defectSaving: false,
    defectError: null,
    progressBridgeId: null,
    progressDraft: emptyProgressDraft(),
    progressSaving: false,
    progressError: null,
    returnBridgeId: null,
    returnDraft: emptyReturnDraft(),
    returnSaving: false,
    returnError: null,
    returnFrozen: false,
    incidentBridgeId: null,
    incidentDraft: emptyIncidentDraft(),
    incidentSaving: false,
    incidentError: null,
    incidentClose: null,
    documentBridgeId: null,
    documentDraft: emptyDocumentDraft(),
    documentSaving: false,
    documentError: null,
    testBridgeId: null,
    testDraft: emptyTestDraft(),
    testSaving: false,
    testError: null,
    itemBridgeId: null,
    itemDraft: emptyItemDraft(),
    itemSaving: false,
    itemError: null,
    itemAct: null,
    itemActError: null,
    refused: null,
    heroComponentTree: null,
    componentTreeCollapsed: [],
  };
}

/** Read persisted state, tolerating a corrupt or stale payload. */
/** Every entry the bridge record's explorer offers — see `detailView`. */
const DETAIL_VIEWS: readonly DetailView[] = [
  'inventory',
  'state',
  'inspections',
  'condition',
  'defects',
  'changes',
  'photos',
  'rating',
  'ratingHistory',
  'baseline',
  'variance',
  'works',
  'returns',
  'incidents',
  'documents',
  'tests',
  'siteItems',
  'charts',
  'progressPhotos',
];

function load(): Partial<Persisted> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const src = parsed as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of PERSIST_KEYS) {
      if (key in src) out[key] = src[key];
    }
    // A persisted selection can point at a bridge that no longer exists.
    if (
      typeof out['selectedId'] === 'string' &&
      !getRegister().some((b) => b.id === out['selectedId'])
    ) {
      delete out['selectedId'];
    }
    // And a persisted base map can name a tile source this build no longer
    // offers, which would leave the map's switch showing neither option as the
    // one in force. Dropped rather than corrected, so the default applies.
    if (out['mapBase'] !== 'street' && out['mapBase'] !== 'topo') delete out['mapBase'];
    if (out['mapMode'] !== '2d' && out['mapMode'] !== '3d' && out['mapMode'] !== 'fly') {
      delete out['mapMode'];
    }
    if (out['mapPaint'] !== 'plain' && out['mapPaint'] !== 'screening') delete out['mapPaint'];
    /* A grouping the CURRENT tenant does not offer is dropped, not kept: the
       preference outlives a tenant switch on a shared device, and a DoR
       "division" breakdown carried into a DPWH session is a tab with nothing
       under it. Dropping it lets the tenant's own default apply. */
    if (!isGroupBy(out['summaryBy'])) delete out['summaryBy'];
    if (!LOCALES.includes(out['locale'] as never)) delete out['locale'];
    // A shell this build no longer offers, dropped so the probe applies instead
    // of leaving somebody on a shell nothing can draw.
    if (out['shell'] !== 'desk' && out['shell'] !== 'field') delete out['shell'];
    const gw = out['ganttTableWidth'];
    if (gw !== null && (typeof gw !== 'number' || !Number.isFinite(gw))) delete out['ganttTableWidth'];
    if (out['scheduleLayout'] !== 'gantt' && out['scheduleLayout'] !== 'calendar') delete out['scheduleLayout'];
    if (!['both', 'register', 'network'].includes(out['homeView'] as string)) delete out['homeView'];
    if (out['ribbonMode'] !== 'simplified' && out['ribbonMode'] !== 'classic') delete out['ribbonMode'];
    // An explorer entry this build no longer draws would leave the property
    // panel empty with nothing in the explorer marked as the reason.
    if (!DETAIL_VIEWS.includes(out['detailView'] as never)) delete out['detailView'];
    if (out['detailStackView'] !== 'both' && out['detailStackView'] !== 'model' && out['detailStackView'] !== 'records') {
      delete out['detailStackView'];
    }
    return out as Partial<Persisted>;
  } catch {
    return {};
  }
}

function save(state: State): void {
  try {
    const out: Record<string, unknown> = {};
    for (const key of PERSIST_KEYS) out[key] = state[key];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch {
    /* storage full, private mode, or disabled — the app still works */
  }
}

/**
 * Adopt one bridge the backend has just returned into the in-memory register,
 * and say so in state.
 *
 * The revision bump is the whole point of the wrapper: see registerRevision.
 */
/**
 * *Fill from model* without the form (LC-83), run when a model is imported:
 * each inventory field the model states and the record leaves blank is written
 * to the record, logged as the model's. A field the record already states is
 * never overwritten here — the two disagreeing is for an engineer to settle,
 * from Edit record's Fill from model — and is reported instead.
 *
 * Only for the model the form would fill from (the model of record, else the
 * newest), so an import into a model that is not the one of record fills
 * nothing. Null where there was nothing to do or nobody here may do it.
 */
async function fillRecordFromModel(
  bridgeId: string,
  modelId: string,
): Promise<{ filled: string[]; differs: string[]; error: string | null } | null> {
  const s = store.get();
  if (s.backend !== 'live' || !roleCan(s, 'inventory.edit') || modelId === '') return null;
  const b = findBridge(bridgeId);
  if (b === null) return null;
  try {
    const read = await api.fetchModelInventory(bridgeId);
    if (read.modelId !== modelId || read.fill === null) return null;
    const values: Record<string, string> = {};
    const filled: string[] = [];
    const differs: string[] = [];
    for (const f of HANDOVER_FIELDS) {
      const v = read.fill.values[f.key];
      if (!f.inventory || v == null) continue;
      const held = (b as unknown as Record<string, unknown>)[f.key];
      if (held == null || held === '') {
        values[f.key] = String(v);
        filled.push(`${f.label}: ${v}${f.unit ? ` ${f.unit}` : ''}`);
      } else if (typeof v === 'number' ? Math.abs(Number(held) - v) > 1e-6 : String(held).trim() !== String(v).trim()) {
        differs.push(`${f.label}: the record says ${held}, the model ${v}`);
      }
    }
    const superstructure = read.fill.superstructure;
    if (superstructure && (b.type == null || b.type.trim() === '')) {
      values['type'] = superstructure;
      filled.push(`Superstructure: ${superstructure}`);
    }
    if (filled.length > 0) {
      const payload = { ...buildEditPayload({ ...draftFrom(b), ...values }, true), fromModel: modelId };
      adopt(await api.updateBridge(bridgeId, payload));
    }
    return filled.length === 0 && differs.length === 0 ? null : { filled, differs, error: null };
  } catch (err) {
    return { filled: [], differs: [], error: err instanceof Error ? err.message : 'The record could not be filled from the model.' };
  }
}

function adopt(bridge: Parameters<typeof updateOne>[0]): void {
  updateOne(bridge);
  store.set((s) => ({ registerRevision: s.registerRevision + 1 }));
}

/**
 * Whether two API payloads carry the same content.
 *
 * Both sides come straight from src/core/api.ts, which builds them field by field
 * in a fixed order, so a string comparison is sound here — and both are a handful
 * of records at most.
 */
/**
 * Put a request in flight now, to be awaited somewhere further down.
 *
 * The no-op `catch` is the whole point of this and is not swallowing anything:
 * the real handling is at the `await`, in the caller's own try/catch, but a
 * promise that rejects BEFORE anybody awaits it — which is exactly what happens
 * when one of several parallel boot reads fails first — is an unhandled
 * rejection as far as the browser is concerned, and shows up in the console as
 * an error nobody can act on. Attaching a handler at the point the request is
 * started makes the rejection expected; the value, and the failure, still arrive
 * at the `await` unchanged.
 */
/** Whether this device's last word from the register had somebody signed in. Unknown reads as no. */
const SIGNED_IN_KEY = 'bw.signed-in';

function believedSignedIn(): boolean {
  try {
    return localStorage.getItem(SIGNED_IN_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberSignedIn(yes: boolean): void {
  try {
    localStorage.setItem(SIGNED_IN_KEY, yes ? '1' : '0');
  } catch {
    /* A device that cannot remember asks identity first every time, which is only slower. */
  }
}

function started<T>(run: () => Promise<T>): Promise<T> {
  const read = run();
  read.catch(() => {});
  return read;
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Point the app at a structure, optionally moving to a screen with it.
 *
 * One patch, then one more when the structure's model index and defect log land
 * — two repaints for opening a record rather than the four it used to take, and
 * paint() rebuilds the entire shell, so each one the user can see.
 *
 * The two per-structure collections are cleared in the same patch that moves the
 * selection, and only when the structure actually changes. Left to lag, the
 * record paints for a moment against the previous structure's model index, and
 * the hero viewport builds a 1.6 MB WebGL frame for a model this bridge does not
 * have — only to tear it down and build the right one when the index arrives.
 * Cleared on a re-select of the structure already in hand, the same tear-down
 * would happen for an index that was still correct.
 */
function selectInto(id: string, screen: Screen | null): void {
  const fresh = id !== store.get().selectedId;
  store.set({
    selectedId: id,
    ...(screen ? { screen } : {}),
    /* The change log belongs to a structure, so it is dropped with the rest of
       the per-structure collections. Left to lag, the record would show the
       previous structure's history under this one's name. */
    ...(fresh
      ? {
          /* Seeded from the device rather than cleared — see `seedModels`. The
             read that follows replaces it either way, so this costs a repaint
             only when it has something to show, and saves the one where the
             hero viewport was torn down and rebuilt around a read. */
          ...seedModels(id),
          modelsRead: 'unknown' as const,
          changes: null,
          changesOpen: false,
          phaseHistory: null,
          phaseNote: null,
          stateEditOpen: false,
          stateDraft: {},
          handover: null,
          modelInventory: null,
          handoverError: null,
          handoverOpen: false,
          baselineForm: null,
          eotDraft: null,
          matchPick: null,
        }
      : {}),
  });
  void actions.loadBridgeContext();
}

type Listener = (state: State) => void;

class Store {
  private state: State;
  private listeners = new Set<Listener>();
  private frame: number | null = null;

  constructor() {
    this.state = { ...defaults(), ...load() };
  }

  get(): Readonly<State> {
    return this.state;
  }

  /**
   * Merge a patch, persist, and schedule one render for this frame.
   *
   * A patch that changes nothing is dropped before it reaches either. paint()
   * rebuilds the whole shell, so a set that says what state already says is a
   * visible flicker for no new information — and several callers do exactly
   * that, the hero viewport's reset-on-build most of all (it clears a ready
   * flag and a component tree that are usually already clear).
   */
  set(patch: Partial<State> | ((s: Readonly<State>) => Partial<State>)): void {
    const next = typeof patch === 'function' ? patch(this.state) : patch;

    let changed = false;
    for (const key of Object.keys(next) as (keyof State)[]) {
      if (!Object.is(this.state[key], next[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    this.state = { ...this.state, ...next };
    save(this.state);
    this.schedule();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private schedule(): void {
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      for (const fn of this.listeners) fn(this.state);
    });
  }

  /** Force a synchronous notify — used once at boot. */
  flush(): void {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    for (const fn of this.listeners) fn(this.state);
  }

  /** Wipe persisted state and reload defaults. */
  reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.state = defaults();
    this.schedule();
  }
}

export const store = new Store();

/* --------------------------- what this browser may do --------------------------- */

/**
 * Whether the signed-in identity carries a capability.
 *
 * THE ONE PLACE THE SCREENS ASK. Every control that writes something is drawn
 * through this, so the rule about what an inspector may press lives in
 * src/data/roles.ts and not in eleven call sites — see the header there.
 *
 * TRUE WHERE NOTHING IS KNOWN, and that is the important half. `identity` is
 * null in three situations and all three must not withhold controls: before
 * /api/health has answered, on a deployment that does not enforce Access at all,
 * and in a gorge with no signal. The last one decides it. An inspector standing
 * at a structure with no connection has no way to be told what they may do, and
 * an application that greyed out "Log defect" until it could ask would be
 * useless in exactly the place it is for. What they record queues on the device
 * and the register refuses it on arrival if it must — which is a message, not a
 * lost afternoon.
 *
 * The SERVER'S capability list is preferred over this build's copy of the table
 * where there is one. A browser holding a cached older build would otherwise
 * decide what to draw from a map the register has since changed.
 */
export function can(s: Readonly<State>, capability: Capability): boolean {
  return roleCan(s, capability) && phaseAllows(s, capability);
}

/**
 * Whether this session may administer people — and, unlike `can`, NO by default.
 *
 * `can` reads unknown roles as "permitted", which is right for an unprotected
 * deployment's working screens and wrong for this one: a session whose roles
 * never arrived (an older API behind the dev proxy, a health read that failed)
 * was shown the People screen and its controls. Only an answer from the server
 * that names the capability, or the admin role, opens it.
 */
export function canManagePeople(s: Readonly<State>): boolean {
  const identity = s.identity;
  if (!identity) return false;
  if (identity.capabilities) return identity.capabilities.includes('people.manage');
  return Boolean(identity.roles?.includes('admin')) && identity.suspended !== true;
}

/** The roles half of `can`, with the phase left out — for a control about the phase itself. */
export function roleCan(s: Readonly<State>, capability: Capability): boolean {
  const identity = s.identity;
  if (!identity) return true;
  if (identity.capabilities) return identity.capabilities.includes(capability);
  return roleAllows(identity.roles ?? null, capability, identity.suspended === true);
}

/**
 * The phase half: whether the selected structure's state lets its application
 * be written to (LC-13, LIFECYCLE.md §3). A bridge under construction has no
 * inspection to log; one in service keeps its programme as history. Only the
 * capabilities that belong to an application are touched — see
 * `CAPABILITY_APP` in src/data/phase.ts — and with no structure in hand
 * nothing is withheld.
 */
export function phaseAllows(s: Readonly<State>, capability: Capability): boolean {
  const b = findBridge(s.selectedId);
  return b === null || phasePermits(phaseOf(b), capability);
}

/** The selected structure's state, or null with none in hand. */
export function selectedPhase(s: Readonly<State>): PhaseState | null {
  const b = findBridge(s.selectedId);
  return b === null ? null : viewPhaseOf(b);
}

/**
 * Whether a baseline taken on the selected structure would open works on it
 * (LC-81): it is built — in service or closed — and has none open.
 */
/** Record a refused review against its row, for the picker to say why. Always false. */
function refusedAt(id: string, err: unknown, fallback: string): false {
  store.set({ refused: { id, message: err instanceof Error ? err.message : fallback } });
  return false;
}

/**
 * The programme of an imported model, from its stored export, or null where the
 * model is parametric or has no stored export to read (QA 2026-09-30).
 */
async function importedProgrammeOf(bridgeId: string, modelId: string): Promise<ImportedProgramme | null> {
  const index = await api.fetchModels(bridgeId).catch(() => null);
  const model = index?.models.find((m) => m.id === modelId);
  if (!model || model.imported !== true || !model.hasSource) return null;
  const texts = await api.fetchModelSource(bridgeId, modelId).catch(() => null);
  return texts === null ? null : programmeFromExport(texts);
}

export function opensWorks(s: Readonly<State>): boolean {
  const p = selectedPhase(s);
  return p !== null && (p.phase === 'in-service' || p.phase === 'closed') && p.works === null;
}

/** "3 recorded in service; 1 skipped, already recorded." */
export function recordedNote(recorded: number, skipped: number): string {
  const one = `${recorded} recorded in service`;
  return skipped === 0 ? `${one}.` : `${one}; ${skipped} skipped, already recorded.`;
}

/**
 * Why a control is not available, in the words its tooltip uses.
 *
 * A disabled button with no explanation is a support call. Where the answer is
 * "your role does not allow it" the message says which role, because the person
 * reading it usually does not know they have one.
 */
export function whyNot(s: Readonly<State>, what: string, capability?: Capability): string {
  /* The roles allow it and the phase does not: the line the application's own
     state gives, which says when it opens. */
  if (capability !== undefined && roleCan(s, capability) && !phaseAllows(s, capability)) {
    const app = CAPABILITY_APP[capability];
    const p = selectedPhase(s);
    const line = app && p ? accessLine(p, app) : null;
    if (line) return line;
  }
  const roles = s.identity?.roles;
  return roles
    ? `${what} is not part of your roles (${rolesLabel(roles, s.identity?.suspended)}). A ` +
        'register administrator can change them.'
    : `${what} is not available.`;
}

/**
 * Whether the baseline's Gantt is drawn: in the Scheduler, whether its Gantt
 * view is the one in front; everywhere else, the fold. See `ganttOpen` and
 * `scheduleView`. Everything that asks "are the bars showing" asks this.
 */
export function ganttShown(s: Readonly<State>): boolean {
  return s.app === 'scheduler' ? s.scheduleView === 'gantt' : s.ganttOpen;
}

/* --------------------------- action helpers --------------------------- */

/**
 * What the status bar says about where the register on screen came from.
 *
 * TEMPORARY in its three-way shape — without the FWO demonstration this is the
 * two-line ternary it used to be, and it goes back to one when
 * src/data/fwo-demo.ts is deleted.
 *
 * It exists at all because the status bar must not say "25 records from D1" when
 * two of them came from D1 and the rest were invented. A count that overstates what
 * the database holds is precisely the claim this application is built not to
 * make, and it is the one line on screen that can say so.
 */
function registerNote(fromD1: number, fromDemo: number, adopted: boolean, filled = 0): string {
  if (!adopted) return 'D1 reachable but empty — showing bundled seed';
  if (fromDemo === 0 && filled > 0) return `${fromD1} records from D1, ${filled} filled in with demonstration data`;
  if (fromDemo === 0) return `${fromD1} records from D1`;
  if (fromD1 === 0) return `D1 reachable but empty — ${fromDemo} demonstration records`;
  return `${fromD1} records from D1, plus ${fromDemo} demonstration records`;
}

export const actions = {
  go(screen: Screen): void {
    /* The one funnel every navigation goes through — the desk nav pane, the field
       tab bar, the number-key shortcuts, and the map's marker sheet all arrive
       here — so the field shell's refusal is applied here rather than at each
       call site. That is what makes it a property of the application instead of
       a property of whichever controls happen to be drawn: the desk shell's
       number keys are still bound on a phone, and `1` must not open a network
       report there. See src/core/shell.ts. */
    const landed = store.get();

    /* The desk's register and network are its start page, so going to either
       from an application is going home — see deskSendsHome. */
    if (deskSendsHome(landed.shell, screen)) {
      actions.openApp('home', 'dashboard');
      return;
    }

    /* WHAT THIS DELIBERATELY DOES NOT DO is leave for another application. Four
       apps share this build, each served as its own page (src/core/apps.ts), and
       navigating between them is a page load rather than a state change — so it
       is in src/core/navigate.ts, which may read the router's encoder, and not
       here, which may not: this module is what the router is built on. Nothing
       routes a foreign screen through `go` in any case, because a nav pane only
       ever offers the screens its own app owns. */
    store.set((s) => ({ screen: shellScreen(s.shell, screen) }));

    /* THE 3D MODEL IS OF ONE STRUCTURE, and on a first visit nothing has been
       picked: the explorer and the property panel are showing the register's
       first record by `findBridge`'s fallback, and the model has to be of that
       one — its index is read for the selection, and an empty selection reads
       nothing. So opening it settles the selection on what is already shown. */
    if (screen === 'detail' && landed.selectedId === '') {
      const shown = findBridge('');
      if (shown) actions.selectBridge(shown.id);
    }

    if (screen !== landed.screen) void actions.screenOpened(screen);
  },

  /**
   * A screen has been shown. Fetch what it needs and nobody else does.
   *
   * TWO SCREENS FETCH WHEN THEY ARE OPENED, and for the same kind of reason.
   * Everything else on the desk shell reads state the boot sequence already
   * filled. The people list is administration data that only an administrator
   * may read, so asking for it on every boot would put a 403 in the console of
   * every inspector's phone. The construction report is a query over a period
   * nobody has named until this screen is looked at, and on a register of
   * bridges in service it would return nothing on every boot for ever.
   *
   * ITS OWN ACTION RATHER THAN A BLOCK INSIDE `go`, because `go` is not the only
   * way a screen gets shown and this cost an administrator a confused minute
   * looking at an empty People screen. `applyHash` in src/core/router.ts sets
   * `screen` straight onto the store — it has to, since decoding an address is
   * not navigating — so reloading the page on `#/people`, following a link to it,
   * or simply signing in while the address bar still said `#/people` all left the
   * panel reporting that it had not been read. Which was true, and useless.
   *
   * IDEMPOTENT, so every path can call it without coordinating: it asks only for
   * what has not been read. That also keeps it out of the way of a screen whose
   * data is already on the store, and makes calling it from two places a
   * non-event rather than a duplicate request.
   */
  async screenOpened(screen: Screen): Promise<void> {
    const s = store.get();

    if (screen === 'people') {
      /* Only what is missing. `null` is the "has not been read" the screen
         prints, so this is the same condition the reader is looking at. */
      if (s.people === null && !s.peopleLoading) void actions.refreshPeople();
      return;
    }

    /* The bridge dashboard reads the site record the start page otherwise
       never asks for (LC-30) — and asks again where the one on hand is another
       structure's. */
    if (screen === 'board') {
      if (s.progressOf !== s.selectedId || s.progressRead === 'unknown') void actions.refreshProgress();
      return;
    }

    if (screen === 'works') {
      /* `unknown` is this screen's "has not been read", exactly as `null` is the
         People screen's — and it is the state a failed read does NOT leave
         behind, so a register that could not be reached is not asked again on
         every repaint. Somebody who wants to try again changes the period, which
         is the control already in front of them. */
      if (s.worksRead === 'unknown' && !s.worksLoading) void actions.refreshWorks();
      return;
    }
  },

  /**
   * Open a structure in whatever this application's structure screen is.
   *
   * The register is carried by three of the four apps and a row means a
   * different thing in each: in Bridge Inspection it is the record, in the
   * Scheduler the programme, in Monitoring the site record. One action, because
   * every caller means the same thing by it — "open this bridge, here" — and a
   * register screen that had to know which app was drawing it would be the
   * routing table in a third place.
   *
   * An app with no structure screen is the home page, whose table sends a row
   * to an app rather than opening one itself; there the selection is all this
   * does, which is what `selectBridge` means.
   */
  openBridge(id: string): void {
    selectInto(id, appForShell(store.get().shell).structure);
  },

  selectBridge(id: string): void {
    selectInto(id, null);
  },

  /**
   * Put an app on screen, at one of its screens or at its landing screen.
   *
   * THE ONE WAY BETWEEN APPS FROM A CLICK. The ribbon's tabs and the start
   * page's cards are anchors (so a middle-click still opens a second tab) and
   * reach the router through the address; this is for the controls that are not
   * links. Either way `running` in src/core/apps.ts and `State.app` move
   * together, which is the invariant the whole single-page arrangement rests on.
   *
   * The field shell is one app and stays one — see `appForShell`.
   */
  openApp(id: AppId, screen?: Screen): void {
    const s = store.get();
    if (s.shell === 'field') {
      if (screen) actions.go(screen);
      return;
    }
    let app = appById(id);
    let wanted = screen && owns(app, screen) ? screen : app.landing;
    if (deskSendsHome(s.shell, wanted)) {
      app = appById('home');
      wanted = 'dashboard';
    }
    id = app.id;
    const landed = shellScreen(s.shell, wanted);
    setRunningApp(id);
    /* From the start page an app opens on Bridge info — see appHref. */
    store.set(
      s.app === 'home' && id !== 'home'
        ? { app: id, screen: landed, ribbonTab: 'app', detailView: 'inventory' }
        : { app: id, screen: landed, ribbonTab: 'app' },
    );
    actions.appOpened();
    if (landed !== s.screen) void actions.screenOpened(landed);
  },

  /**
   * An app has come on screen. Fetch what it draws that the last one did not.
   *
   * The site record is read only where something draws it (`readsSiteRecord`),
   * which used to be decided once per document. With every app on one page, a
   * move from Bridge Inspection to the Scheduler is the first moment the
   * programme has a reader, so this is where it is asked for. Idempotent in the
   * same way `screenOpened` is: `unknown` is "never read".
   */
  appOpened(): void {
    const s = store.get();
    if (readsSiteRecord(s.shell, s.screen) && s.progressRead === 'unknown') void actions.refreshProgress();
  },

  setRibbonTab(tab: State['ribbonTab']): void {
    store.set({ ribbonTab: tab });
  },

  toggleRibbonMode(): void {
    store.set((s) => ({ ribbonMode: s.ribbonMode === 'simplified' ? 'classic' : 'simplified' }));
  },

  setRibbonMode(mode: State['ribbonMode']): void {
    store.set({ ribbonMode: mode, menu: null });
  },

  /** Open a flyout, or close it if it is the one already open. */
  toggleMenu(id: MenuId): void {
    store.set((s) => ({ menu: s.menu === id ? null : id }));
  },

  closeMenu(): void {
    if (store.get().menu !== null) store.set({ menu: null });
  },

  setTheme(theme: Theme): void {
    store.set({ theme });
  },

  toggleTheme(): void {
    store.set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' }));
  },

  toggleGuide(): void {
    store.set((s) => ({ guideOpen: !s.guideOpen, menu: null }));
  },

  closeGuide(): void {
    store.set({ guideOpen: false });
  },

  toggleNav(): void {
    store.set((s) => ({ navOpen: !s.navOpen }));
  },

  setDetailPanelWidth(px: number): void {
    store.set({ detailPanelWidth: Math.round(px) });
  },

  setGanttTableWidth(px: number | null): void {
    store.set({ ganttTableWidth: px === null ? null : Math.round(px) });
  },

  toggleDetailPanel(): void {
    /* Beside the programme the pin closes the page rather than leaving a rail. */
    if (store.get().screen === 'schedule') {
      store.set({ schedulePanel: false });
      return;
    }
    store.set((s) => ({ detailPanelCollapsed: !s.detailPanelCollapsed }));
  },

  toggleDetailExplorer(): void {
    store.set((s) => ({ detailExplorerCollapsed: !s.detailExplorerCollapsed }));
  },

  /** Show the stacked record page's model, its docked panel, or both. */
  setDetailStackView(view: DetailStackView): void {
    store.set({ detailStackView: view });
  },

  /**
   * Show one entry of the record's explorer in the property panel. `group` is an
   * inventory group to open and bring into view — Bridge details, General and
   * the rest are one page of the panel, and the explorer's entries for them are
   * places on it. A panel unpinned to its rail is pinned back, since picking
   * something to read is asking to see it.
   */
  showDetailView(view: DetailView, group?: string): void {
    store.set((s) => ({
      detailView: view,
      defectFocusId: null,
      detailPanelCollapsed: false,
      collapsedGroups: group ? s.collapsedGroups.filter((n) => n !== group) : s.collapsedGroups,
    }));
    /* The programme stays in the content panel, with the page beside it. */
    if (store.get().screen === 'schedule') store.set({ schedulePanel: true });
  },

  setListVariant(listVariant: ListVariant): void {
    store.set({ listVariant });
  },

  /** Register row height, in px — the S / M / L control above the table. */
  setRowHeight(rowHeight: number): void {
    store.set({ rowHeight });
  },

  sort(key: string): void {
    store.set((s) => ({
      sortKey: key,
      sortDir: s.sortKey === key && s.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  },

  /** Load the register out of D1, falling back to the bundled seed. Called once on boot. */
  async hydrate(): Promise<void> {
    /* FOUR READS, STARTED TOGETHER AND ADOPTED IN ORDER.

       None of these four needs another's answer — the server scopes every one of
       them to the caller's own tenant, so none of them needs the identity call to
       have landed first. Awaited one after another they cost four round trips
       before the first screen is complete, and on a `wrangler pages dev` session
       the D1 binding is remote (see wrangler.jsonc), so a round trip is most of a
       second rather than a millisecond. Four of them, plus the two below, was the
       wait in front of every reload.

       WHAT IS NOT BEING CHANGED IS THE ORDER THEY ARE ADOPTED IN, which is the
       part that carries the correctness property described below. Starting a
       request early is not the same as believing its answer early. */
    const identityRead = started(() => api.health());

    /* UNLESS THIS DEVICE WAS SIGNED OUT LAST TIME (QA 2026-09-30). Every read
       but health is refused without a session (functions/lib/sessionGuard.ts),
       so the sign-in page used to send three requests only to be told 403.
       Where the last answer here had nobody signed in, identity is asked
       first, and a health that names nobody is the sign-in screen, with
       nothing else sent. A device that was signed in keeps the four together. */
    if (!believedSignedIn()) {
      const report = await identityRead.catch(() => null);
      if (report !== null && !report.identity) {
        await actions.refreshIdentity(identityRead);
        actions.sessionLost({ message: 'Not signed in. This register asks for a username and password.', loginUrl: null });
        return;
      }
    }

    const registerRead = started(() => api.fetchRegister());
    const defectsRead = started(() => api.fetchRegisterDefects());
    const ratesRead = started(() => api.fetchRateTables());
    const wordingRead = started(() => api.fetchTreatmentWording());

    /* IDENTITY FIRST, AND THAT ORDER IS LOAD-BEARING. /api/health carries the
       tenant, and the tenant decides which bundled seed the fallback is, which
       agency block is drawn and which groupings the summary offers. Fetching the
       register first would have been harmless — the server scopes it either way
       — but a failed fetch would then have fallen back to the WRONG
       department's seed, under the wrong letterhead, with nothing on screen to
       say so. It is one cheap request against a correctness property. */
    /* Signed in to another country's register: its rows are already on their
       way and are not adopted. See refreshIdentity. */
    if (!(await actions.refreshIdentity(identityRead))) return;

    try {
      const bridges = await registerRead;

      /* TEMPORARY — THE FWO DEMONSTRATION, AND IT IS AN ADDITION, NOT A CHOICE.
         D1 beats the bundled seed, always; that rule is the register's oldest
         and is not being bent here. But this deployment's D1 already holds two
         real `pk-fwo` records, so adopting its answer threw the demonstration
         away the instant anybody signed in — ten structures before
         authentication, two after. So the fabricated records are APPENDED to
         what the database returned, a real row wins on id, and the note below
         says plainly that the count on screen is a mixture. Empty for every
         other department and every build without the flag. Delete with
         src/data/fwo-demo.ts. */
      /* And the DPWH demonstration FILLS IN two of its own records rather than
         adding any (src/data/dpwh-demo.ts); a value D1 holds still wins. */
      const merged = withDemoBridges(active().id, bridges);
      const demo = merged.slice(bridges.length);
      const filled = demoFilledCount(active().id, merged);

      const adopted = setRegister(merged);
      store.set((s) => ({
        registerRevision: s.registerRevision + 1,
        backend: 'live',
        backendNote: registerNote(bridges.length, demo.length, adopted, filled),
      }));
    } catch (err) {
      // Not an error state for the user: the seed is a complete register and
      // every screen still works. Say where the numbers came from and move on.
      store.set({
        backend: 'unreachable',
        backendNote: err instanceof Error ? err.message : 'Backend unreachable.',
      });
    }
    /* The last three settle together. The defects and the rates were started at
       the top and are most likely back already; the structure's own context
       could not be started until the register had said which structures there
       are, and is the one read here that still costs a round trip. */
    await Promise.all([
      actions.loadBridgeContext(),
      actions.refreshAllDefects(defectsRead),
      actions.refreshRateTables(ratesRead),
      actions.refreshTreatmentWording(wordingRead),
    ]);
  },

  /**
   * Ask the backend who is signed in.
   *
   * Failure is not worth reporting on its own: /api/health failing means the
   * register call almost certainly failed too, and that is already on screen.
   */
  async refreshIdentity(inFlight?: Promise<api.HealthReport>): Promise<boolean> {
    try {
      const report = await (inFlight ?? api.health());
      /* A SESSION FROM ANOTHER COUNTRY IS NOT THIS WINDOW'S. The server serves
         the register the session signed in to, so a Philippines window holding
         a Nepal session — the localhost cookie is shared across ports, and a
         browser holds one — would draw Nepal's bridges under the DPWH
         letterhead. It is signed out here instead, saying which register it
         was in, and signing in again opens this one. */
      const wanted = requestedTenant();
      if (report.identity && wanted && report.tenant && report.tenant !== wanted) {
        const held = byId(report.tenant)?.name ?? report.tenant;
        const here = byId(wanted)?.name ?? wanted;
        actions.sessionLost({
          message: `This browser is signed in to ${held}, not ${here}. Sign in to work in ${here}’s register.`,
          loginUrl: null,
        });
        return false;
      }
      store.set({ identity: report.identity ?? null });
      rememberSignedIn(Boolean(report.identity));
      /* The server's answer about which register this is, applied after the
         store write so one repaint covers both. Null on a deployment too old to
         report it, which resolves to the build's own tenant or the default. */
      actions.tenantResolved(report.tenant ?? null);
    } catch {
      store.set({ identity: null });
    }
    return true;
  },

  /**
   * Open or close one of the bridge record's blocks.
   *
   * The inverse of `toggleGroup` and deliberately a second action rather than a
   * parameter on it: the two lists mean opposite things — one names what is
   * shut on a panel that starts open, the other what is open on a page that
   * starts shut — and one action over both would be a boolean nobody could read
   * at the call site.
   */
  toggleBlock(id: string): void {
    store.set((s) => ({
      openBlocks: s.openBlocks.includes(id)
        ? s.openBlocks.filter((n) => n !== id)
        : [...s.openBlocks, id],
    }));
  },

  /** Print the fields the source does not record, or stop printing them. */
  toggleEmptyFields(): void {
    store.set((s) => ({ showEmptyFields: !s.showEmptyFields }));
  },

  toggleGroup(name: string): void {
    store.set((s) => ({
      collapsedGroups: s.collapsedGroups.includes(name)
        ? s.collapsedGroups.filter((n) => n !== name)
        : [...s.collapsedGroups, name],
    }));
  },

  /* --------------------------- editing the inventory record --------------------------- */

  /** Open the edit dialog, seeded from the bridge's current values. */
  openEdit(bridgeId: string): void {
    const current = findBridge(bridgeId);
    /* Nothing to seed the form from, which happens only on an empty register —
       where no control that opens this dialog is on screen either. Refused
       rather than opened over a blank draft: a form seeded from nothing saves a
       record of nothing. */
    if (current === null) return;
    store.set({
      editingId: bridgeId,
      editCreating: false,
      editDraft: draftFrom(current),
      editSaving: false,
      editError: null,
      editFill: null,
      editOther: [],
    });
  },

  /**
   * *Fill from model* (LC-83): the inventory fields the structure's model
   * states — the model of record, else the newest — put into the draft, each
   * still editable before saving. Read as the handover reads it.
   */
  async fillEditFromModel(): Promise<void> {
    const id = store.get().editingId;
    if (!id || store.get().editCreating || store.get().editSaving) return;
    store.set({ editSaving: true, editError: null });
    try {
      const read = await api.fetchModelInventory(id);
      if (store.get().editingId !== id) return;
      if (read.modelId === null || read.fill === null) {
        store.set({
          editSaving: false,
          editError: read.modelId === null
            ? 'This structure has no imported model to fill from.'
            : `Model ${read.modelId} has no export this register can read.`,
        });
        return;
      }
      const values: Record<string, string> = {};
      for (const f of HANDOVER_FIELDS) {
        const v = read.fill.values[f.key];
        if (f.inventory && v != null) values[f.key] = String(v);
      }
      if (read.fill.superstructure) values['type'] = read.fill.superstructure;
      if (Object.keys(values).length === 0) {
        store.set({ editSaving: false, editError: `Model ${read.modelId} states none of the inventory it could fill.` });
        return;
      }
      store.set((s) => ({
        editSaving: false,
        editDraft: { ...s.editDraft, ...values },
        editFill: { modelId: read.modelId!, values, notes: read.fill!.notes },
      }));
    } catch (err) {
      if (store.get().editingId === id) {
        store.set({ editSaving: false, editError: err instanceof Error ? err.message : 'The model could not be read.' });
      }
    }
  },

  /**
   * The same dialog on a blank record: Home's New bridge.
   *
   * Blank rather than seeded from the selected structure, because a copy of
   * another bridge's inventory saved under a new id is a record that looks
   * surveyed and is not.
   */
  openNewBridge(): void {
    store.set({
      editingId: '',
      editCreating: true,
      editDraft: {},
      editSaving: false,
      editError: null,
      editFill: null,
      editOther: [],
    });
  },

  closeEdit(): void {
    if (store.get().editingId === null) return;
    store.set({
      editingId: null,
      editCreating: false,
      editDraft: {},
      editSaving: false,
      editError: null,
      editFill: null,
      editOther: [],
    });
  },

  setEditField(key: string, value: string): void {
    store.set((s) => ({ editDraft: { ...s.editDraft, [key]: value } }));
  },

  /** A dropdown field typed instead of picked ("Other…"), or back to its list. */
  setEditOther(key: string, typing: boolean): void {
    store.set((s) => ({
      editOther: typing ? [...s.editOther.filter((k) => k !== key), key] : s.editOther.filter((k) => k !== key),
      editDraft: typing ? { ...s.editDraft, [key]: '' } : s.editDraft,
    }));
  },

  /** Validate the draft, send it, and adopt the result into the in-memory register. */
  async saveEdit(): Promise<void> {
    if (store.get().editCreating) return actions.saveNewBridge();
    const id = store.get().editingId;
    if (!id) return;

    let payload: Record<string, unknown>;
    try {
      payload = buildEditPayload(store.get().editDraft);
    } catch (err) {
      store.set({ editError: err instanceof Error ? err.message : 'Invalid input.' });
      return;
    }
    /* Filled from a model: the server logs what is still the model's as it (LC-83). */
    const fill = store.get().editFill;
    if (fill) payload = { ...payload, fromModel: fill.modelId };

    store.set({ editSaving: true, editError: null });
    try {
      const bridge = await api.updateBridge(id, payload);
      adopt(bridge);
      // The dialog may have been closed (or reopened on a different bridge)
      // while the request was in flight — don't reopen or re-error over it.
      if (store.get().editingId === id) {
        store.set({ editingId: null, editDraft: {}, editSaving: false, editError: null, editFill: null });
      }
    } catch (err) {
      if (store.get().editingId === id) {
        store.set({
          editSaving: false,
          editError: err instanceof Error ? err.message : 'The edit could not be saved.',
        });
      }
    }
  },

  /**
   * Create the structure in the draft, then open its record in Bridge Inspection.
   *
   * The register is re-read rather than the row patched in: `adopt` replaces a
   * record it already holds, and this one is not held anywhere yet.
   */
  async saveNewBridge(): Promise<void> {
    const draft = store.get().editDraft;
    const id = (draft['id'] ?? '').trim().toUpperCase();

    let payload: Record<string, unknown>;
    try {
      if (id === '') throw new Error('"Asset id" is required.');
      /* A planned structure (LC-10) is the same record with its first phase
         row, and `structure.create` rather than `inventory.edit` on the
         server. Somebody who may make only one kind makes that kind. */
      const planned = draft['phase'] === 'planned' || (draft['phase'] === undefined && !roleCan(store.get(), 'inventory.edit'));
      payload = { id, ...buildEditPayload(draft), ...(planned ? { phase: 'planned' } : {}) };
    } catch (err) {
      store.set({ editError: err instanceof Error ? err.message : 'Invalid input.' });
      return;
    }

    store.set({ editSaving: true, editError: null });
    try {
      await api.createBridge(payload);
      await actions.hydrate();
      if (!store.get().editCreating) return; // closed while in flight
      actions.closeEdit();
      actions.selectBridge(id);
      /* A planned structure has no inspection to open on (LC-13): its first
         application is the programme. */
      actions.openApp(payload['phase'] === 'planned' ? 'scheduler' : 'inspection');
    } catch (err) {
      if (store.get().editCreating) {
        store.set({
          editSaving: false,
          editError: err instanceof Error ? err.message : 'The structure could not be created.',
        });
      }
    }
  },

  /* --------------------------- adding an inspection --------------------------- */

  /** Open the "Add inspection" dialog for a bridge, with a blank draft. */
  openAddInspection(bridgeId: string): void {
    store.set({
      addingInspectionId: bridgeId,
      inspectionDraft: emptyInspectionDraft(),
      inspectionSaving: false,
      inspectionError: null,
    });
  },

  /** Pick the tab under the model on the record's Defects page. */
  showDefectsTab(tab: State['defectsTab']): void {
    store.set({ defectsTab: tab });
  },

  /** Narrow the Inspection tab's table to the rows whose text contains `query`. */
  setInspectionFilter(query: string): void {
    store.set({ inspectionFilter: query });
  },

  /** List every component on the Inspection tab, or only those inspected. */
  setInspectedOnly(on: boolean): void {
    store.set({ inspectedOnly: on });
  },

  /* --------------------------- a defect photograph, full size --------------------------- */

  /** Enlarge one defect's photograph over the record. */
  /** Show one defect's details on the record's Defects page; null goes back to the list. */
  showDefect(id: string | null): void {
    store.set(
      id === null
        ? { defectFocusId: null, defectMove: null, defectMoveError: null, defectSight: null }
        : { defectFocusId: id, detailView: 'defects', defectsTab: 'defects', detailPanelCollapsed: false, defectMove: null, defectMoveError: null, defectSight: null },
    );
    if (id !== null) void actions.loadDefectHistory(id);
  },

  /**
   * Read one defect's history (LC-62). Only the live register keeps one; a
   * demo record, or one on a device that cannot reach it, says so instead.
   */
  async loadDefectHistory(id: string): Promise<void> {
    if (store.get().backend !== 'live') {
      store.set({ defectHistory: { id, history: null, error: null } });
      return;
    }
    try {
      const history = await api.fetchDefectHistory(id);
      if (store.get().defectFocusId === id) store.set({ defectHistory: { id, history, error: null } });
    } catch (err) {
      store.set({
        defectHistory: { id, history: null, error: err instanceof Error ? err.message : 'The history could not be read.' },
      });
    }
  },

  /** Open the form for a move that carries more than its status: a repair, a verification, a reopen. */
  openDefectMove(id: string, to: string): void {
    store.set({
      defectMove: { id, to, note: '', repairedOn: new Date().toISOString().slice(0, 10), repairRef: '' },
      defectMoveError: null,
      defectSight: null,
    });
  },

  setDefectMoveField(key: 'note' | 'repairedOn' | 'repairRef', value: string): void {
    const m = store.get().defectMove;
    if (m) store.set({ defectMove: { ...m, [key]: value } });
  },

  cancelDefectMove(): void {
    store.set({ defectMove: null, defectMoveError: null });
  },

  /** Send the move being written. Refused here as the endpoint refuses it. */
  async submitDefectMove(): Promise<void> {
    const m = store.get().defectMove;
    if (!m) return;
    const to = m.to === 'reopen' ? 'Open' : m.to;
    if (m.to === 'reopen' && m.note.trim() === '') {
      store.set({ defectMoveError: 'Say why it is reopened.' });
      return;
    }
    if (to === 'Repaired' && (!isIsoDay(m.repairedOn) || m.repairRef.trim() === '')) {
      store.set({ defectMoveError: 'A repair has the day it was done and a reference.' });
      return;
    }
    try {
      await api.updateDefectStatus(m.id, to, {
        ...(m.note.trim() ? { note: m.note.trim() } : {}),
        ...(to === 'Repaired' ? { repairedOn: m.repairedOn, repairRef: m.repairRef.trim() } : {}),
      });
      store.set({ defectMove: null, defectMoveError: null });
      await actions.refreshAllDefects();
      await actions.loadDefectHistory(m.id);
    } catch (err) {
      store.set({ defectMoveError: err instanceof Error ? err.message : 'That could not be recorded.' });
    }
  },

  /** Open the form for a later inspection seeing the open defect again (LC-62). */
  openDefectSight(id: string): void {
    const d = store.get().registerDefects.find((x) => x.id === id);
    if (!d) return;
    store.set({
      defectSight: { id, inspectionId: '', severity: d.lastSeen?.severity ?? d.severity, values: {}, note: '' },
      defectMove: null,
      defectMoveError: null,
    });
  },

  setDefectSightField(key: 'inspectionId' | 'severity' | 'note', value: string): void {
    const v = store.get().defectSight;
    if (v) store.set({ defectSight: { ...v, [key]: value } });
  },

  setDefectSightValue(key: string, value: string): void {
    const v = store.get().defectSight;
    if (v) store.set({ defectSight: { ...v, values: { ...v.values, [key]: value } } });
  },

  cancelDefectSight(): void {
    store.set({ defectSight: null, defectMoveError: null });
  },

  /** Send the sighting being written: measured where a figure was typed, else not measured. */
  async submitDefectSight(): Promise<void> {
    const v = store.get().defectSight;
    if (!v) return;
    if (!v.inspectionId) {
      store.set({ defectMoveError: 'Say which inspection saw it.' });
      return;
    }
    const figures: Record<string, number> = {};
    for (const [key, raw] of Object.entries(v.values)) {
      if (raw.trim() === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        store.set({ defectMoveError: 'A measurement is a number.' });
        return;
      }
      figures[key] = n;
    }
    try {
      await api.recordDefectSighting(v.id, {
        inspectionId: v.inspectionId,
        severity: v.severity,
        ...(v.note.trim() ? { note: v.note.trim() } : {}),
        ...(Object.keys(figures).length ? { quantity: { method: 'measured', ...figures } as DefectQuantity } : {}),
      });
      store.set({ defectSight: null, defectMoveError: null });
      await actions.refreshAllDefects();
      await actions.loadDefectHistory(v.id);
    } catch (err) {
      store.set({ defectMoveError: err instanceof Error ? err.message : 'That could not be recorded.' });
    }
  },

  openPhoto(defectId: number, n = 1): void {
    store.set({ photoDefectId: defectId, photoDefectN: n });
  },

  /**
   * Step to the next or previous photograph of the open defect, wrapping at
   * either end. The count is the caller's, off the row it is drawing, so this
   * does not look the defect up a second time.
   */
  stepPhoto(delta: 1 | -1, count: number): void {
    const s = store.get();
    if (s.photoDefectId === null || count < 2) return;
    const n = ((s.photoDefectN - 1 + delta + count) % count) + 1;
    store.set({ photoDefectN: n });
  },

  closePhoto(): void {
    if (store.get().photoDefectId === null) return;
    store.set({ photoDefectId: null });
  },

  closeAddInspection(): void {
    if (store.get().addingInspectionId === null) return;
    store.set({ addingInspectionId: null, inspectionSaving: false, inspectionError: null });
  },

  setInspectionField(key: keyof InspectionDraft, value: string): void {
    store.set((s) => ({ inspectionDraft: { ...s.inspectionDraft, [key]: value } }));
  },

  /**
   * Attach one already-downscaled photograph to the draft.
   *
   * The downscale happens in the dialog, before this is called, because it is
   * asynchronous and can fail on a file the browser cannot decode — and the
   * person who just picked that file needs to be told, which is a dialog concern
   * rather than a store one.
   */
  /** Type a condition against one element. An empty string clears it back to unassessed. */
  setElementRating(elementId: string, value: string): void {
    actions.setElementBox(elementId, 'rating', value);
  },

  /**
   * Type into one of DoR's three boxes for one element.
   *
   * One action for all three rather than three near-identical ones. They differ
   * only in which map on the draft they write to, and a fourth box — if DoR ever
   * adds one — should be a row in the table below rather than another copy of
   * this function.
   *
   * Clearing the condition clears the other two with it. That is not tidiness:
   * `buildElements` refuses an extent with no condition beside it, so leaving
   * them behind would make clearing a rating produce a validation error about a
   * box the inspector cannot see any more.
   */
  setElementBox(elementId: string, box: 'rating' | 'extent' | 'urgency', value: string): void {
    const MAP = { rating: 'elements', extent: 'extents', urgency: 'urgencies' } as const;
    store.set((s) => {
      const d = s.inspectionDraft;
      const next: InspectionDraft = {
        ...d,
        [MAP[box]]: { ...d[MAP[box]], [elementId]: value },
      };
      if (box === 'rating' && value.trim() === '') {
        next.extents = { ...next.extents, [elementId]: '' };
        next.urgencies = { ...next.urgencies, [elementId]: '' };
      }
      return { inspectionDraft: next };
    });
  },

  /**
   * Add an item of the inspector's own to the breakdown. Refused with a message
   * rather than ignored when it is blank, too long, or already on the list —
   * one rating per element per inspection, whichever list it came from.
   */
  addCustomElement(label: string): boolean {
    const name = label.trim().replace(/\s+/g, ' ');
    if (name === '') return false;
    if (name.length > CUSTOM_ELEMENT_MAX) {
      actions.setInspectionError(`An item name can be at most ${CUSTOM_ELEMENT_MAX} characters.`);
      return false;
    }
    const id = customElementId(name);
    const d = store.get().inspectionDraft;
    const lower = name.toLowerCase();
    const taken =
      BRIDGE_ELEMENTS.some((e) => e.label.toLowerCase() === lower) ||
      d.customElements.some((c) => elementLabel(c).toLowerCase() === lower) ||
      isKnownElement(id);
    if (taken) {
      actions.setInspectionError(`"${name}" is already on the list.`);
      return false;
    }
    store.set((s) => ({
      inspectionDraft: { ...s.inspectionDraft, customElements: [...s.inspectionDraft.customElements, id] },
      inspectionError: null,
    }));
    return true;
  },

  /** Take an added item off the breakdown, with whatever was typed against it. */
  removeCustomElement(id: string): void {
    store.set((s) => {
      const d = s.inspectionDraft;
      const drop = (m: Record<string, string>) => {
        const { [id]: _, ...rest } = m;
        return rest;
      };
      return {
        inspectionDraft: {
          ...d,
          customElements: d.customElements.filter((c) => c !== id),
          elements: drop(d.elements),
          extents: drop(d.extents),
          urgencies: drop(d.urgencies),
        },
      };
    });
  },

  addInspectionPhoto(dataUrl: string): void {
    store.set((s) => {
      if (s.inspectionDraft.photos.length >= MAX_INSPECTION_PHOTOS) return {};
      return { inspectionDraft: { ...s.inspectionDraft, photos: [...s.inspectionDraft.photos, dataUrl] } };
    });
  },

  /**
   * Report a problem with something the dialog did on its own — today, a picked
   * file the browser could not decode. Shares the banner the save path uses,
   * because to the person looking at it there is one place errors appear.
   */
  /** Enlarge one photograph from an inspection on the selected structure. */
  openInspectionPhoto(photoId: string): void {
    store.set({ inspectionPhotoId: photoId });
  },

  closeInspectionPhoto(): void {
    if (store.get().inspectionPhotoId === null) return;
    store.set({ inspectionPhotoId: null });
  },

  setPhotoLayout(layout: PhotoLayout): void {
    store.set({ photoLayout: layout });
  },

  togglePhotoOrder(): void {
    store.set((s) => ({ photoNewestFirst: !s.photoNewestFirst }));
  },

  setPhotoMonth(month: string): void {
    store.set({ photoMonth: month });
  },

  setReturnLayout(layout: ReturnLayout): void {
    store.set({ returnLayout: layout });
  },

  setReturnMonth(month: string): void {
    store.set({ returnMonth: month });
  },

  setWorksLayout(layout: WorksLayout): void {
    store.set({ worksLayout: layout });
  },

  setWorksMonth(month: string): void {
    store.set({ worksMonth: month });
  },

  /** Open one works entry on the selected structure, with the model. */
  openWorksEntry(entryId: string): void {
    store.set({ worksEntryId: entryId });
  },

  closeWorksEntry(): void {
    if (store.get().worksEntryId === null) return;
    store.set({ worksEntryId: null });
  },

  /** Enlarge one construction entry's photograph on the selected structure. */
  openProgressPhoto(entryId: string): void {
    store.set({ progressPhotoId: entryId });
  },

  closeProgressPhoto(): void {
    if (store.get().progressPhotoId === null) return;
    store.set({ progressPhotoId: null });
  },

  setInspectionError(message: string): void {
    store.set({ inspectionError: message });
  },

  removeInspectionPhoto(index: number): void {
    store.set((s) => ({
      inspectionDraft: {
        ...s.inspectionDraft,
        photos: s.inspectionDraft.photos.filter((_, i) => i !== index),
      },
    }));
  },

  /** Validate the draft, send it, and adopt the result into the in-memory register. */
  async saveInspection(): Promise<void> {
    const id = store.get().addingInspectionId;
    if (!id) return;

    let insp: Inspection;
    try {
      insp = buildInspection(store.get().inspectionDraft);
    } catch (err) {
      store.set({ inspectionError: err instanceof Error ? err.message : 'Invalid input.' });
      return;
    }

    /* Minted before the attempt, not after it fails: the id has to be the same
       whether this lands now or from the queue in an hour, or a retry would
       record the visit twice. */
    const clientId = crypto.randomUUID();

    store.set({ inspectionSaving: true, inspectionError: null });
    try {
      const bridge = await api.addInspection(id, insp, store.get().inspectionDraft.photos, clientId);
      adopt(bridge);
      // The dialog may have been closed (or reopened on a different bridge)
      // while the request was in flight — don't reopen or re-error over it.
      if (store.get().addingInspectionId === id) {
        store.set({ addingInspectionId: null, inspectionSaving: false, inspectionError: null });
      }
    } catch (err) {
      /* Unreachable, not refused. A null status means the request never got an
         answer — no signal, DNS, a timeout — which is the case this whole item
         exists for. A 4xx is the register saying no, and queueing that would be
         queueing a mistake. */
      const unreachable = err instanceof api.ApiError && err.status === null;

      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({
          kind: 'inspection',
          bridgeId: id,
          clientId,
          body: { ...insp, clientId, photos: store.get().inspectionDraft.photos },
        });

        /* Shown on the record straight away, marked as not yet synced. An
           inspector who records something at the structure and cannot see it
           records it again.

           SKIPPED, NOT ABANDONED, where there is no record to show it on: the
           entry is already in the queue by this point and goes up when the
           signal returns either way. Only reachable on an empty register, which
           is a device that has not hydrated rather than a device with nothing to
           inspect — so losing the optimistic paint is right and losing the
           queued write would not be. */
        const current = findBridge(id);
        if (current !== null) {
          adopt({
            ...current,
            inspections: [...current.inspections, { ...insp, clientId, pending: true }],
          });
        }

        await actions.refreshQueue();
        if (store.get().addingInspectionId === id) {
          store.set({ addingInspectionId: null, inspectionSaving: false, inspectionError: null });
        }
        return;
      }

      if (store.get().addingInspectionId === id) {
        store.set({
          inspectionSaving: false,
          inspectionError: err instanceof Error ? err.message : 'The inspection could not be saved.',
        });
      }
    }
  },

  /* --------------------------- importing a model --------------------------- */

  /**
   * An import has started on this structure's model.
   *
   * Driven from src/modeler/importModel.ts, which owns the hidden frame; this
   * is only what the record reads to show it. Clearing `modelImportResult` at
   * the same time is deliberate: the previous import's summary must not sit
   * over the one now running.
   */
  beginModelImport(
    bridgeId: string,
    /** The model it will land in, or null when one has to be created for it. */
    model: { id: string; name: string } | null,
  ): void {
    store.set({
      modelImport: {
        bridgeId,
        modelId: model?.id ?? '',
        modelName: model?.name ?? '',
        /* Called from the click handler, before anything is read or requested —
           see the note on `reading` above. The phase the record shows first is
           therefore the first thing that actually happens. */
        phase: 'reading',
        steps: model
          ? ['reading', 'storing', 'opening', 'importing', 'saving', 'geometry']
          : ['reading', 'creating', 'storing', 'opening', 'importing', 'saving', 'geometry'],
      },
      modelImportResult: null,
    });
  },

  /**
   * Record whether this device holds a model's imported mesh.
   *
   * Only ever called from restoreImportedModel, which is the only thing that knows:
   * the answer needs an IndexedDB read, and neither the record nor the viewport can
   * wait on a database while painting.
   */
  setModelMeshState(modelId: string, present: boolean): void {
    const cur = store.get().modelMeshState;
    if (cur?.modelId === modelId && cur.present === present) return;
    store.set({ modelMeshState: { modelId, present } });
  },

  /** Forget the answer — a different model is on screen, or one has just gone. */
  clearModelMeshState(): void {
    if (store.get().modelMeshState === null) return;
    store.set({ modelMeshState: null });
  },

  /**
   * Announce a restore: this device fetching a mesh it does not have.
   *
   * Fewer phases than an import, and each absence is a fact rather than a saving.
   * There is no `creating` (the model exists — that is why there is something to
   * restore), no `storing` (the export came FROM the server), and no `geometry` (an
   * export of unchanged state would produce the bytes already in the bucket).
   * `reading` is the fetch of the stored export, which is the first thing that
   * happens and the one part that is not the modeller's work.
   */
  beginModelRestore(bridgeId: string, model: { id: string; name: string }): void {
    store.set({
      modelImport: {
        bridgeId,
        modelId: model.id,
        modelName: model.name,
        phase: 'reading',
        restoring: true,
        steps: ['reading', 'opening', 'importing', 'saving'],
      },
      modelImportResult: null,
    });
  },

  /**
   * Name the model an import is landing in, once it exists.
   *
   * Only reached on a first import, where the model is created after the run has
   * already been announced. Everything on screen that names it reads this, so it
   * fills in rather than appearing late.
   */
  modelImportTarget(model: { id: string; name: string }): void {
    const current = store.get().modelImport;
    if (!current) return;
    store.set({ modelImport: { ...current, modelId: model.id, modelName: model.name } });
  },

  modelImportPhase(phase: ModelImportPhase): void {
    const current = store.get().modelImport;
    if (!current) return;
    store.set({ modelImport: { ...current, phase } });
  },

  /**
   * The import is over, one way or the other.
   *
   * The model index is re-read rather than patched: an import changes the
   * model's size and its `updatedAt`, and on a first import it is also what
   * gives the model a thumbnail — none of which this side can work out for
   * itself. The nonce bump is what makes the record fetch that new thumbnail
   * instead of the one it already has.
   */
  async finishModelImport(result: ModelImportResult): Promise<void> {
    /* NO SUMMARY FOR A RESTORE. A restore is the app repairing itself — nobody
       chose a file, so a dialog listing what was applied would be an interruption
       about a device-local cache the reader has never heard of, on a record they
       opened to read an inspection report. What they get instead is the truth
       either way: the bridge appears, or the panel goes on saying the model has no
       geometry on this device. The console carries the detail. */
    const quiet = store.get().modelImport?.restoring === true;

    /* THE INDEX IS RE-READ FIRST, before the summary goes on screen. What the
       summary shows depends on it: an import that brought the source's own mesh
       makes the model a displayed one, and the parameter warnings then describe
       geometry that is never drawn. Setting the result first would show those
       warnings for the moment it takes the index to land, and then take them
       away — which reads as the register changing its mind. */
    store.set({
      modelImport: null,
      modelThumbNonce: result.ok ? store.get().modelThumbNonce + 1 : store.get().modelThumbNonce,
    });
    if (result.ok) await actions.refreshModels();
    if (quiet) {
      if (!result.ok) console.warn('[restore] could not restore the model', result.error);
      return;
    }
    const inventory = result.ok ? await fillRecordFromModel(result.bridgeId, result.modelId) : null;
    store.set({ modelImportResult: inventory ? { ...result, inventory } : result });
  },

  closeModelImportResult(): void {
    if (store.get().modelImportResult === null) return;
    store.set({ modelImportResult: null });
  },

  /* --------------------------- model geometry --------------------------- */

  geometryBuildStarted(bridgeId: string, modelId: string): void {
    store.set({ geometryBuild: { bridgeId, modelId }, geometryError: null });
  },

  geometryBuildFinished(error: string | null): void {
    store.set({ geometryBuild: null, geometryError: error });
  },

  /* --------------------------- the network map --------------------------- */

  /**
   * Look at a new centre and zoom.
   *
   * One action for pan and zoom together because that is how a map moves: a
   * wheel zoom about the cursor changes both, and splitting them would paint an
   * intermediate view nobody asked for. The map clamps the zoom itself — it owns
   * what its tile source can serve.
   */
  mapMoved(view: MapView): void {
    store.set({ mapView: view });
  },

  /** Back to the home view — whatever the map works out that is. */
  mapHome(): void {
    store.set({ mapView: null });
  },

  /** Show this stretch of a road on its route card. */
  pickRouteStretch(road: string, from: number): void {
    const s = store.get();
    store.set({ routeStretch: { ...s.routeStretch, [road]: { from, forId: s.selectedId } } });
  },

  /**
   * Select a structure and bring the map onto it.
   *
   * What a click on a pin, or on a marker in the route strips below, does — see
   * selectOnMap in src/map/nepalMap.ts, which decides the view. Selection and view
   * move together deliberately: the map highlights the selected structure and draws
   * its footprint, and zooming to a bridge without selecting it would leave the
   * highlight on whichever one was selected before.
   *
   * Does NOT navigate. That is the second click's job, so that "show me this one"
   * and "open its record" are different gestures rather than the same one.
   */
  focusBridgeOnMap(id: string, view: MapView): void {
    /* THROUGH `selectInto`, NOT PAST IT — and it used to be past it, with a bare
       `store.set({ selectedId, mapView })`.

       Moving the selection is not just writing one field. Everything hung off a
       structure — the model index, the change log, the construction record — is
       read when the selection moves, and cleared when it moves, and this path did
       neither. So a pin click left the store naming one bridge and holding the
       PREVIOUS one's collections, with nothing on its way to correct them.

       On the map that is invisible, which is why it survived: this action draws a
       highlight and a footprint, and neither reads a model. It surfaces on the
       record opened afterwards, because opening it is a screen change and not a
       selection change — `selectInto` never runs, so the reads never happen. The
       symptom was a record showing the right structure's name over "No 3D model
       built", for a bridge whose geometry was in the bucket all along, with no
       request for it in the network log to explain the gap. Where the previous
       structure HAD models, it is worse than an empty card: it is that one's
       models listed under this one's name.

       The view is set first so the two land in one repaint, and the screen stays
       where it is — `selectInto(id, null)` moves the selection and nothing else,
       which is what keeps "show me this one" and "open its record" two gestures. */
    store.set({ mapView: view });
    selectInto(id, null);
  },

  /** The map's box was measured, or has changed size. */
  mapResized(width: number, height: number): void {
    const current = store.get().mapSize;
    // Sub-pixel churn from a panel drag would otherwise repaint every frame.
    if (Math.abs(current.width - width) < 1 && Math.abs(current.height - height) < 1) return;
    store.set({ mapSize: { width, height } });
  },

  /** A tile did not load. Turns the map into the bundled outline. */
  mapTilesFailed(): void {
    if (store.get().mapTiles === 'failed') return;
    store.set({ mapTiles: 'failed' });
  },

  /** The user asked the map to try the tile server again. */
  mapRetryTiles(): void {
    store.set({ mapTiles: 'unknown' });
  },

  /* --------------------------- placing a structure --------------------------- */

  /**
   * Start placing a structure by hand, from wherever it currently is.
   *
   * Only ever the selected one, and the map is already on it — the action is
   * reached from the map's own bar, not from a list. A structure with no position
   * starts at the centre of the view, which is the one place the user has
   * definitely just looked at.
   */
  startPlacing(b: { id: string; lat: number | null; lng: number | null; deckBearing: number | null }, at: { lat: number; lng: number }): void {
    store.set({
      mapPlacing: {
        bridgeId: b.id,
        lat: b.lat ?? at.lat,
        lng: b.lng ?? at.lng,
        bearing: b.deckBearing,
        from: { lat: b.lat, lng: b.lng, bearing: b.deckBearing },
      },
      mapPlaceError: null,
    });
  },

  /** Move or rotate the draft. Called from the drag handlers, once per gesture. */
  placeAt(patch: { lat?: number; lng?: number; bearing?: number }): void {
    const current = store.get().mapPlacing;
    if (!current || store.get().mapPlaceSaving) return;
    store.set({ mapPlacing: { ...current, ...patch } });
  },

  /** Give up, leaving the record as it was. */
  cancelPlacing(): void {
    if (store.get().mapPlacing === null) return;
    store.set({ mapPlacing: null, mapPlaceError: null, mapPlaceSaving: false });
  },

  /**
   * Write the placement to the register.
   *
   * Through the same `PATCH /api/bridges/:id` as the edit dialog, with the same
   * whole-record payload — a position is an inventory field like any other, and
   * giving it a private endpoint would mean two ways to write one row. The record
   * in memory is replaced by what the server returns rather than by the draft, so
   * what the map draws afterwards is what was actually stored.
   */
  async savePlacing(): Promise<void> {
    const draft = store.get().mapPlacing;
    if (!draft || store.get().mapPlaceSaving) return;

    store.set({ mapPlaceSaving: true, mapPlaceError: null });
    try {
      const current = findBridge(draft.bridgeId);
      /* No record to place. Only reachable on an empty register, where nothing
         can have been dragged onto the map in the first place. */
      if (current === null) {
        store.set({ mapPlaceSaving: false, mapPlacing: null });
        return;
      }
      const payload = buildEditPayload(draftFrom(current));
      payload['lat'] = draft.lat;
      payload['lng'] = draft.lng;
      payload['deckBearing'] = draft.bearing;

      const bridge = await api.updateBridge(draft.bridgeId, payload);
      adopt(bridge);
      // Still the same placement in flight? A second structure picked up while
      // this was saving must not have its draft closed by this one's response.
      if (store.get().mapPlacing?.bridgeId === draft.bridgeId) {
        store.set({ mapPlacing: null, mapPlaceSaving: false, mapPlaceError: null });
      }
    } catch (err) {
      store.set({
        mapPlaceSaving: false,
        mapPlaceError: err instanceof Error ? err.message : 'The position could not be saved.',
      });
    }
  },

  /**
   * Flat map or ground.
   *
   * Clears any placement in progress: the handles and the drag arithmetic belong
   * to the 2D map, and carrying a half-finished placement into a view that cannot
   * show it would leave a draft nobody can either finish or see.
   */
  mapModeSelected(mode: MapMode): void {
    if (store.get().mapMode === mode) return;
    store.set({ mapMode: mode, mapPlacing: null, mapPlaceError: null });
  },

  /**
   * Draw a different base map.
   *
   * Clears `mapTiles` with it, and that is the point rather than housekeeping: a
   * failure belongs to the server that failed. OpenTopoMap is a volunteer host and
   * will be down when OSM's is not, so a switch has to be given its own chance —
   * otherwise the first source's failure would leave the second showing an outline
   * and claiming its tiles were unavailable without ever having asked for one.
   *
   * The view is deliberately kept. Changing the style is not changing where you
   * are looking, and re-fitting the map under someone who had panned to a bridge
   * would be the wrong answer to a question about layers.
   */
  mapBaseSelected(base: MapBase): void {
    if (store.get().mapBase === base) return;
    store.set({ mapBase: base, mapTiles: 'unknown' });
  },

  /**
   * Colour the pins by screening band, or leave them plain.
   *
   * Nothing else moves. This is a legend change, not a filter: every structure
   * stays on the map and keeps its place, because a screening layer that hid the
   * structures it could not screen would hide exactly the ones worth chasing.
   */
  mapPaintSelected(paint: MapPaint): void {
    if (store.get().mapPaint === paint) return;
    store.set({ mapPaint: paint });
  },

  /** Break the network summary down by one of the tenant's own groupings. */
  summaryGroupedBy(by: GroupBy): void {
    if (store.get().summaryBy === by) return;
    store.set({ summaryBy: by });
  },

  /**
   * Switch the interface language.
   *
   * The locale lives in a module variable as well as in state, because every
   * component reads it through `t()` rather than taking it as an argument @
   * see src/data/locale.ts. Both are set here so the two cannot disagree, and
   * this is the only place that writes either.
   */
  localeSelected(locale: Locale): void {
    if (store.get().locale === locale) return;
    setLocale(locale);
    store.set({ locale });
  },

  /**
   * Switch shells — the toggle in Options, and nothing else.
   *
   * Moves the screen too where it has to: switching to the field shell while
   * looking at something the field shell refuses would otherwise leave a tab bar
   * under a network summary. In practice the toggle lives on `options`, which
   * both shells can draw, so this is the belt rather than the braces.
   */
  shellChosen(shell: Shell): void {
    if (store.get().shell === shell) return;
    store.set((s) => ({ shell, screen: shellScreen(shell, s.screen) }));
  },

  /**
   * Apply `?shell=` if it is there, once, at boot.
   *
   * Separate from `shellChosen` because it is not a choice somebody made in the
   * interface — it is an instruction in the address, and it has to beat both the
   * stored preference and the probe. Called by src/main.ts before the first
   * paint; silent when the parameter is absent, which is almost always.
   */
  applyShellOverride(search: string): void {
    const forced = shellOverride(search);
    if (forced) actions.shellChosen(forced);
  },

  /**
   * Settle which department this session is working in.
   *
   * Called twice, and the two calls are different jobs:
   *
   *   · at boot from src/main.ts, BEFORE the first paint, with `?tenant=` and
   *     the build's own `VITE_TENANT`. This is what stops a single-department
   *     deployment showing one frame of the wrong letterhead while it waits for
   *     the network — and what makes a support session reproducible offline.
   *   · from `hydrate`, with what /api/health says the signed-in user's row
   *     holds. This is the AUTHORITATIVE answer in a shared deployment, and it
   *     is the server's: see the header of src/tenant/registry.ts on why the
   *     client never gets to choose which register it reads.
   *
   * A change drops the rows in hand. They belong to the department that was in
   * force when they were fetched, and showing them under another department's
   * branding for the second and a half until the next fetch lands is the exact
   * failure this whole arrangement exists to prevent.
   */
  tenantResolved(session?: string | null): void {
    const next = resolveTenant({
      url: new URLSearchParams(window.location.search).get('tenant'),
      build: buildTenant(),
      session: session ?? null,
    });
    if (!setActive(next)) return;
    resetRegister();

    /* A LANGUAGE THE NEW DEPARTMENT DOES NOT OFFER GOES BACK TO ENGLISH, in the
       store as well as in the catalogue. Both, and that is the bug this line
       exists for: setting only the catalogue left `s.locale` reading `ne` on a
       DPWH session that has no language control to change it with — a
       preference nobody could see or undo, which would come back the moment
       they were switched to a department that does offer it. The preference is
       per device and outlives a tenant switch on a shared machine. */
    const locale: Locale = active().locales.includes(store.get().locale)
      ? store.get().locale
      : 'en';
    setLocale(locale);

    store.set((s) => ({
      locale,
      registerRevision: s.registerRevision + 1,
      /* The selected structure is an id in the register that is no longer
         loaded. Cleared rather than carried, or the detail screen looks up a
         bridge that is not there and falls back to the first of a register the
         reader did not ask for. */
      selectedId: '',
      /* A grouping the new department does not offer, dropped so its own default
         applies — the same rule the persisted preference follows on hydrate. */
      summaryBy: isGroupBy(s.summaryBy) ? s.summaryBy : defaultGroupBy(),
    }));
  },

  /** Set by src/modeler/viewport.ts: false the moment a new hero viewport starts loading, true on its `ready`. */
  setHeroViewportReady(ready: boolean): void {
    store.set({ heroViewportReady: ready });
  },

  /** Set by src/modeler/viewport.ts on the hero viewport's `component-tree` response, or null to clear it. */
  setHeroComponentTree(roots: ComponentNode[] | null): void {
    store.set({ heroComponentTree: roots });
  },

  /** Fold or unfold one node of the Components tree, keyed by its label path. */
  toggleComponentNode(path: string): void {
    store.set((s) => ({
      componentTreeCollapsed: s.componentTreeCollapsed.includes(path)
        ? s.componentTreeCollapsed.filter((p) => p !== path)
        : [...s.componentTreeCollapsed, path],
    }));
  },

  /**
   * Open every node of the Components tree again — called whenever the tree it
   * describes changes.
   *
   * A fresh `[]` would be a new array every time and so always look like a
   * change; nothing is patched when there was nothing folded, which is the usual
   * case each time the viewport is built or torn down.
   */
  resetComponentTreeExpanded(): void {
    store.set((s) => (s.componentTreeCollapsed.length ? { componentTreeCollapsed: [] } : {}));
  },

  /**
   * Read the selected structure's model index, dropping the reply if the
   * selection has moved on while it was in flight.
   *
   * It used to read the structure's defects alongside it — the modeller's own log
   * was a per-structure endpoint and there was no bulk one, because the log lived
   * inside each model's state. Migration 0015 made defects rows, so they arrive
   * for the whole network in the one request `refreshAllDefects` makes, and this
   * is down to a single collection.
   *
   * A failure is not worth interrupting anyone over: the panel renders an empty
   * state, and an unreachable model index says nothing about the condition record
   * itself.
   */
  async loadBridgeContext(): Promise<void> {
    if (store.get().backend !== 'live') return;

    const id = store.get().selectedId;

    /* NO STRUCTURE, NO READS. The selection is empty between a tenant switch and
       the register that follows it — see `adoptTenant`, which clears it because an
       id from the department being left names nothing in the one being joined. A
       read started in that gap asks the server about the empty string, which is
       not a bridge id: `assertBridgeId` refuses it and the answer is a 400,
       measured in this project's own dev session at 3.5 seconds of a boot nobody
       had to spend. Both reads below are per-structure and neither has a
       structure, so there is nothing here to ask yet. */
    if (!id) return;

    /* AND THE BOOT HAS NO `selectInto` TO HAVE SEEDED IT. `selectedId` is
       persisted, so a reload lands on a record without the selection ever
       moving — which is the commonest way to arrive at one, and the one where
       nothing would have read the device's memory of what is modelled here.
       Guarded on all three so it can never overwrite an index the register has
       actually given: a re-selection of the structure in hand keeps what it has. */
    const held = store.get();
    if (held.modelsRead === 'unknown' && !held.modelsRemembered && held.models.models.length === 0) {
      const remembered = recallModelIndex(id);
      if (remembered) store.set({ models: remembered, modelsRemembered: true });
    }

    /* BESIDE THE MODELS, NOT BEHIND THEM. This used to await the model index and
       only then start the construction read, on the reasoning that the models
       call is what decides whether the selection is still current. It does not
       have to be: `refreshProgress` captures the selection itself and checks it
       again before it writes, which is the same guard by the same rule, so the
       two reads are independent and the second need not wait out the first. On a
       remote D1 binding that was a whole round trip per selection, and the
       selection moves every time somebody taps a row. */
    /* AND ONLY WHERE SOMETHING WILL DRAW IT. Six reads settle inside
       `refreshProgress`, and none of them has a consumer in Bridge Inspection at
       a desk any more: the record gave up its Works and Programme cards when the
       site became four applications, and the register's row click moves the
       selection, so this was six requests per row click for nothing on screen.
       See `readsSiteRecord`, which asks what will be drawn rather than which
       product this is — the field record still carries the Works card.

       `progressRead` stays `unknown` when this is skipped, which is the honest
       value: nothing has been read. The screens that draw it are in applications
       where this is true. */
    const progressRead = readsSiteRecord(store.get().shell, store.get().screen)
      ? started(() => actions.refreshProgress())
      : Promise.resolve();
    /* The state history, which Bridge info lists under History. */
    if (store.get().phaseHistory === null) void actions.loadPhase();
    /* NULL IS "THE REGISTER DID NOT ANSWER", AND IT USED TO BE AN EMPTY INDEX.
       `.catch(() => NO_MODELS)` made a timed-out read indistinguishable from a
       structure nobody has modelled, and this read happens more than once in a
       boot — so a slow second answer overwrote a good first one and the record's
       3D view was torn down in front of somebody who was looking at it. See
       `modelsRead`, which is what the difference is now carried in. */
    const models = await api.fetchModels(id).catch(() => null);
    const s = store.get();
    if (s.selectedId !== id) {
      await progressRead;
      return;
    }
    if (models === null) {
      /* Said, and nothing else touched. What is held is still the last thing the
         register actually said about THIS structure — `selectInto` reseeded it
         from this device when the selection moved, and never from another
         structure's — so it is the best available and `modelsRemembered` says
         where it came from. */
      store.set({ modelsRead: 'unreachable' });
      await progressRead;
      return;
    }

    // A re-read is a new object even when it says exactly what state already
    // says — re-selecting the structure in hand, or closing the modeller having
    // changed nothing. Keeping the object we have means store.set sees no change
    // and the shell is not rebuilt; a rebuild would also tear the 3D viewport
    // down and reload it for a model index that never moved.
    store.set({
      models: sameJson(s.models, models) ? s.models : models,
      modelsRead: 'read',
      modelsRemembered: false,
    });
    /* AND ONLY HERE. The device's memory of this structure is written from a
       read that actually landed, never from a failure — an empty index put over
       a good one on a timeout is the bug `modelsRead` exists to prevent, and
       writing it to disk would let it outlive the page. */
    rememberModelIndex(id, models);
    /* What the model says about the structure, for Structure & levels. Only
       where there is a model to ask. */
    if (models.models.length > 0) void actions.refreshModelInventory();
    else store.set({ modelInventory: null });

    /* The construction record for the same structure, read here rather than by
       the card that draws it: the card is redrawn on every state change and a
       read hung off rendering would fire dozens of times while somebody scrolls.
       Started above; this is where it lands. */
    await progressRead;
  },

  /** Read what the selected structure's model says about its inventory. A failure leaves the last one read. */
  async refreshModelInventory(): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().backend !== 'live' || !id) return;
    try {
      const read = await api.fetchModelInventory(id);
      if (store.get().selectedId !== id) return;
      const next = read.modelId !== null && read.fill !== null ? { bridgeId: id, modelId: read.modelId, fill: read.fill } : null;
      store.set((s) => ({ modelInventory: sameJson(s.modelInventory, next) ? s.modelInventory : next }));
    } catch {
      /* The record's own values stand. */
    }
  },

  /**
   * Re-read the selected structure's construction record.
   *
   * Cheap and idempotent, and called from three places: when the selection
   * moves, when an entry is recorded, and when one is reviewed.
   *
   * THE GUARD IS THE SAME ONE `refreshChanges` KEEPS. A slow read for one
   * structure must not land on another — somebody who taps through three
   * records on a list while the first request is still out would otherwise see
   * the first bridge's pours under the third bridge's name.
   */
  async refreshProgress(): Promise<void> {
    const id = store.get().selectedId;

    /* A register being read from the bundled seed has no construction record
       and cannot have one: the seed is inventory, and progress lives only in
       D1. `unknown` rather than `read`, because "this device is not talking to
       a register" is not the same answer as "nothing has been built".
       And NO STRUCTURE, NO READS, the rule `loadBridgeContext` keeps: the
       Scheduler and Monitoring opened with nothing selected sent ten reads for
       `bridgeId=` and got ten 400s (QA 2026-09-30). */
    if (store.get().backend !== 'live' || !id) {
      store.set({
        progress: [],
        progressSummary: NO_PROGRESS_SUMMARY,
        siteReturns: [],
        incidents: [],
        documents: [],
        tests: [],
        items: [],
        baseline: null,
        proposedBaseline: null,
        baselineHistory: [],
        extensions: [],
        matches: [],
        progressRead: 'unknown',
        progressOf: null,
      });
      return;
    }

    try {
      /* FOUR READS IN PARALLEL AND ONE ANSWER. They are four tables only
         because they are four shapes; to the person looking at the record
         they are one thing — what has happened on this site. Settled
         together so the card cannot draw a half-read record, and awaited as a
         group so the four round trips cost one. */
      const [report, returns, incidents, documents, baselines, matches, tests, items, history, extensions, calendars] =
        await Promise.all([
          api.fetchProgress(id),
          api.fetchSiteReturns(id),
          api.fetchIncidents(id),
          api.fetchDocuments(id),
          api.fetchBaseline(id),
          api.fetchScheduleMatches(id),
          api.fetchTests(id),
          api.fetchItems(id),
          api.fetchBaselineHistory(id),
          api.fetchExtensions(id),
          api.fetchCalendars(),
        ]);
      if (store.get().selectedId !== id) return;

      /* TEMPORARY — THE FWO DEMONSTRATION. Empty for every department and every
         build that did not ask for it; see the header of src/data/fwo-demo.ts,
         and delete this with that file.

         APPENDED, and a real row wins on id — the same rule `hydrate` and
         `refreshAllDefects` keep, for the same reason. The moment FWO records
         real work against one of these structures it sits beside the fabricated
         rows rather than being replaced by them, and the card says how many of
         what is on screen is invented.

         ONLY ON THIS PATH, which is the live one. The two branches that clear
         the record — no backend, or a read that failed — say something specific
         about the register and must go on saying it: "this device is not talking
         to a register" and "the record could not be read" are not the same
         answer as "here is a site with six entries on it". */
      const demoWorks = demoProgressFor(active().id, id).filter(
        (e) => !report.events.some((r) => r.id === e.id),
      );
      const events = demoWorks.length ? [...report.events, ...demoWorks] : report.events;
      /* The same, for the rest of the site record, and by the same rule. */
      const demoSite = demoSiteRecordFor(active().id, id);
      const demoPlan = demoScheduleFor(active().id, id);
      const unseen = <T extends { id: string }>(real: T[], demo: T[]): T[] =>
        demo.length ? [...real, ...demo.filter((d) => !real.some((r) => r.id === d.id))] : real;

      store.set({
        progress: events,
        progressSummary: demoWorks.length ? summariseWithDemo(events) : report.summary,
        siteReturns: unseen(returns, demoSite.returns),
        incidents: unseen(incidents, demoSite.incidents),
        documents: unseen(documents, demoSite.documents),
        tests: unseen(tests, demoSite.tests),
        items: unseen(items, demoSite.items),
        /* The demonstration's programme is written to its own works, so on its own
           structure it stands in for whatever baseline the register holds. */
        baseline: demoPlan?.baseline ?? baselines.baseline,
        proposedBaseline: demoPlan ? null : baselines.proposed,
        baselineHistory: demoPlan ? [headerOf(demoPlan.baseline)] : history,
        extensions: demoPlan?.extensions ?? extensions,
        calendars: demoPlan?.baseline.calendar ? [...calendars, demoPlan.baseline.calendar] : calendars,
        matches: demoPlan?.matches ?? matches,
        progressRead: 'read',
        progressOf: id,
      });
      void actions.refreshHandover();
    } catch {
      if (store.get().selectedId !== id) return;

      /* A COMPLETE RECORD ALREADY READ FOR THIS STRUCTURE IS KEPT, and it used
         to be thrown away.

         Six reads settle as one here, so one of them timing out rejected the
         group and this branch cleared all six — the entries, the diary, the
         incidents, the documents, the baseline and its matches. On a record
         somebody was already looking at, that emptied the Works card and made the
         Programme card VANISH, because it draws nothing at all unless
         `progressRead` is `read` (see src/screens/programme.ts). A 204-activity
         programme disappearing because one request was slow is not a statement
         about the structure; it is the register editorialising on the strength of
         a timeout, which is the exact thing this state exists to prevent.

         What is kept is coherent, which is the property the note below was
         written to protect: every collection held came from ONE successful pass,
         and `selectInto` cleared the lot when the selection moved, so none of it
         can belong to another structure. It is the last complete answer rather
         than a mixture of a fresh read and a stale one.

         NOTHING HELD IS STILL SAID PLAINLY. Where no pass has succeeded there is
         nothing to keep, and the cards go on reporting that the record could not
         be read — which is not the same claim as "nothing has been built here",
         and is the distinction `progressRead` was added for.

         ALL SIX TOGETHER EITHER WAY, even where only one request failed: a record
         showing the pours and no diary, with nothing saying why, would read as a
         site that built things and never wrote a return. */
      if (store.get().progressRead === 'read') return;

      store.set({
        progress: [],
        progressSummary: NO_PROGRESS_SUMMARY,
        siteReturns: [],
        incidents: [],
        documents: [],
        tests: [],
        items: [],
        baseline: null,
        proposedBaseline: null,
        baselineHistory: [],
        extensions: [],
        matches: [],
        progressRead: 'unreachable',
        progressOf: id,
      });
    }
  },

  /* --------------------------- the period reports --------------------------- */

  /**
   * Switch cadence — weekly, monthly, quarterly, or a range somebody types.
   *
   * THE RANGE IS REBUILT FROM TODAY rather than from the range in hand, and
   * that is the behaviour somebody pressing "Monthly" expects: they are asking
   * for *this* month, not for the month containing whichever week they were
   * reading. Stepping through history is what the previous and next controls
   * are for.
   *
   * `custom` is the exception and keeps what is on screen. It is chosen in
   * order to edit the dates, so replacing them with today would throw away the
   * range the person was about to adjust.
   */
  setWorksPeriod(kind: PeriodKind): void {
    const range =
      kind === 'custom'
        ? store.get().worksRange
        : periodFor(kind, new Date().toISOString().slice(0, 10));
    store.set({ worksPeriod: kind, worksRange: range });
    void actions.refreshWorks();
  },

  /** Move the whole period, keeping its cadence. */
  setWorksRange(range: Period): void {
    store.set({ worksRange: range });
    void actions.refreshWorks();
  },

  /**
   * One end of a custom range, as typed.
   *
   * REFUSES A DATE THAT IS NOT ONE rather than sending it: a half-typed year
   * reaches this on every keystroke, and posting `202-` to the endpoint would
   * produce a 400 and an empty report for as long as it took to finish typing.
   * An invalid value simply does not move the range, so the last good one stays
   * on screen.
   *
   * It also refuses to invert the range. Dragging `from` past `to` pushes the
   * other end along rather than producing a period the endpoint would reject —
   * which is what somebody moving one box actually meant.
   */
  setWorksRangeEnd(end: 'from' | 'to', value: string): void {
    if (!isIsoDay(value)) return;
    const cur = store.get().worksRange;
    const next: Period =
      end === 'from'
        ? { from: value, to: value > cur.to ? value : cur.to }
        : { from: value < cur.from ? value : cur.from, to: value };
    store.set({ worksPeriod: 'custom', worksRange: next });
    void actions.refreshWorks();
  },

  /**
   * Read the department's construction record over the period on screen.
   *
   * NO PER-STRUCTURE GUARD HERE, unlike `refreshProgress`: this read is not
   * about a selected bridge, so there is nothing for a slow answer to land on
   * the wrong one of. What it does guard is the RANGE — a slow answer for
   * September must not be drawn under an October heading — so the range is
   * captured before the request and compared after it.
   */
  /**
   * The report's own words and the quantities to date (LC-55): the structure
   * in hand's summary and chosen photographs for the period, and every entry
   * up to the period's end. Read after the records and never in their way.
   */
  async refreshWorksExtras(): Promise<void> {
    const asked = store.get().worksRange;
    const bridgeId = store.get().selectedId;
    const [toDate, report] = await Promise.all([
      api.fetchProgressPeriod(FIRST_DAY, asked.to).then(
        (r) => {
          const demo = demoProgressInPeriod(active().id, FIRST_DAY, asked.to).filter(
            (e) => !r.events.some((x) => x.id === e.id),
          );
          return [...r.events, ...demo];
        },
        () => null,
      ),
      bridgeId ? api.fetchPeriodReport(bridgeId, asked.from, asked.to).catch(() => null) : Promise.resolve(null),
    ]);
    const now = store.get().worksRange;
    if (now.from !== asked.from || now.to !== asked.to) return;
    store.set({ worksToDate: toDate, worksReport: report, worksSummaryDraft: null, worksPhotoPick: null, worksReportError: null });
  },

  /** Start writing the executive summary, from what is stored. */
  editWorksSummary(): void {
    store.set((s) => ({ worksSummaryDraft: s.worksReport?.summary ?? '', worksReportError: null }));
  },

  setWorksSummaryDraft(text: string): void {
    if (store.get().worksSummaryDraft !== null) store.set({ worksSummaryDraft: text });
  },

  cancelWorksSummary(): void {
    store.set({ worksSummaryDraft: null, worksReportError: null });
  },

  /** Start choosing the photographs the report prints, from the ones it prints now. */
  openPhotoPick(ids: readonly string[]): void {
    store.set({ worksPhotoPick: [...ids], worksReportError: null });
  },

  togglePhotoPick(id: string): void {
    const pick = store.get().worksPhotoPick;
    if (pick === null) return;
    store.set({ worksPhotoPick: pick.includes(id) ? pick.filter((x) => x !== id) : [...pick, id] });
  },

  cancelPhotoPick(): void {
    store.set({ worksPhotoPick: null, worksReportError: null });
  },

  /**
   * Write the structure's report for the period (LC-55): the summary being
   * written or the one stored, and the photographs being chosen where they are.
   */
  async saveWorksReport(): Promise<void> {
    const s = store.get();
    const bridgeId = s.selectedId;
    if (!bridgeId) return;
    try {
      const report = await api.savePeriodReport({
        bridgeId,
        from: s.worksRange.from,
        to: s.worksRange.to,
        summary: (s.worksSummaryDraft ?? s.worksReport?.summary ?? '').trim(),
        ...(s.worksPhotoPick !== null ? { photoIds: s.worksPhotoPick } : {}),
      });
      store.set({ worksReport: report, worksSummaryDraft: null, worksPhotoPick: null, worksReportError: null });
    } catch (err) {
      store.set({ worksReportError: err instanceof Error ? err.message : 'The report could not be saved.' });
    }
  },

  async refreshWorks(): Promise<void> {
    if (store.get().backend !== 'live') {
      store.set({
        worksEvents: [],
        worksSummary: NO_PROGRESS_SUMMARY,
        worksReturns: [],
        worksIncidents: [],
        worksDocuments: [],
        worksTests: [],
        worksItems: [],
        worksToDate: null,
        worksReport: null,
        worksRead: 'unknown',
        worksLoading: false,
      });
      return;
    }

    const asked = store.get().worksRange;
    store.set({ worksLoading: true });

    try {
      /* The four records the report's eight sections are assembled from, in
         one pass over one period. Settled together for the reason the
         record's are: a document that reported the pours and left the safety
         section empty because one request failed would be a document making a
         claim about a site's safety record. */
      const [report, returns, incidents, documents, tests, items] = await Promise.all([
        api.fetchProgressPeriod(asked.from, asked.to),
        api.fetchSiteReturnsPeriod(asked.from, asked.to),
        api.fetchIncidentsPeriod(asked.from, asked.to),
        api.fetchDocumentsPeriod(asked.from, asked.to),
        api.fetchTestsPeriod(asked.from, asked.to),
        api.fetchItemsPeriod(asked.from, asked.to),
      ]);
      const now = store.get().worksRange;
      if (now.from !== asked.from || now.to !== asked.to) return;

      /* TEMPORARY — THE FWO DEMONSTRATION; delete with src/data/fwo-demo.ts.
         Selected by date rather than by structure, because this report spans the
         department over a period. Appended, real rows winning on id, the same as
         everywhere else it is merged.

         THIS ONE PRINTS, which the record's card does not, so the document itself
         has to name them — see the preamble in src/screens/worksReport.ts. A
         printed sheet read against FIDIC 4.21 is exactly where invented rows must
         not pass as a site's record. */
      const demoWorks = demoProgressInPeriod(active().id, asked.from, asked.to).filter(
        (e) => !report.events.some((r) => r.id === e.id),
      );
      const events = demoWorks.length ? [...report.events, ...demoWorks] : report.events;
      /* The rest of the site record, as the Site Diary pages merge it, cut to the
         period the way the register's own period reads cut it. */
      const inPeriod = (day: string | null | undefined) => !!day && day >= asked.from && day <= asked.to;
      const demoSites = [
        ...new Set(demoProgressInPeriod(active().id, FIRST_DAY, asked.to).map((e) => e.bridgeId)),
      ].map((id) => demoSiteRecordFor(active().id, id));
      const unseen = <T extends { id: string }>(real: T[], demo: T[]): T[] =>
        demo.length ? [...real, ...demo.filter((d) => !real.some((r) => r.id === d.id))] : real;
      const demoOf = <T>(pick: (r: (typeof demoSites)[number]) => T[], keep: (x: T) => boolean): T[] =>
        demoSites.flatMap(pick).filter(keep);

      store.set({
        worksEvents: events,
        worksSummary: demoWorks.length ? summariseWithDemo(events) : report.summary,
        worksReturns: unseen(returns, demoOf((r) => r.returns, (x) => inPeriod(x.returnedOn))),
        worksIncidents: unseen(incidents, demoOf((r) => r.incidents, (x) => inPeriod(x.occurredOn))),
        worksDocuments: unseen(
          documents,
          demoOf((r) => r.documents, (x) => inPeriod(x.datedOn ?? x.createdAt.slice(0, 10))),
        ),
        worksTests: unseen(tests, demoOf((r) => r.tests, (x) => inPeriod(x.testedOn))),
        worksItems: unseen(
          items,
          demoOf(
            (r) => r.items,
            (x) => x.raisedAt.slice(0, 10) <= asked.to && (x.closedAt === null || x.closedAt.slice(0, 10) >= asked.from),
          ),
        ),
        worksRead: 'read',
        worksLoading: false,
      });
      /* What the report adds to the records (LC-55), read beside them and never
         in their way: a failure here leaves the report's own sections as they
         are, and these two say they could not be read. */
      void actions.refreshWorksExtras();
    } catch {
      const now = store.get().worksRange;
      if (now.from !== asked.from || now.to !== asked.to) return;
      store.set({
        worksEvents: [],
        worksSummary: NO_PROGRESS_SUMMARY,
        worksReturns: [],
        worksIncidents: [],
        worksDocuments: [],
        worksTests: [],
        worksItems: [],
        worksRead: 'unreachable',
        worksLoading: false,
      });
    }
  },

  /* --------------------------- recording work on site --------------------------- */

  /**
   * Open the "Record work" sheet against a structure.
   *
   * A FRESH DRAFT EVERY TIME, unlike the defect dialog which keeps the last
   * category. A crew records several entries in a session — a pour, then the
   * curing, then a hold point — and carrying the previous activity forward is
   * how the second one gets recorded as the first. The date is the exception:
   * it is set to today by the factory, and somebody entering Tuesday's work on
   * Thursday changes it once for the batch.
   */
  openRecordProgress(bridgeId: string): void {
    store.set({
      ...ONE_SITE_FORM,
      progressBridgeId: bridgeId,
      progressDraft: emptyProgressDraft(),
      progressSaving: false,
      progressError: null,
    });
  },

  closeRecordProgress(): void {
    if (store.get().progressBridgeId === null) return;
    store.set({ progressBridgeId: null, progressSaving: false, progressError: null });
  },

  setProgressField(key: keyof ProgressDraft, value: string): void {
    store.set((s) => ({ progressDraft: { ...s.progressDraft, [key]: value } }));
  },

  /** The component, by its label and the model's id for it together (LC-14). */
  setProgressComponent(component: string, componentId: string | null): void {
    store.set((s) => ({
      progressDraft: { ...s.progressDraft, component, componentId: component ? (componentId ?? '') : '' },
    }));
  },

  /**
   * Choose the activity.
   *
   * ITS OWN ACTION RATHER THAN `setProgressField`, because changing it can
   * invalidate what is in the quantity box: the library gives each activity a
   * unit or none, and "Formwork struck: 12" measures nothing. So a change to an
   * activity that takes no quantity clears one that was typed — deliberately,
   * rather than sending it and being refused by the endpoint.
   */
  setProgressActivity(activity: string): void {
    store.set((s) => ({
      progressDraft: {
        ...s.progressDraft,
        activity,
        quantity: activityUnit(activity) === null ? '' : s.progressDraft.quantity,
      },
      progressError: null,
    }));
  },

  /**
   * Record against a planned activity (LC-45): its matched component, with the
   * model's id, and the kind of work it is, in one tap. The activity is only
   * set where the kind names one, and the person can still change either.
   */
  pickPlannedActivity(component: string, componentId: string | null, activity: string | null): void {
    store.set((s) => ({
      progressDraft: {
        ...s.progressDraft,
        component,
        componentId: component ? (componentId ?? '') : '',
        ...(activity
          ? { activity, quantity: activityUnit(activity) === null ? '' : s.progressDraft.quantity }
          : {}),
      },
      progressError: null,
    }));
  },

  setProgressPhoto(dataUrl: string | null): void {
    store.set((s) => ({ progressDraft: { ...s.progressDraft, photo: dataUrl } }));
  },

  setProgressError(message: string): void {
    store.set({ progressError: message });
  },

  /**
   * Record the entry, or queue it where there is no signal.
   *
   * The same three-branch shape `saveDefect` has, and the same reasoning
   * throughout: mint the id before the attempt so a replay lands once, queue
   * only an unreachable register and never a refusal, and show the entry on the
   * record straight away marked as not yet synced — because somebody who
   * records a pour at the structure and cannot see it records it again.
   *
   * WHAT IS CHECKED HERE RATHER THAN BY THE ENDPOINT. An activity must be
   * chosen and a quantity must be a non-negative number. Both are refused
   * before anything is sent or queued, so a mistyped figure is a message in
   * this sheet rather than a queue entry that fails silently on a riverbank
   * tomorrow. Everything else — whether the activity exists, whether it takes a
   * unit at all — is the endpoint's to refuse, and it does.
   */
  async saveProgress(): Promise<void> {
    const bridgeId = store.get().progressBridgeId;
    if (bridgeId === null || store.get().progressSaving) return;

    const d = store.get().progressDraft;

    if (d.activity.trim() === '') {
      store.set({ progressError: 'Choose what was done.' });
      return;
    }
    if (!ISO_DATE.test(d.occurredOn.trim())) {
      store.set({ progressError: '"Done on" is required.' });
      return;
    }

    /* Optional, and only where the activity is measured in something. A blank
       box is not a zero: zero is a legitimate quantity somebody typed and an
       empty box is a measurement nobody took. */
    const typed = d.quantity.trim();
    let quantity: number | null = null;
    if (typed !== '') {
      const n = Number(typed);
      if (!Number.isFinite(n)) {
        store.set({ progressError: 'The quantity must be a number.' });
        return;
      }
      if (n < 0) {
        store.set({ progressError: 'A quantity cannot be negative.' });
        return;
      }
      if (activityUnit(d.activity) === null) {
        store.set({
          progressError: `${activityLabel(d.activity)} is not measured in anything, so it cannot carry a quantity.`,
        });
        return;
      }
      quantity = n;
    }

    store.set({ progressSaving: true, progressError: null });
    /* Minted before the attempt, so the same id is used whether this lands now
       or from the queue in three days — which is what stops a retry recording
       the pour twice. */
    const clientId = crypto.randomUUID();
    const payload = {
      bridgeId,
      clientId,
      activity: d.activity,
      occurredOn: d.occurredOn.trim(),
      element: d.element.trim() || null,
      component: d.component.trim() || null,
      /* Beside the label and only with it — the endpoint refuses an id alone. */
      ...(d.component.trim() && d.componentId ? { componentId: d.componentId } : {}),
      note: d.note.trim(),
      /* Only when there is one, so an unmeasured entry's POST — and the queue
         entry behind it — carries no quantity field at all rather than a null
         the endpoint would have to interpret. */
      ...(quantity !== null ? { quantity } : {}),
      ...(d.photo ? { image: d.photo } : {}),
    };

    try {
      await api.createProgress(payload);
      await actions.refreshProgress();
      if (store.get().progressBridgeId === bridgeId) {
        store.set({ progressBridgeId: null, progressSaving: false, progressError: null });
      }
    } catch (err) {
      /* Unreachable, not refused. A null status means the request never got an
         answer — no signal, DNS, a timeout — which is the case this whole
         application exists for. A 4xx is the register saying no, and queueing
         that would be queueing a mistake. */
      const unreachable = err instanceof api.ApiError && err.status === null;

      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'progress', bridgeId, clientId, body: payload });

        /* On the record straight away, as an ordinary entry, replaced by the
           register's own copy when the queue drains. Only where the record on
           screen is the structure this was recorded against — a crew recording
           against one bridge while another is selected is not a case this has,
           but painting the entry onto the wrong record would be worse than not
           painting it, and the queued write goes up either way. */
        if (store.get().selectedId === bridgeId) {
          store.set((st) => ({
            progress: [
              {
                id: `pending-${clientId}`,
                clientId,
                bridgeId,
                activity: payload.activity,
                element: payload.element,
                component: payload.component,
                componentId: payload.componentId ?? null,
                quantity: quantity,
                /* The library's unit, which is what the server would have
                   stored. Null with no quantity, so the pair stays together on
                   the optimistic copy exactly as the column requires. */
                unit: quantity === null ? null : activityUnit(payload.activity),
                note: payload.note,
                occurredOn: payload.occurredOn,
                author: null,
                state: 'draft',
                reviewedBy: null,
                reviewedAt: null,
                /* FALSE EVEN WHERE A PHOTOGRAPH WAS TAKEN, because `hasPhoto`
                   means "the register is holding one this row can be asked
                   for", and it is not — the bytes are in the queue on this
                   device. True would draw a tile pointing at
                   /api/progress-photo for an entry the register has never heard
                   of, which is a 404 dressed up as a photograph. */
                hasPhoto: false,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                pending: true,
              },
              ...st.progress,
            ],
          }));
        }

        await actions.refreshQueue();
        if (store.get().progressBridgeId === bridgeId) {
          store.set({ progressBridgeId: null, progressSaving: false, progressError: null });
        }
        return;
      }

      if (store.get().progressBridgeId === bridgeId) {
        store.set({
          progressSaving: false,
          progressError: err instanceof Error ? err.message : 'The entry could not be recorded.',
        });
      }
    }
  },

  /* --------------------------- the programme --------------------------- */

  /**
   * Open the Take / Revise baseline form on the Scheduler's Baseline page.
   *
   * PREFILLED FROM THE CONTRACT IN FORCE — the accepted baseline's header, or
   * the proposed one's — because a revision re-freezes the programme under
   * the same contract far more often than it changes the contract. The
   * commencement date is never filled from today (LC-42); with no contract it
   * is filled from the modeller's own project start where the model has one,
   * and left empty otherwise, which the form then refuses.
   */
  openBaselineForm(): void {
    const s = store.get();
    const from = s.proposedBaseline ?? s.baseline;
    const c = from?.contract ?? null;
    const calendars = s.calendars;
    const calendarId = from?.calendarId && calendars.some((k) => k.id === from.calendarId)
      ? from.calendarId
      : (calendars[0]?.id ?? '');
    const works = opensWorks(s);
    store.set({
      baselineForm: {
        name: works ? 'Works programme' : from === null ? 'Contract programme' : `Revision ${s.baselineHistory.length || 1}`,
        worksKind: '',
        reason: '',
        calendarId,
        contractor: c?.contractor ?? '',
        contractNo: c?.contractNo ?? '',
        value: c?.value == null ? '' : String(c.value),
        commencement: c?.commencement ?? '',
        completionDays: c ? String(c.completionDays) : '',
        milestones: c ? milestoneLines(c.milestones) : '',
      },
      baselineNote: null,
    });
    actions.showDetailView('baseline');
    if (c === null) {
      const bridgeId = s.selectedId;
      void api
        .fetchModelerState(bridgeId)
        .then((entries) => {
          const start = readProgramme(entries, findBridge(bridgeId)?.modelOfRecord?.modelId ?? null).schedule
            ?.projectStartIso;
          const form = store.get().baselineForm;
          if (form && form.commencement === '' && start && isIsoDay(start) && store.get().selectedId === bridgeId) {
            store.set({ baselineForm: { ...form, commencement: start } });
          }
        })
        .catch(() => undefined);
    }
  },

  setBaselineFormField(key: keyof BaselineForm, value: string): void {
    const f = store.get().baselineForm;
    if (f) store.set({ baselineForm: { ...f, [key]: value } });
  },

  cancelBaselineForm(): void {
    store.set({ baselineForm: null, baselineNote: null });
  },

  /**
   * Freeze a baseline from the structure's own model, as PROPOSED (LC-40).
   *
   * FIVE STEPS, AND THE SOLVE IS THE FOURTH. Check the form — a revision's
   * reason, the contract's commencement (LC-42); read the modeller's persisted
   * namespace; find the model of record's schedule in it; solve its durations
   * and links on the contract's calendar into days (LC-43), keeping the links
   * (LC-44); post the lot. The server never runs a solve — see
   * src/data/programme.ts and functions/lib/baselines.ts.
   *
   * NOTHING IS MEASURED AGAINST IT until an approver who did not take it
   * accepts it. The note says so.
   *
   * EVERY FAILURE GETS A SENTENCE, because there are several quite different
   * ones and they have different answers: no model at all, a model with no
   * schedule in it, a model that will not parse, a form missing what the
   * contract needs, and a register that did not answer.
   */
  async captureBaseline(bridgeId: string): Promise<void> {
    const s = store.get();
    const f = s.baselineForm;
    if (s.baselineBusy || f === null) return;
    if (f.name.trim() === '') {
      store.set({ baselineNote: 'Give the baseline a name somebody will recognise.' });
      return;
    }
    /* On a bridge built with no works open, a baseline programmes works and
       says which (LC-81); it revises nothing, so it needs no reason. */
    const works = opensWorks(s);
    if (works && !isWorksKind(f.worksKind)) {
      store.set({ baselineNote: 'Say what the works are: rehabilitation, strengthening, widening, deck replacement, repair or other.' });
      return;
    }
    const revising = !works && (s.baseline !== null || s.proposedBaseline !== null || s.baselineHistory.length > 0);
    if (revising && f.reason.trim() === '') {
      store.set({ baselineNote: 'Say why the programme is being revised. The reason is kept with the revision.' });
      return;
    }
    const { milestones, bad } = parseMilestones(f.milestones);
    if (bad.length > 0) {
      store.set({ baselineNote: `A milestone needs a date and a name, as “2026-08-15 Substructure complete”: ${bad[0]}` });
      return;
    }
    const value = f.value.trim() === '' ? null : Number(f.value.replace(/,/g, ''));
    const contract: ContractHeader = {
      contractor: f.contractor.trim(),
      contractNo: f.contractNo.trim(),
      /* NaN where something unreadable was typed, which the check refuses. */
      value,
      currency: active().currency.code,
      commencement: f.commencement,
      completionDays: Number(f.completionDays),
      milestones,
    };
    const why = checkContract(contract);
    if (why) {
      store.set({ baselineNote: why });
      return;
    }
    const calendar = s.calendars.find((c) => c.id === f.calendarId) ?? null;
    /* The Scheduler's Gantt, edited over the baseline in force: the revision
       is that, rather than whatever the model holds. */
    const inForce = works ? null : s.baseline;
    const draft = inForce === null ? null : readDraft(inForce.id);

    store.set({ baselineBusy: true, baselineNote: null });

    try {
      const entries = draft === null ? await api.fetchModelerState(bridgeId) : {};
      /* The model of record where one is set (LC-12), and the newest otherwise. */
      const read = readProgramme(entries, findBridge(bridgeId)?.modelOfRecord?.modelId ?? null);

      /* WHICH PROGRAMME (QA 2026-09-30). Works programme the works, not the
         model's new build (LC-81). An imported model is programmed from the
         export's own parts: the modeller's schedule describes its parametric
         tree, which an import does not rebuild. */
      let schedule: SerialisedSchedule | null = read.schedule;
      let components: Record<string, ProgrammeComponent> = {};
      let modelId = read.modelId;
      if (draft !== null && inForce !== null) {
        const { DraftSchedule } = await import('../data/draftSchedule.ts');
        const cal = inForce.calendar ?? s.calendars.find((c) => c.id === inForce.calendarId) ?? EVERY_DAY;
        schedule = new DraftSchedule(inForce, cal, draft).ToSerialised();
        modelId = inForce.modelId;
        for (const a of inForce.activities) {
          if (a.component) components[a.activityId] = { component: a.component, componentId: a.componentId ?? null };
        }
      } else if (works && isWorksKind(f.worksKind)) {
        const cal = calendar ?? EVERY_DAY;
        const days = workingDaysBetween(cal, contract.commencement, addDays(contract.commencement, contract.completionDays));
        schedule = worksProgramme(WORKS_KIND_LABEL[f.worksKind], days);
      } else if (read.modelId !== null) {
        const imported = await importedProgrammeOf(bridgeId, read.modelId);
        if (imported !== null) {
          schedule = imported.schedule;
          components = imported.components;
        }
      }

      if (schedule === null) {
        store.set({ baselineBusy: false, baselineNote: read.why });
        return;
      }

      /* Day zero is the contract's commencement — never today (LC-42). */
      const activities = baselineFrom(schedule, calendar ?? EVERY_DAY, contract.commencement).map((a) =>
        components[a.activityId] ? { ...a, ...components[a.activityId] } : a,
      );
      if (activities.length === 0) {
        store.set({
          baselineBusy: false,
          baselineNote: 'That schedule solved to no activities at all, so there is no plan to freeze.',
        });
        return;
      }

      await api.createBaseline({
        bridgeId,
        name: f.name.trim(),
        projectStart: contract.commencement,
        activities,
        links: baselineLinks(schedule),
        contract,
        calendarId: calendar?.id ?? null,
        reason: f.reason.trim() || null,
        modelId,
        ...(works && isWorksKind(f.worksKind) ? { worksKind: f.worksKind } : {}),
      });

      if (inForce !== null && draft !== null) clearDraft(inForce.id);
      await actions.refreshProgress();
      store.set({
        baselineBusy: false,
        baselineForm: null,
        baselineNote:
          `Baseline taken: ${activities.filter((a) => !a.isGroup).length} activities, waiting for an ` +
          'approver to accept it. Until then the programme in force is unchanged.',
      });
    } catch (err) {
      store.set({
        baselineBusy: false,
        baselineNote:
          err instanceof Error ? err.message : 'The baseline could not be taken.',
      });
    }
  },

  /**
   * Accept the proposed baseline (LC-40). The approver's, and refused by the
   * register to whoever took it. Accepting the first baseline of a planned
   * bridge starts its construction, so the register is read again.
   */
  async acceptProposedBaseline(): Promise<void> {
    const s = store.get();
    const p = s.proposedBaseline;
    if (s.baselineBusy || p === null) return;
    store.set({ baselineBusy: true, baselineNote: null });
    try {
      const { started, worksOpened } = await api.acceptBaseline(p.id);
      if (started || worksOpened) await actions.hydrate();
      await actions.refreshProgress();
      store.set({
        baselineBusy: false,
        baselineNote: started
          ? `${p.name} accepted. The structure is now under construction.`
          : worksOpened
            ? `${p.name} accepted. Works are open on the structure, and its site record with them.`
            : `${p.name} accepted. It is the programme measured against from now on.`,
      });
    } catch (err) {
      store.set({
        baselineBusy: false,
        baselineNote: err instanceof Error ? err.message : 'The baseline could not be accepted.',
      });
    }
  },

  /* --------------------------- extensions of time (LC-42) --------------------------- */

  openExtension(): void {
    store.set({ eotDraft: { reason: '', days: '' }, baselineNote: null });
    actions.showDetailView('baseline');
  },

  setExtensionField(key: 'reason' | 'days', value: string): void {
    const d = store.get().eotDraft;
    if (d) store.set({ eotDraft: { ...d, [key]: value } });
  },

  cancelExtension(): void {
    store.set({ eotDraft: null, baselineNote: null });
  },

  /** Raise the extension being drafted. Refused here as the endpoint refuses it. */
  async submitExtension(): Promise<void> {
    const d = store.get().eotDraft;
    if (!d) return;
    const days = Number(d.days);
    if (d.reason.trim() === '') {
      store.set({ baselineNote: 'An extension of time needs its reason.' });
      return;
    }
    if (!Number.isInteger(days) || days < 1) {
      store.set({ baselineNote: 'An extension of time is a whole number of days, one or more.' });
      return;
    }
    try {
      await api.raiseExtension({ bridgeId: store.get().selectedId, reason: d.reason.trim(), days });
      store.set({ eotDraft: null, baselineNote: null });
      await actions.refreshProgress();
    } catch (err) {
      store.set({ baselineNote: err instanceof Error ? err.message : 'The extension could not be raised.' });
    }
  },

  /** Accept or reject a proposed extension. The approver's, never the raiser's. */
  async decideExtension(id: string, act: 'accept' | 'reject'): Promise<void> {
    try {
      await api.decideExtension(id, act);
      await actions.refreshProgress();
    } catch (err) {
      store.set({ baselineNote: err instanceof Error ? err.message : 'The extension could not be decided.' });
    }
  },

  /* --------------------------- working calendars (LC-43) --------------------------- */

  /* The calendars themselves are read with the site record, in `refreshProgress`. */

  /** Open a calendar for editing, or a new one with `null`. The administrator's. */
  editCalendar(id: string | null): void {
    const c = id === null ? null : (store.get().calendars.find((k) => k.id === id) ?? null);
    const lines = c ? calendarLines(c) : { holidays: '', windows: '' };
    store.set({
      calendarEdit: {
        id: c?.id ?? null,
        name: c?.name ?? '',
        week: c?.week ?? '1111110',
        holidays: lines.holidays,
        windows: lines.windows,
      },
      calendarNote: null,
    });
  },

  setCalendarField(key: 'name' | 'holidays' | 'windows', value: string): void {
    const c = store.get().calendarEdit;
    if (c) store.set({ calendarEdit: { ...c, [key]: value } });
  },

  /** Turn one day of the working week on or off, Monday first. */
  toggleCalendarDay(day: number): void {
    const c = store.get().calendarEdit;
    if (!c || day < 0 || day > 6) return;
    const week = [...c.week];
    week[day] = week[day] === '1' ? '0' : '1';
    store.set({ calendarEdit: { ...c, week: week.join('') } });
  },

  cancelCalendar(): void {
    store.set({ calendarEdit: null, calendarNote: null });
  },

  /** Save the calendar being edited. Refused here by the rule the endpoint keeps. */
  async saveCalendar(): Promise<void> {
    const c = store.get().calendarEdit;
    if (!c) return;
    const holidays = parseCalendarLines(c.holidays);
    const windows = parseCalendarLines(c.windows);
    const bad = [...holidays.bad, ...windows.bad, ...holidays.windows.map((w) => w.name), ...windows.holidays.map((h) => h.on)];
    if (bad.length > 0) {
      store.set({ calendarNote: `A line did not read as a holiday or a window: ${bad[0]}` });
      return;
    }
    const payload = {
      id: c.id,
      name: c.name.trim(),
      week: c.week,
      holidays: holidays.holidays,
      windows: windows.windows,
    };
    const why = checkCalendar(payload);
    if (why) {
      store.set({ calendarNote: why });
      return;
    }
    try {
      const saved = await api.saveCalendar(payload);
      store.set((s) => ({
        calendars: [...s.calendars.filter((k) => k.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name)),
        calendarEdit: null,
        calendarNote: `${saved.name} saved.`,
      }));
    } catch (err) {
      store.set({ calendarNote: err instanceof Error ? err.message : 'The calendar could not be saved.' });
    }
  },

  /**
   * Confirm that a scheduled activity is realised by a component's entries.
   *
   * THE ACT THE 2026-09-16 DECISION RESERVED FOR A PERSON. The screen may
   * suggest the component — see `proposeComponent`, which refuses to guess
   * between two candidates — and only this call, made by somebody pressing
   * something, records it.
   */
  async matchActivity(
    activityId: string,
    activityName: string,
    component: string,
    completedOn: string | null = null,
    /* The model's id for the component (LC-14), where the entries recorded
       against the label agree on one — see `idForLabel`. */
    componentId: string | null = null,
  ): Promise<void> {
    const bridgeId = store.get().selectedId;
    try {
      await api.putScheduleMatch({ bridgeId, activityId, activityName, component, componentId, completedOn });
      if (store.get().matchPick === activityId) store.set({ matchPick: null });
      await actions.refreshProgress();
    } catch (err) {
      store.set({
        baselineNote: err instanceof Error ? err.message : 'That match could not be recorded.',
      });
    }
  },

  /**
   * Fold the baseline's Gantt open or shut.
   *
   * Nothing is fetched and nothing is torn down here. The view is mounted and
   * unmounted by `syncGantt` in the paint tail, from this flag and the
   * baseline — the same arrangement the 3D scene and the fly map have, and for
   * the same reason: a WebGL context or a subscribed view is not state, and a
   * store action that reached into the DOM would be the one place in this
   * application that did.
   */
  toggleGantt(): void {
    const open = !ganttShown(store.get());
    /* FOLDING THE BARS SHUT ALSO LEAVES FULL VIEW. The button that does this
       is in the card head, which full view covers — so a reader who is in it
       cannot press this; but the report and the record share the flag, and
       one of them folding the bars while the other is full would leave a
       window-sized overlay with nothing mounted in it. */
    store.set({ ganttOpen: open, ganttFull: open && store.get().ganttFull });
  },

  /**
   * Put one of the Scheduler's two views in front, opening the programme
   * screen first where the reader is elsewhere in the app (the register, the
   * guide). Full view is left, because the Scheduler's views already fill
   * the page and have no card to come back to.
   */
  /** Bridge Rating's screen or open rating has moved; the address follows (LC-31). */
  ratingMoved(screen: RatingScreen, open: string | null): void {
    const s = store.get();
    if (s.ratingScreen !== screen || s.ratingOpen !== open) store.set({ ratingScreen: screen, ratingOpen: open });
  },

  showSchedule(view: State['scheduleView']): void {
    store.set({ scheduleView: view, ganttFull: false });
    if (store.get().screen !== 'schedule') actions.go('schedule');
  },

  setHomeView(homeView: State['homeView']): void {
    store.set({ homeView });
  },

  setScheduleLayout(layout: State['scheduleLayout']): void {
    store.set({ scheduleLayout: layout, ganttFull: false });
  },

  setScheduleMonth(month: string): void {
    const bridgeId = store.get().selectedId;
    store.set({ scheduleMonth: bridgeId ? { bridgeId, month } : null });
  },

  /**
   * Throw the bars across the window, or put them back in the card.
   *
   * NOTHING IS REMOUNTED. The slot keeps its place in the tree and its
   * `data-keep` marker; what changes is a class on the box around it, and the
   * schedule view — which is flex all the way down, see the panes in the
   * modeller's own stylesheet — simply lays itself out in the new size. So
   * the reader comes back to the row they were looking at rather than to the
   * top of the programme.
   */
  toggleGanttFull(): void {
    store.set({ ganttFull: !store.get().ganttFull });
  },

  /** Open the component picker on one unmatched activity, or shut it with `null` (LC-41). */
  pickMatch(activityId: string | null): void {
    store.set({ matchPick: activityId });
  },

  /** Withdraw a match. The plan and the entries both stand, unjoined. */
  async unmatchActivity(activityId: string): Promise<void> {
    const bridgeId = store.get().selectedId;
    try {
      await api.deleteScheduleMatch(bridgeId, activityId);
      await actions.refreshProgress();
    } catch (err) {
      store.set({
        baselineNote: err instanceof Error ? err.message : 'That match could not be withdrawn.',
      });
    }
  },

  /**
   * Confirm every activity whose name matches exactly one component, in one go.
   *
   * STILL A PERSON'S ACT, and the distinction is worth being precise about.
   * This is not the register assuming the vocabularies line up — it is
   * somebody looking at a list of proposals and saying "yes, all of those",
   * which is the same decision they would make one row at a time and is
   * recorded against their name exactly the same way. What it cannot do is
   * propose where two components would answer: `proposeComponent` returns
   * null there, and those rows stay for a human to settle.
   */
  async matchAllProposed(): Promise<void> {
    const s = store.get();
    if (s.baseline === null) return;

    /* What can be matched to: the model's own components, where the 3D model
       has reported its tree, and the components the site has recorded work
       against — so a programme can be matched before any work starts (LC-41). */
    const tree = componentChoices(s.heroComponentTree ?? []);
    const components = [
      ...new Set([...tree.map((c) => c.name), ...s.progress.map((e) => e.component).filter((c): c is string => !!c)]),
    ];
    /* The model's id for a name, where the tree has exactly one part of that
       name; otherwise the one the entries agree on. */
    const idOf = (name: string): string | null => {
      const parts = tree.filter((c) => c.name === name && c.id !== null);
      return parts.length === 1 ? parts[0]!.id : idForLabel(s.progress, name);
    };
    const already = new Set(s.matches.map((m) => m.activityId));

    const proposals = s.baseline.activities
      .filter((a) => !a.isGroup && !already.has(a.activityId))
      .map((a) => ({ a, match: proposeMatch(a, components, idOf) }))
      .filter((p): p is { a: (typeof s.baseline.activities)[number]; match: { component: string; componentId: string | null } } =>
        p.match !== null,
      );

    if (proposals.length === 0) {
      store.set({ baselineNote: 'Nothing left to match by name. The rest need a person.' });
      return;
    }

    store.set({ baselineBusy: true, baselineNote: null });
    try {
      /* One at a time rather than in parallel: they all write the same table
         through the same unique index, and a burst of thirty concurrent
         upserts against D1 buys nothing an operator would notice. */
      for (const p of proposals) {
        await api.putScheduleMatch({
          bridgeId: s.selectedId,
          activityId: p.a.activityId,
          activityName: p.a.name,
          component: p.match.component,
          componentId: p.match.componentId,
        });
      }
      await actions.refreshProgress();
      store.set({
        baselineBusy: false,
        baselineNote: `${proposals.length} matched.`,
      });
    } catch (err) {
      await actions.refreshProgress();
      store.set({
        baselineBusy: false,
        baselineNote:
          err instanceof Error
            ? `Stopped part way: ${err.message}`
            : 'Stopped part way through matching.',
      });
    }
  },

  /* --------------------------- the daily return --------------------------- */

  /**
   * Open the return sheet for a structure, on today by default.
   *
   * PRE-FILLED FROM THE DAY'S EXISTING RETURN where there is one, which is the
   * opposite of the work sheet's fresh-every-time rule and is right for the
   * same underlying reason. A pour is a new event each time; a day has one
   * return, and somebody opening the sheet at four o'clock to add the plant is
   * correcting this morning's entry rather than writing a second one. Arriving
   * at an empty form would invite them to retype the labour figures and get
   * one of them wrong.
   */
  openSiteReturn(bridgeId: string, day?: string): void {
    const on = day !== undefined && isIsoDay(day) ? day : new Date().toISOString().slice(0, 10);
    const existing = store.get().siteReturns.find((r) => r.returnedOn === on);
    store.set({
      ...ONE_SITE_FORM,
      returnBridgeId: bridgeId,
      returnDraft: existing ? draftFromReturn(existing) : { ...emptyReturnDraft(), returnedOn: on },
      returnSaving: false,
      returnError: null,
      returnFrozen: false,
    });
  },

  closeSiteReturn(): void {
    if (store.get().returnBridgeId === null) return;
    store.set({ returnBridgeId: null, returnSaving: false, returnError: null, returnFrozen: false });
  },

  setReturnField(key: keyof ReturnDraft, value: string): void {
    store.set((s) => ({ returnDraft: { ...s.returnDraft, [key]: value } }));
  },

  /** One cell of a trade or plant row on the return (LC-56). */
  setReturnRow(list: 'trades' | 'plantRows', i: number, key: string, value: string): void {
    store.set((s) => {
      const rows = [...(s.returnDraft[list] as Record<string, string>[])];
      if (!rows[i]) return {};
      rows[i] = { ...rows[i]!, [key]: value };
      return { returnDraft: { ...s.returnDraft, [list]: rows } };
    });
  },

  addReturnRow(list: 'trades' | 'plantRows'): void {
    store.set((s) => ({
      returnDraft: {
        ...s.returnDraft,
        ...(list === 'trades'
          ? { trades: [...s.returnDraft.trades, { trade: '', count: '' }] }
          : { plantRows: [...s.returnDraft.plantRows, { plant: '', working: '', idle: '' }] }),
      },
    }));
  },

  removeReturnRow(list: 'trades' | 'plantRows', i: number): void {
    store.set((s) => ({
      returnDraft: { ...s.returnDraft, [list]: (s.returnDraft[list] as unknown[]).filter((_, n) => n !== i) },
    }));
  },

  setReturnPhoto(dataUrl: string | null): void {
    store.set((s) => ({ returnDraft: { ...s.returnDraft, photo: dataUrl } }));
  },

  setIncidentPhoto(dataUrl: string | null): void {
    store.set((s) => ({ incidentDraft: { ...s.incidentDraft, photo: dataUrl } }));
  },

  /** Say, on the open site form, why a photograph could not be read. */
  setSiteFormError(form: 'return' | 'incident', message: string): void {
    store.set(form === 'return' ? { returnError: message } : { incidentError: message });
  },

  /**
   * Change the day the sheet is about, and load that day's return if it has one.
   *
   * Its own action because moving the date is not editing a field — it changes
   * which record is being written. Somebody entering Tuesday's return on
   * Thursday must see Tuesday's figures if Tuesday already has some, not
   * Thursday's with a new date on them.
   */
  setReturnDay(day: string): void {
    if (!isIsoDay(day)) return;
    const existing = store.get().siteReturns.find((r) => r.returnedOn === day);
    store.set((s) => ({
      returnDraft: existing ? draftFromReturn(existing) : { ...emptyReturnDraft(), returnedOn: day },
      returnError: null,
      returnFrozen: false,
      /* Not reset: whatever they had typed for another day is gone either way,
         and keeping the sheet open is the point of changing the date. */
      returnSaving: s.returnSaving,
    }));
  },

  async saveSiteReturn(): Promise<void> {
    const bridgeId = store.get().returnBridgeId;
    if (bridgeId === null || store.get().returnSaving) return;

    const d = store.get().returnDraft;
    if (!isIsoDay(d.returnedOn)) {
      store.set({ returnError: 'Give the day this return is about.' });
      return;
    }
    if (!isWeather(d.weather) || d.weather === 'not-recorded') {
      store.set({ returnError: 'Choose the weather on site that day.' });
      return;
    }

    const labour = optionalCount(d.labour);
    if (labour === 'bad') {
      store.set({ returnError: 'The number of people on site must be a number.' });
      return;
    }
    const hours = optionalCount(d.hours);
    if (hours === 'bad') {
      store.set({ returnError: 'Hours worked must be a number.' });
      return;
    }
    const rainfall = optionalCount(d.rainfall);
    if (rainfall === 'bad') {
      store.set({ returnError: 'Rainfall must be a number of millimetres.' });
      return;
    }
    /* The trades and the plant, each row checked as the endpoint checks it: a
       row with nothing in it is dropped, a figure needs its name. */
    const trades: TradeCount[] = [];
    for (const t of d.trades) {
      if (t.trade.trim() === '' && t.count.trim() === '') continue;
      const n = optionalCount(t.count);
      if (t.trade.trim() === '' || n === null || n === 'bad' || !Number.isInteger(n)) {
        store.set({ returnError: 'Each trade needs its name and a whole number of people.' });
        return;
      }
      trades.push({ trade: t.trade.trim(), count: n });
    }
    const plantRows: PlantDay[] = [];
    for (const p of d.plantRows) {
      if (p.plant.trim() === '' && p.working.trim() === '' && p.idle.trim() === '') continue;
      const working = optionalCount(p.working);
      const idle = optionalCount(p.idle);
      if (p.plant.trim() === '' || working === 'bad' || idle === 'bad' || (working ?? 0) + (idle ?? 0) > 24) {
        store.set({ returnError: 'Each item of plant needs its name, and its hours working and idle within a day.' });
        return;
      }
      plantRows.push({ plant: p.plant.trim(), workingHours: working, idleHours: idle });
    }

    store.set({ returnSaving: true, returnError: null, returnFrozen: false });
    const clientId = crypto.randomUUID();
    const payload = {
      bridgeId,
      clientId,
      returnedOn: d.returnedOn,
      weather: d.weather,
      /* With trades the total is theirs, derived by the register (LC-56). */
      labour: trades.length ? null : labour,
      labourNote: d.labourNote.trim(),
      plant: d.plant.trim(),
      hours,
      note: d.note.trim(),
      ...(trades.length ? { labourByTrade: trades } : {}),
      ...(plantRows.length ? { plantRows } : {}),
      rainfallMm: rainfall,
      ...(d.photo ? { image: d.photo } : {}),
    };

    try {
      const { frozen } = await api.putSiteReturn(payload);
      await actions.refreshProgress();
      if (store.get().returnBridgeId !== bridgeId) return;
      if (frozen) {
        /* THE SHEET STAYS OPEN AND SAYS SO. The day was accepted before this
           correction arrived, so nothing was written — and closing the sheet
           as though it had been would be the worst available outcome: the
           person walks away believing the register holds their figures. */
        store.set({ returnSaving: false, returnFrozen: true });
        return;
      }
      store.set({ returnBridgeId: null, returnSaving: false, returnError: null });
    } catch (err) {
      const unreachable = err instanceof api.ApiError && err.status === null;

      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'site-return', bridgeId, clientId, body: payload });
        await actions.refreshQueue();
        if (store.get().returnBridgeId === bridgeId) {
          store.set({ returnBridgeId: null, returnSaving: false, returnError: null });
        }
        return;
      }

      if (store.get().returnBridgeId === bridgeId) {
        store.set({
          returnSaving: false,
          returnError: err instanceof Error ? err.message : 'The return could not be recorded.',
        });
      }
    }
  },

  /** Move a daily return through review. Accepting it also freezes its figures. */
  async setReturnEntryState(id: string, state: ProgressState): Promise<boolean> {
    const onRecord = store.get().siteReturns.some((r) => r.id === id);
    const onWorks = store.get().worksReturns.some((r) => r.id === id);
    try {
      await api.setSiteReturnState(id, state);
      store.set({ refused: null });
      if (onRecord) await actions.refreshProgress();
      if (onWorks) await actions.refreshWorks();
      return true;
    } catch (err) {
      /* Left as it was, and said. See setProgressEntryState. */
      return refusedAt(id, err, 'That return could not be moved.');
    }
  },

  /* --------------------------- the safety record --------------------------- */

  openIncident(bridgeId: string): void {
    store.set({
      ...ONE_SITE_FORM,
      incidentBridgeId: bridgeId,
      incidentDraft: emptyIncidentDraft(),
      incidentSaving: false,
      incidentError: null,
    });
  },

  closeIncident(): void {
    if (store.get().incidentBridgeId === null) return;
    store.set({ incidentBridgeId: null, incidentSaving: false, incidentError: null });
  },

  setIncidentField(key: keyof IncidentDraft, value: string): void {
    store.set((s) => ({ incidentDraft: { ...s.incidentDraft, [key]: value } }));
  },

  async saveIncident(): Promise<void> {
    const bridgeId = store.get().incidentBridgeId;
    if (bridgeId === null || store.get().incidentSaving) return;

    const d = store.get().incidentDraft;
    if (d.kind.trim() === '') {
      store.set({ incidentError: 'Say what kind of thing happened.' });
      return;
    }
    if (!isIsoDay(d.occurredOn)) {
      store.set({ incidentError: 'Give the day it happened.' });
      return;
    }
    /* THE DESCRIPTION, REFUSED HERE AS WELL AS BY THE ENDPOINT. A kind and a
       date on their own are a tally mark: the number goes up and nobody can
       act on it. */
    if (d.description.trim() === '') {
      store.set({ incidentError: 'Say what happened. A kind and a date on their own are a tally mark.' });
      return;
    }
    /* What the form did not ask for is not sent: a count typed under one kind
       and left behind by a change to a kind that hides the box. */
    const asks = incidentOption(d.kind);
    const asksPeople = asks?.asksPeople ?? true;
    const people = asksPeople ? optionalCount(d.peopleInvolved) : null;
    if (people === 'bad' || (people !== null && !Number.isInteger(people))) {
      store.set({ incidentError: 'The people involved is a whole number.' });
      return;
    }
    const daysLost = (asks?.asksDaysLost ?? true) ? optionalCount(d.daysLost) : null;
    if (daysLost === 'bad') {
      store.set({ incidentError: 'Days lost must be a number.' });
      return;
    }

    store.set({ incidentSaving: true, incidentError: null });
    const clientId = crypto.randomUUID();
    const payload = {
      bridgeId,
      clientId,
      kind: d.kind,
      occurredOn: d.occurredOn,
      element: d.element.trim() || null,
      description: d.description.trim(),
      action: d.action.trim(),
      peopleInvolved: people,
      peopleNames: asksPeople ? d.peopleNames.trim() : '',
      daysLost,
      ...(d.photo ? { image: d.photo } : {}),
    };

    try {
      await api.createIncident(payload);
      await actions.refreshProgress();
      if (store.get().incidentBridgeId === bridgeId) {
        store.set({ incidentBridgeId: null, incidentSaving: false, incidentError: null });
      }
    } catch (err) {
      const unreachable = err instanceof api.ApiError && err.status === null;

      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'incident', bridgeId, clientId, body: payload });
        await actions.refreshQueue();
        if (store.get().incidentBridgeId === bridgeId) {
          store.set({ incidentBridgeId: null, incidentSaving: false, incidentError: null });
        }
        return;
      }

      if (store.get().incidentBridgeId === bridgeId) {
        store.set({
          incidentSaving: false,
          incidentError: err instanceof Error ? err.message : 'The incident could not be recorded.',
        });
      }
    }
  },

  /** Record what was done about an incident, and whether that is finished. */
  async closeIncidentEntry(
    id: string,
    action: string,
    closed: boolean,
    closedOn: string | null = null,
  ): Promise<void> {
    try {
      await api.closeIncident(id, action, closed, closedOn);
      await actions.refreshProgress();
      if (store.get().incidentClose?.id === id) store.set({ incidentClose: null, incidentError: null });
    } catch (err) {
      store.set({
        incidentError: err instanceof Error ? err.message : 'That could not be recorded.',
      });
    }
  },

  /**
   * Open the close-out of one incident on the site record (LC-50), with what has
   * been written so far and today's date. The resident engineer's
   * (`works.supervise`); the endpoint refuses anybody else.
   */
  openIncidentClose(id: string): void {
    const i = store.get().incidents.find((x) => x.id === id);
    if (!i || i.closed) return;
    store.set({
      incidentClose: { id, action: i.action, closedOn: new Date().toISOString().slice(0, 10) },
      incidentError: null,
    });
  },

  setIncidentCloseField(key: 'action' | 'closedOn', value: string): void {
    const c = store.get().incidentClose;
    if (c) store.set({ incidentClose: { ...c, [key]: value } });
  },

  cancelIncidentClose(): void {
    store.set({ incidentClose: null, incidentError: null });
  },

  /** Close the incident being closed out. Refused here as the endpoint refuses it. */
  async submitIncidentClose(): Promise<void> {
    const c = store.get().incidentClose;
    if (!c) return;
    if (c.action.trim() === '') {
      store.set({ incidentError: 'Say what was done before closing an incident.' });
      return;
    }
    if (!isIsoDay(c.closedOn)) {
      store.set({ incidentError: 'Give the day it was closed out.' });
      return;
    }
    await actions.closeIncidentEntry(c.id, c.action.trim(), true, c.closedOn);
  },

  /* --------------------------- documents --------------------------- */

  openDocument(bridgeId: string, eventId: string | null = null): void {
    store.set({
      documentBridgeId: bridgeId,
      documentDraft: { ...emptyDocumentDraft(), eventId: eventId ?? '' },
      documentSaving: false,
      documentError: null,
    });
  },

  closeDocument(): void {
    if (store.get().documentBridgeId === null) return;
    store.set({ documentBridgeId: null, documentSaving: false, documentError: null });
  },

  setDocumentField(key: keyof DocumentDraft, value: string): void {
    store.set((s) => ({ documentDraft: { ...s.documentDraft, [key]: value } }));
  },

  /**
   * Attach the chosen file.
   *
   * THE TITLE IS FILLED FROM THE FILE NAME WHERE NOBODY HAS TYPED ONE, minus
   * its extension. A required field that somebody has to retype from the thing
   * they just chose is a required field they will fill with "scan" — and the
   * whole reason the title is required is that `scan_0042.pdf` is not a
   * document register. Suggested rather than forced: it is a normal field and
   * can be replaced.
   */
  setDocumentFile(file: { dataUrl: string; filename: string; bytes: number } | null): void {
    store.set((s) => ({
      documentDraft: file
        ? {
            ...s.documentDraft,
            file: file.dataUrl,
            filename: file.filename,
            bytes: file.bytes,
            title:
              s.documentDraft.title.trim() === ''
                ? file.filename.replace(/\.[^.]+$/, '')
                : s.documentDraft.title,
          }
        : { ...s.documentDraft, file: null, filename: '', bytes: 0 },
      documentError: null,
    }));
  },

  setDocumentError(message: string): void {
    store.set({ documentError: message });
  },

  async saveDocument(): Promise<void> {
    const bridgeId = store.get().documentBridgeId;
    if (bridgeId === null || store.get().documentSaving) return;

    const d = store.get().documentDraft;
    if (d.kind.trim() === '') {
      store.set({ documentError: 'Say what kind of document this is.' });
      return;
    }
    if (d.title.trim() === '') {
      store.set({ documentError: 'Give it a title somebody could search for.' });
      return;
    }
    if (d.file === null) {
      store.set({ documentError: 'Choose the file.' });
      return;
    }
    if (d.datedOn.trim() !== '' && !isIsoDay(d.datedOn.trim())) {
      store.set({ documentError: 'The date on the document is not a date.' });
      return;
    }

    store.set({ documentSaving: true, documentError: null });
    const clientId = crypto.randomUUID();

    try {
      await api.createDocument({
        bridgeId,
        clientId,
        kind: d.kind,
        title: d.title.trim(),
        file: d.file,
        filename: d.filename,
        eventId: d.eventId.trim() || null,
        datedOn: d.datedOn.trim() || null,
        note: d.note.trim(),
      });
      await actions.refreshProgress();
      if (store.get().documentBridgeId === bridgeId) {
        store.set({ documentBridgeId: null, documentSaving: false, documentError: null });
      }
    } catch (err) {
      /* NOT QUEUED, and it is the one field-side write that is not. A document
         is up to eight megabytes; the offline queue holds its payloads in
         IndexedDB and replays them oldest-first, so one certificate photographed
         at full size would sit in front of every pour recorded after it and
         fail the whole drain on a connection that cannot carry it. Uploading a
         certificate is also desk work in practice — it comes from a laboratory
         by email — so the register says plainly that this one needs a
         connection rather than pretending to hold it. */
      store.set({
        documentSaving: false,
        documentError:
          err instanceof api.ApiError && err.status === null
            ? 'A document needs a connection — it is too large to queue. Try again where there is signal.'
            : err instanceof Error
              ? err.message
              : 'The document could not be filed.',
      });
    }
  },

  /* --------------------------- test results (LC-53) --------------------------- */

  openTest(bridgeId: string, eventId: string | null = null): void {
    store.set({
      testBridgeId: bridgeId,
      testDraft: { ...emptyTestDraft(), eventId: eventId ?? '' },
      testSaving: false,
      testError: null,
    });
  },

  closeTest(): void {
    if (store.get().testBridgeId === null) return;
    store.set({ testBridgeId: null, testSaving: false, testError: null });
  },

  setTestField(key: keyof TestDraft, value: string): void {
    store.set((s) => ({ testDraft: { ...s.testDraft, [key]: value } }));
  },

  /**
   * Record the result on the form. Only the kind's own figures are sent, and no
   * pass: the register derives it, and a failure comes back with its NCR.
   * Queued where there is no signal, as an incident is — a cube is crushed at a
   * site laboratory as often as at a desk.
   */
  async saveTest(): Promise<void> {
    const bridgeId = store.get().testBridgeId;
    if (bridgeId === null || store.get().testSaving) return;

    const d = store.get().testDraft;
    const family = testFamily(d.kind);
    if (family === null) {
      store.set({ testError: 'Say what kind of test this is.' });
      return;
    }
    if (!isIsoDay(d.testedOn)) {
      store.set({ testError: 'Give the day of the test.' });
      return;
    }
    const n = (v: string): number | null => (v.trim() === '' || !Number.isFinite(Number(v)) ? null : Number(v));
    const part = splitChoice(d.component);
    const figures: Record<string, unknown> =
      family === 'concrete'
        ? { mix: d.mix.trim(), ageDays: n(d.ageDays), strengthMpa: n(d.strengthMpa), requiredMpa: n(d.requiredMpa) }
        : family === 'integrity'
          ? { method: d.method, resultClass: d.resultClass }
          : family === 'load'
            ? { testLoadKn: n(d.testLoadKn), settlementMm: n(d.settlementMm), allowedMm: n(d.allowedMm) }
            : { lot: d.lot.trim(), grade: d.grade.trim(), complies: d.complies === '' ? null : d.complies === 'yes' };
    const missing = Object.entries(figures).find(([, v]) => v === null || v === '');
    if (missing) {
      store.set({ testError: `Give the ${TEST_FIELD_WORDS[missing[0]] ?? missing[0]}.` });
      return;
    }
    if ((family === 'integrity' || family === 'load') && part.component === null) {
      store.set({ testError: 'Say which pile was tested.' });
      return;
    }

    store.set({ testSaving: true, testError: null });
    const clientId = crypto.randomUUID();
    const payload = {
      bridgeId,
      clientId,
      kind: d.kind,
      testedOn: d.testedOn,
      eventId: d.eventId || null,
      documentId: d.documentId || null,
      component: part.component,
      componentId: part.componentId,
      note: d.note.trim(),
      ...figures,
    };

    try {
      await api.createTest(payload);
      await actions.refreshProgress();
      if (store.get().testBridgeId === bridgeId) {
        store.set({ testBridgeId: null, testSaving: false, testError: null });
      }
    } catch (err) {
      const unreachable = err instanceof api.ApiError && err.status === null;
      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'site-test', bridgeId, clientId, body: payload });
        await actions.refreshQueue();
        if (store.get().testBridgeId === bridgeId) {
          store.set({ testBridgeId: null, testSaving: false, testError: null });
        }
        return;
      }
      if (store.get().testBridgeId === bridgeId) {
        store.set({
          testSaving: false,
          testError: err instanceof Error ? err.message : 'The result could not be recorded.',
        });
      }
    }
  },

  /* --------------------------- hold points, NCRs and RFIs (LC-54) --------------------------- */

  openItem(bridgeId: string, kind = '', activityId = ''): void {
    store.set({
      itemBridgeId: bridgeId,
      itemDraft: { ...emptyItemDraft(kind), activityId },
      itemSaving: false,
      itemError: null,
    });
  },

  closeItem(): void {
    if (store.get().itemBridgeId === null) return;
    store.set({ itemBridgeId: null, itemSaving: false, itemError: null });
  },

  setItemField(key: keyof ItemDraft, value: string): void {
    store.set((s) => ({ itemDraft: { ...s.itemDraft, [key]: value } }));
  },

  /**
   * Raise the item on the form. The endpoint numbers it, and refuses a kind the
   * person's roles do not raise. A hold point names the activity it holds, by
   * id and by name, so it still reads once a later baseline has dropped it.
   */
  async saveItem(): Promise<void> {
    const bridgeId = store.get().itemBridgeId;
    if (bridgeId === null || store.get().itemSaving) return;

    const d = store.get().itemDraft;
    if (d.kind === '') {
      store.set({ itemError: 'Say whether this is a hold point, an NCR or an RFI.' });
      return;
    }
    if (d.title.trim() === '') {
      store.set({ itemError: 'Give it a title.' });
      return;
    }
    const activity = store.get().baseline?.activities.find((a) => a.activityId === d.activityId) ?? null;
    if (d.kind === 'hold-point' && activity === null) {
      store.set({ itemError: 'Say which planned activity the hold point holds.' });
      return;
    }
    if (d.dueOn.trim() !== '' && !isIsoDay(d.dueOn.trim())) {
      store.set({ itemError: 'The due date is not a date.' });
      return;
    }

    store.set({ itemSaving: true, itemError: null });
    const clientId = crypto.randomUUID();
    const part = splitChoice(d.component);
    const payload = {
      bridgeId,
      clientId,
      kind: d.kind,
      title: d.title.trim(),
      detail: d.detail.trim(),
      owner: d.owner.trim() || null,
      dueOn: d.dueOn.trim() || null,
      activityId: activity?.activityId ?? null,
      activityName: activity?.name ?? null,
      component: part.component,
      componentId: part.componentId,
      eventId: d.eventId || null,
    };

    try {
      await api.createItem(payload);
      await actions.refreshProgress();
      if (store.get().itemBridgeId === bridgeId) {
        store.set({ itemBridgeId: null, itemSaving: false, itemError: null });
      }
    } catch (err) {
      const unreachable = err instanceof api.ApiError && err.status === null;
      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'site-item', bridgeId, clientId, body: payload });
        await actions.refreshQueue();
        if (store.get().itemBridgeId === bridgeId) {
          store.set({ itemBridgeId: null, itemSaving: false, itemError: null });
        }
        return;
      }
      if (store.get().itemBridgeId === bridgeId) {
        store.set({
          itemSaving: false,
          itemError: err instanceof Error ? err.message : 'The item could not be raised.',
        });
      }
    }
  },

  /** Open the answer, close-out or release of one item, under its row. */
  openItemAct(id: string, act: ItemAct): void {
    store.set({ itemAct: { id, act, note: '' }, itemActError: null });
  },

  setItemActNote(note: string): void {
    const a = store.get().itemAct;
    if (a) store.set({ itemAct: { ...a, note } });
  },

  cancelItemAct(): void {
    store.set({ itemAct: null, itemActError: null });
  },

  /**
   * Send the act. NOT QUEUED, as closing an incident is not: it is a decision
   * made at a desk, and the endpoint's refusal — somebody releasing a hold point
   * they raised — has to reach the person while they are looking at it.
   */
  async submitItemAct(): Promise<void> {
    const a = store.get().itemAct;
    if (!a) return;
    if (a.note.trim() === '') {
      store.set({ itemActError: a.act === 'answer' ? 'Write the answer.' : 'Say what was found.' });
      return;
    }
    const onWorks = store.get().worksItems.some((i) => i.id === a.id);
    try {
      await api.actOnItem(a.id, a.act, a.note.trim());
      store.set({ itemAct: null, itemActError: null });
      await actions.refreshProgress();
      if (onWorks) await actions.refreshWorks();
    } catch (err) {
      store.set({ itemActError: err instanceof Error ? err.message : 'That could not be recorded.' });
    }
  },

  /**
   * Move one construction entry through draft -> review -> accepted, or back.
   *
   * NOT QUEUED, and that is the line between the two halves of this feature.
   * Recording is field work and goes through the queue because the site has no
   * signal; reviewing is office work done by somebody looking at a screen with
   * a connection, and a queued review replayed an hour later would accept an
   * entry whose author had since been asked to correct it.
   *
   * The whole record is re-read rather than the one row patched in place,
   * because the server stamps the reviewer and the time and the screen should
   * print what was actually stored, not what this device assumed.
   */
  async setProgressEntryState(eventId: string, state: ProgressState): Promise<boolean> {
    /* WHICH LIST THE ROW CAME FROM, decided before the request rather than
       after it: the same row is drawn by the record's Works card and by the
       Works screen's queue, and re-reading both on every review would double
       every request to serve whichever screen is not on show. Checked against
       both lists rather than against the current screen, because the answer is
       about where the row is held and not about what is being looked at. */
    const onRecord = store.get().progress.some((e) => e.id === eventId);
    const onWorks = store.get().worksEvents.some((e) => e.id === eventId);

    try {
      await api.setProgressState(eventId, state);
      store.set({ refused: null });
      if (onRecord) await actions.refreshProgress();
      if (onWorks) await actions.refreshWorks();
      return true;
    } catch (err) {
      /* Left exactly as it was, and SAID. A state that flipped on screen without
         landing would be worse than one that did not move — this is the column
         a report to the client distinguishes a verified entry by. */
      return refusedAt(eventId, err, 'That entry could not be moved.');
    }
  },

  /** Re-read the model index for the selected structure. */
  async refreshModels(): Promise<void> {
    if (store.get().backend !== 'live') return;
    /* CAPTURED, AND CHECKED AGAIN BELOW — the same guard `loadBridgeContext`
       keeps, and it became load-bearing when this started writing to the device:
       an answer that lands after the user has tapped another row would otherwise
       be adopted under the new structure's name and then REMEMBERED under it,
       where a wrong index outlives the page instead of the moment. */
    const id = store.get().selectedId;
    try {
      const models = await api.fetchModels(id);
      if (store.get().selectedId !== id) return;
      store.set({ models, modelsRead: 'read', modelsRemembered: false });
      rememberModelIndex(id, models);
    } catch {
      /* The same rule `loadBridgeContext` states, and this is the other half of
         the same fault: a re-read that fails says the register did not answer. It
         does not say the structure has no models, and it does not throw away the
         index the last successful read left — which, on this path, is very often
         one the user is looking at a bridge in. */
      if (store.get().selectedId !== id) return;
      store.set({ modelsRead: 'unreachable' });
    }
  },

  /**
   * Re-read the defect log for every structure, for the overview's network-wide
   * rollups.
   *
   * A fan-out of one request per bridge, run in parallel and settled
   * individually: one structure whose model state is unreadable must not cost
   * the other twenty their counts. Structures that fail are simply absent from
   * the map, which the overview reports as "not counted" rather than as zero.
   */
  async refreshAllDefects(inFlight?: Promise<RegisterDefect[]>): Promise<void> {
    /* TEMPORARY — THE FWO DEMONSTRATION, AND THE ONLY LINE OF IT IN THIS FILE.
       Empty for every department and every build that did not ask for it; see
       the header of src/data/fwo-demo.ts, and delete this with that file. It is
       a FALLBACK rather than a merge on purpose: a real defect from the backend
       always wins, so the moment FWO's own rows exist the fabricated ones stop
       being drawn rather than sitting alongside them. */
    const demo = () => demoDefectsFor(active().id);

    /* APPENDED, not chosen between — the same correction `hydrate` needed and
       for the same cause. D1 holds a defect against each of the two real
       Pakistan records, so "use the demonstration only when the query came back
       empty" meant it was never used at all once anybody signed in. A real row
       wins on id. */
    const withDemo = (rows: RegisterDefect[]): RegisterDefect[] => {
      const known = new Set(rows.map((d) => d.id));
      return [...rows, ...demo().filter((d) => !known.has(d.id))];
    };

    if (store.get().backend !== 'live') {
      store.set({ registerDefects: demo() });
      return;
    }
    try {
      store.set({ registerDefects: withDemo(await (inFlight ?? api.fetchRegisterDefects())) });
    } catch {
      /* One request now, so a failure is total rather than partial — which is
         simpler to report than the old fan-out's "counts for the first N".
         Cleared rather than left stale: a number from the last time the network
         worked, drawn as though it were current, is the thing this register
         does not do. */
      store.set({ registerDefects: demo() });
    }
  },

  /* --------------------------- measuring on a photograph --------------------------- */

  /**
   * Open the measuring assist over the defect draft's photograph.
   *
   * The natural dimensions are passed in by the caller because only the DOM
   * knows them — the dialog reads `naturalWidth` off a decoded image. They are
   * the frame every distance is computed in, so a zero here would divide by zero
   * later; `mmPerPixel` refuses that rather than returning `Infinity`, and this
   * refuses to open at all.
   */
  openMeasure(index: number, width: number, height: number): void {
    const photo = store.get().defectDraft.photos[index];
    const d = store.get().defectDraft;
    if (!photo || width <= 0 || height <= 0) return;
    store.set({
      measure: {
        photo,
        width,
        height,
        code: d.code,
        phase: 'scale',
        scalePoints: [],
        referenceMm: '',
        target: null,
        points: [],
      },
    });
  },

  closeMeasure(): void {
    store.set({ measure: null });
  },

  measurePhase(phase: 'scale' | 'trace'): void {
    store.set((s) => {
      if (!s.measure) return {};
      /* Where the category takes one measurement only, it is chosen already:
         nothing to pick, and nothing to leave "Use" waiting on (QA 2026-09-30). */
      const only = traceableFields(s.measure.code);
      const target = s.measure.target ?? (phase === 'trace' && only.length === 1 ? only[0]!.key : null);
      return { measure: { ...s.measure, phase, target } };
    });
  },

  /**
   * Add a mark, to whichever set the current phase is collecting.
   *
   * The scale takes exactly two: a third tap replaces the pair and starts again,
   * rather than being ignored. Ignoring it leaves somebody tapping at a picture
   * that has stopped responding, and the commonest reason for a third tap is
   * that the first two were in the wrong place.
   */
  addMeasurePoint(p: PhotoPoint): void {
    store.set((s) => {
      if (!s.measure) return {};
      if (s.measure.phase === 'scale') {
        const next = s.measure.scalePoints.length >= 2 ? [p] : [...s.measure.scalePoints, p];
        return { measure: { ...s.measure, scalePoints: next } };
      }
      /* A width is a span between two sides, so a third tap replaces the pair
         there too. A length and an area are paths and take as many as offered. */
      if (s.measure.target === 'widthMm') {
        const next = s.measure.points.length >= 2 ? [p] : [...s.measure.points, p];
        return { measure: { ...s.measure, points: next } };
      }
      return { measure: { ...s.measure, points: [...s.measure.points, p] } };
    });
  },

  undoMeasurePoint(): void {
    store.set((s) =>
      s.measure
        ? {
            measure:
              s.measure.phase === 'scale'
                ? { ...s.measure, scalePoints: s.measure.scalePoints.slice(0, -1) }
                : { ...s.measure, points: s.measure.points.slice(0, -1) },
          }
        : {},
    );
  },

  clearMeasurePoints(): void {
    store.set((s) =>
      s.measure
        ? {
            measure:
              s.measure.phase === 'scale'
                ? { ...s.measure, scalePoints: [] }
                : { ...s.measure, points: [] },
          }
        : {},
    );
  },

  setMeasureReference(value: string): void {
    store.set((s) => (s.measure ? { measure: { ...s.measure, referenceMm: value } } : {}));
  },

  /**
   * Choose what is being measured. Changing it clears the trace — it is a
   * different shape — but choosing it for the first time keeps what was traced
   * before it was chosen (QA 2026-09-30).
   */
  setMeasureTarget(key: string): void {
    store.set((s) =>
      s.measure
        ? { measure: { ...s.measure, target: key, points: s.measure.target === null ? s.measure.points : [] } }
        : {},
    );
  },

  /**
   * Write the traced measurement into the defect draft and close.
   *
   * THE METHOD IS SET TO `photo-derived` AND THE SCALE SOURCE IS RECORDED. Both
   * are the point of the item: a figure traced on a photograph must not reach
   * the record looking like a gauge reading, and the commonest way one of these
   * is wrong is that the reference was mismeasured — which nobody can check
   * later unless the reference is written down beside the answer.
   *
   * It also overwrites the method even where the draft already said `measured`.
   * That is deliberate: the number in the box is now the traced one, and leaving
   * the stronger claim beside it would be the worst of both.
   */
  acceptMeasurement(): void {
    const m = store.get().measure;
    if (!m) return;

    const result = traceResult(m);
    const scale = scaleOf(m);
    if (!result || !scale) return;

    store.set((s) => ({
      defectDraft: {
        ...s.defectDraft,
        quantity: {
          ...s.defectDraft.quantity,
          [result.field.key]: String(Number(result.value.toFixed(4))),
        },
        method: 'photo-derived',
        instrument: scaleSourceNote(scale),
      },
      measure: null,
    }));
  },

  /**
   * Re-read the approved district rate tables.
   *
   * Cleared rather than left stale on a failure, for exactly the reason
   * refreshAllDefects gives: a rate from the last time the network worked, drawn
   * under a heading saying it was approved by a named office, is worse than no
   * rate. And an empty list is not an error state here — it is the state this
   * register ships in.
   */
  async refreshRateTables(inFlight?: Promise<api.StoredRateTable[]>): Promise<void> {
    if (store.get().backend !== 'live') {
      store.set({ rateTables: [] });
      return;
    }
    try {
      store.set({ rateTables: await (inFlight ?? api.fetchRateTables()) });
    } catch {
      store.set({ rateTables: [] });
    }
  },

  /**
   * Re-read the department's wording of recommended work. On a failure the
   * built-in wording is shown, which is what a register with no rows reads as.
   */
  async refreshTreatmentWording(inFlight?: Promise<StoredWording[]>): Promise<void> {
    if (store.get().backend !== 'live') {
      store.set({ treatmentWording: [] });
      return;
    }
    try {
      store.set({ treatmentWording: await (inFlight ?? api.fetchTreatmentWording()) });
    } catch {
      store.set({ treatmentWording: [] });
    }
  },

  /* --------------------------- the session --------------------------- */

  /**
   * The register has stopped recognising this browser.
   *
   * Called from the hook in src/core/api.ts rather than from a catch block, so
   * that it does not matter which request was in flight when the session lapsed.
   * Idempotent: several calls in flight at once will each report it, and the
   * screen must not be rebuilt four times over one event.
   */
  sessionLost(lost: api.SessionLost): void {
    const s = store.get();
    if (s.session === 'lost' && s.loginUrl === (lost.loginUrl ?? null)) return;
    rememberSignedIn(false);
    store.set({
      session: 'lost',
      loginUrl: lost.loginUrl ?? null,
      sessionNote: lost.message,
      /* The identity goes with it. Leaving the old one in place would have the
         title bar naming somebody who is no longer signed in, and the role table
         deciding what to draw from a role the register has stopped agreeing to. */
      identity: null,
    });
  },

  setSignInField(key: 'signInUser' | 'signInPassword', value: string): void {
    store.set({ [key]: value } as Partial<State>);
  },

  toggleSignInReveal(): void {
    store.set({ signInReveal: !store.get().signInReveal });
  },

  /**
   * Sign in with a username and password.
   *
   * WHERE CLOUDFLARE ACCESS IS STILL IN FRONT, this form is never reached: the
   * edge authenticates before the application loads, and the sign-in screen only
   * appears once a session has lapsed. The two coexist on purpose while Access
   * is being retired — see functions/lib/access.ts.
   *
   * The session is a cookie the browser keeps and this code never sees. There is
   * nothing to store here, which is the point: an HttpOnly cookie cannot be read
   * by script, so it cannot be stolen by any bug in this file.
   */
  async submitSignIn(): Promise<void> {
    const s = store.get();
    if (s.signInBusy) return;
    if (!s.signInUser.trim() || !s.signInPassword) {
      store.set({ signInError: 'Type your username and password.' });
      return;
    }

    store.set({ signInBusy: true, signInError: null });
    try {
      /* Into THIS window's register, where it was opened for one — see
         `requestedTenant`. The session is that country's and no other's. */
      const who = await api.signIn(s.signInUser.trim(), s.signInPassword, requestedTenant());
      rememberSignedIn(true);
      store.set({
        session: 'ok',
        sessionNote: null,
        signInBusy: false,
        /* The password leaves state the moment it has been used. It was only
           ever here to be typed into. */
        signInPassword: '',
        signInReveal: false,
        signInError: null,
        mustChangePassword: who.mustChange,
        passwordCurrent: '',
        passwordNext: '',
        passwordError: null,
      });
      await actions.hydrate();
    } catch (err) {
      store.set({
        signInBusy: false,
        signInPassword: '',
        signInError: err instanceof Error ? err.message : 'That did not work.',
      });
    }
  },

  setPasswordField(key: 'passwordCurrent' | 'passwordNext', value: string): void {
    store.set({ [key]: value } as Partial<State>);
  },

  /** Change your own password. The current one is required — a session is not enough. */
  async submitPasswordChange(): Promise<void> {
    const s = store.get();
    if (s.passwordBusy) return;

    store.set({ passwordBusy: true, passwordError: null, passwordDone: null });
    try {
      await api.changePassword(s.passwordCurrent, s.passwordNext);
      store.set({
        passwordBusy: false,
        passwordCurrent: '',
        passwordNext: '',
        passwordError: null,
        passwordDone: 'Your password has been changed. Every other browser you were signed in on has been signed out.',
        mustChangePassword: false,
      });
      await actions.refreshIdentity();
    } catch (err) {
      store.set({
        passwordBusy: false,
        passwordError: err instanceof Error ? err.message : 'That password was not accepted.',
      });
    }
  },

  /**
   * End this browser's session.
   *
   * Through the API rather than by navigating, so the row is deleted server-side
   * rather than the cookie merely being forgotten — a session nobody deleted is
   * a session that still works. The reload afterwards is what puts the sign-in
   * screen up, from a clean store.
   */
  async signOut(): Promise<void> {
    try {
      await api.signOutRequest();
    } catch {
      /* Nothing to report and nothing to do differently: the cookie is cleared
         by the response where there was one, and a failure here means there was
         no session to end. */
    }
    window.location.assign('/');
  },

  /** Issue somebody a new password, as an administrator. */
  async issuePassword(email: string): Promise<void> {
    if (store.get().personSaving) return;
    store.set({ personSaving: true, peopleError: null, peopleNote: null });
    try {
      const issued = await api.issuePassword(email);
      store.set({ personSaving: false, issuedPassword: issued });
      await actions.refreshPeople();
    } catch (err) {
      store.set({
        personSaving: false,
        peopleError: err instanceof Error ? err.message : 'A password could not be issued.',
      });
    }
  },

  /** Close the one-time password panel. */
  clearIssuedPassword(): void {
    store.set({ issuedPassword: null });
  },

  /* --------------------------- people and roles --------------------------- */

  /**
   * Read the people list.
   *
   * A 403 here is not a fault to hide: it means somebody reached this screen
   * without the role for it — which the nav pane tries not to allow and a pasted
   * `#/people` link can — and the message the endpoint sends says so better than
   * an empty table would.
   */
  async refreshPeople(): Promise<void> {
    if (store.get().backend !== 'live') {
      store.set({
        people: null,
        peopleError:
          'The register has not been reached, so who holds which role cannot be read. Roles are ' +
          'never held on this device.',
      });
      return;
    }

    store.set({ peopleLoading: true, peopleError: null });
    try {
      const report = await api.fetchPeople();
      store.set({
        people: report.people,
        adminLog: report.log,
        peopleDeploymentAdmins: report.deploymentAdmins,
        peopleLoading: false,
        peopleError: null,
      });
    } catch (err) {
      store.set({
        people: null,
        peopleLoading: false,
        peopleError: err instanceof Error ? err.message : 'The people list could not be read.',
      });
    }
  },

  /** Open the form on a new grant. */
  openGrant(): void {
    store.set({
      personDraft: emptyPersonDraft(),
      personEditing: null,
      peopleError: null,
      peopleNote: null,
    });
  },

  /**
   * Open the form on somebody who already has a row.
   *
   * The address is carried in `personEditing` and the field is drawn read-only
   * from it — see the note on that field. An administrator from the
   * deployment's ADMIN_EMAILS may be granted other roles here; their `admin`
   * and their suspension are the deployment's, and the form draws them fixed.
   */
  openPersonEdit(person: Person): void {
    store.set({
      personDraft: {
        email: person.email,
        roles: [...person.roles],
        suspended: person.suspended,
        fullName: person.fullName ?? '',
        division: person.division ?? '',
        note: person.note ?? '',
      },
      personEditing: person.email,
      peopleError: null,
      peopleNote: null,
    });
  },

  closePersonForm(): void {
    store.set({ personDraft: null, personEditing: null, personSaving: false });
  },

  setPersonField(key: 'email' | 'fullName' | 'division' | 'note', value: string): void {
    store.set((s) =>
      s.personDraft ? { personDraft: { ...s.personDraft, [key]: value } } : {},
    );
  },

  /** Tick or untick one role on the open form. */
  setPersonRole(role: Role, held: boolean): void {
    store.set((s) => {
      const draft = s.personDraft;
      if (!draft) return {};
      const roles = held
        ? [...new Set([...draft.roles, role])]
        : draft.roles.filter((r) => r !== role);
      return { personDraft: { ...draft, roles } };
    });
  },

  /** Suspend, or lift the suspension, on the open form. The roles are kept either way. */
  setPersonSuspended(suspended: boolean): void {
    store.set((s) => (s.personDraft ? { personDraft: { ...s.personDraft, suspended } } : {}));
  },

  /**
   * Grant the role, or change it.
   *
   * Refused here for the two things a form can know — an address that is not one,
   * and a role nobody chose — and sent for everything else. The interesting
   * refusals are the server's and are meant to be: whether this would leave the
   * register with no administrator is a question about the whole table, and a
   * client holding one page of it cannot answer it. See lastAdministratorBlock.
   */
  async submitPerson(): Promise<void> {
    const s = store.get();
    const draft = s.personDraft;
    if (!draft || s.personSaving) return;

    const email = normaliseIdentity(s.personEditing ?? draft.email);
    if (!isGrantableIdentity(email)) {
      store.set({
        peopleError:
          'A username can be an email address or a plain name — letters, digits and . _ % + - ' +
          'only, at least three characters. Use "service-token:<client id>" for a machine.',
      });
      return;
    }

    store.set({ personSaving: true, peopleError: null, peopleNote: null });
    try {
      const person = await api.putPerson({
        email,
        roles: draft.roles,
        suspended: draft.suspended,
        fullName: draft.fullName.trim() || null,
        division: draft.division.trim() || null,
        note: draft.note.trim() || null,
      });
      store.set({
        personDraft: null,
        personEditing: null,
        personSaving: false,
        peopleNote: `${person.email}: ${rolesLabel(person.roles, person.suspended)}.`,
      });
      await actions.refreshPeople();
      /* Their own roles may be the ones that just changed — an administrator who
         granted themselves `rater` has a nav pane that should now carry Bridge
         Rating's controls. Re-asked rather than patched from the response, because the
         authority on what this browser may do is /api/health and not a reply
         about somebody else. */
      await actions.refreshIdentity();
    } catch (err) {
      store.set({
        personSaving: false,
        peopleError: err instanceof Error ? err.message : 'The role could not be saved.',
      });
    }
  },

  /** Ask before removing a row, and stop asking. */
  askRemovePerson(email: string | null): void {
    store.set({ personRemoving: email, peopleError: null, peopleNote: null });
  },

  /**
   * Remove somebody's row.
   *
   * The endpoint's own sentence is kept and shown, because it says the thing
   * this action does NOT do: removing an account leaves somebody reading the
   * register where Access still admits them, not with no access. Suspending is
   * how to say no.
   */
  async removePerson(email: string): Promise<void> {
    if (store.get().personSaving) return;
    store.set({ personSaving: true, peopleError: null, peopleNote: null });
    try {
      const note = await api.deletePerson(email);
      store.set({ personSaving: false, personRemoving: null, peopleNote: note });
      await actions.refreshPeople();
      await actions.refreshIdentity();
    } catch (err) {
      store.set({
        personSaving: false,
        peopleError: err instanceof Error ? err.message : 'The row could not be removed.',
      });
    }
  },

  /* --------------------------- district rates --------------------------- */

  openRates(district = ''): void {
    store.set((s) => ({
      ratesOpen: true,
      ratesFaults: [],
      /* Whatever was typed before is kept — closing this to go and look
         something up must not lose a half-pasted table. The district is
         overwritten from the caller, because the commonest way this is opened is
         from a structure whose estimate reported no rates, and retyping the
         district already on screen is a step this does not need. */
      ratesDraft: { ...s.ratesDraft, ...(district ? { district } : {}) },
    }));
  },

  closeRates(): void {
    store.set({ ratesOpen: false, ratesFaults: [] });
  },

  setRatesField(key: keyof RatesDraft, value: string): void {
    store.set((s) => ({ ratesDraft: { ...s.ratesDraft, [key]: value } }));
  },

  /**
   * Parse, refuse, and load one district's approved rates.
   *
   * Refused client-side before it is sent, through the same `validateRateTable`
   * the endpoint refuses through — so a spreadsheet with a unit error is a list
   * of faults on screen rather than a round trip. The parse faults and the
   * validation faults are reported TOGETHER: a paste with one unreadable line
   * and one wrong unit has two problems, and telling somebody about them one at
   * a time is how a ten-minute job becomes an afternoon.
   */
  async submitRateTable(): Promise<void> {
    if (store.get().ratesSaving) return;
    const d = store.get().ratesDraft;

    const parsed = parseRateRows(d.rows);
    const table: RateTable = {
      district: d.district.trim(),
      fiscalYear: d.fiscalYear.trim(),
      approvedOn: d.approvedOn.trim(),
      approvedBy: d.approvedBy.trim(),
      rows: parsed.rows,
      ...(d.contingencyPct.trim() ? { contingencyPct: Number(d.contingencyPct) } : {}),
      ...(d.vatPct.trim() ? { vatPct: Number(d.vatPct) } : {}),
    };

    const faults = [...parsed.faults, ...validateRateTable(table)];
    if (!parsed.rows.length) faults.push('No rates were read out of the pasted text.');
    if (faults.length) {
      store.set({ ratesFaults: faults });
      return;
    }

    store.set({ ratesSaving: true, ratesFaults: [] });
    try {
      await api.putRateTable(table);
      await actions.refreshRateTables();
      store.set({ ratesSaving: false, ratesOpen: false, ratesDraft: emptyRatesDraft() });
    } catch (err) {
      store.set({
        ratesSaving: false,
        ratesFaults: [err instanceof Error ? err.message : 'The rate table could not be loaded.'],
      });
    }
  },

  /** Withdraw a loaded table. Its rates go with it, and every estimate on that district changes. */
  async withdrawRateTable(id: string): Promise<void> {
    try {
      await api.deleteRateTable(id);
      await actions.refreshRateTables();
    } catch (err) {
      store.set({
        ratesFaults: [err instanceof Error ? err.message : 'The rate table could not be withdrawn.'],
      });
    }
  },

  /* --------------------------- the wording of recommended work --------------------------- */

  /** Open the recommended-work dialog, rewording `edit` straight away when given. */
  openTreatments(edit?: TreatmentEdit): void {
    store.set({ treatmentsOpen: true, treatmentEdit: edit ?? null, treatmentError: null });
  },

  closeTreatments(): void {
    if (store.get().treatmentSaving) return;
    store.set({ treatmentsOpen: false, treatmentEdit: null, treatmentError: null });
  },

  editTreatment(edit: TreatmentEdit | null): void {
    store.set({ treatmentEdit: edit, treatmentError: null });
  },

  setTreatmentField(key: 'label' | 'note', value: string): void {
    store.set((s) => (s.treatmentEdit ? { treatmentEdit: { ...s.treatmentEdit, [key]: value } } : {}));
  },

  async saveTreatment(): Promise<void> {
    const s = store.get();
    const edit = s.treatmentEdit;
    if (!edit || s.treatmentSaving) return;
    if (!edit.label.trim()) {
      store.set({ treatmentError: 'The work item cannot be empty.' });
      return;
    }
    store.set({ treatmentSaving: true, treatmentError: null });
    try {
      const wording = await api.putTreatmentWording({ treatment: edit.id, label: edit.label, note: edit.note });
      store.set({ treatmentWording: wording, treatmentSaving: false, treatmentEdit: null });
    } catch (err) {
      store.set({
        treatmentSaving: false,
        treatmentError: err instanceof Error ? err.message : 'The wording could not be saved.',
      });
    }
  },

  /** Put a treatment back in its built-in wording. */
  async restoreTreatment(id: string): Promise<void> {
    if (store.get().treatmentSaving) return;
    store.set({ treatmentSaving: true, treatmentError: null });
    try {
      const wording = await api.deleteTreatmentWording(id);
      store.set((s) => ({
        treatmentWording: wording,
        treatmentSaving: false,
        treatmentEdit: s.treatmentEdit?.id === id ? null : s.treatmentEdit,
      }));
    } catch (err) {
      store.set({
        treatmentSaving: false,
        treatmentError: err instanceof Error ? err.message : 'The wording could not be restored.',
      });
    }
  },

  /* --------------------------- history --------------------------- */

  /**
   * Show or hide the record's history, reading it the first time it is opened.
   *
   * Not read on selection: most visits to a record are not about who last
   * touched it, and the log is the one per-structure collection that grows
   * without bound. Fetched when it is actually wanted.
   */
  async toggleChanges(): Promise<void> {
    const open = !store.get().changesOpen;
    store.set({ changesOpen: open });
    if (!open) return;
    await actions.refreshChanges();
  },

  async refreshChanges(): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().backend !== 'live') {
      store.set({ changes: [] });
      return;
    }
    try {
      const changes = await api.fetchChanges(id);
      // Still the same structure? A slow read for one record must not land on
      // another, which is the same guard loadBridgeContext already applies.
      if (store.get().selectedId === id) store.set({ changes });
    } catch {
      if (store.get().selectedId === id) store.set({ changes: [] });
    }
  },

  /* --------------------------- the phase --------------------------- */

  /** Read the selected structure's phase history. Where there is no register, none. */
  async loadPhase(): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().backend !== 'live') {
      store.set({ phaseHistory: [] });
      return;
    }
    /* TEMPORARY — the FWO demonstration's invented structures have no rows, so
       their history is the bundle's; delete with src/data/fwo-demo.ts. A real
       history always wins. */
    const demo = demoPhaseFor(active().id, id);
    try {
      const report = await api.fetchPhase(id);
      if (store.get().selectedId === id) {
        const own = demo.length > 0 && demoOwnsPhase(active().id, id);
        store.set({ phaseHistory: report.history.length && !own ? report.history : demo });
      }
    } catch {
      if (store.get().selectedId === id) store.set({ phaseHistory: demo });
    }
  },

  /**
   * Record the selected structure, already built, as in service (LC-80), from
   * the day or year given, else the year on its record.
   */
  async recordInService(inServiceOn: string): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().phaseBusy) return;
    const when = inServiceOn.trim();
    if (when !== '' && !/^\d{4}(-\d{2}-\d{2})?$/.test(when)) {
      store.set({ phaseNote: 'The in-service date is a day, or the year of completion.' });
      return;
    }
    store.set({ phaseBusy: true, phaseNote: null });
    try {
      const report = await api.recordInService(id, when || null);
      await actions.hydrate();
      if (store.get().selectedId === id) store.set({ phaseHistory: report.history });
      store.set({ phaseBusy: false });
    } catch (err) {
      store.set({
        phaseBusy: false,
        phaseNote: err instanceof Error ? err.message : 'The structure could not be recorded in service.',
      });
    }
  },

  /** Open the Edit state dialog on the selected structure, its fields as the record holds them. */
  openStateEdit(): void {
    const b = findBridge(store.get().selectedId);
    if (b === null) return;
    store.set({
      stateEditOpen: true,
      phaseNote: null,
      stateDraft: {
        inService: b.year != null ? String(b.year) : '',
        months: b.underwaterMonths != null ? String(b.underwaterMonths) : '',
      },
    });
  },

  closeStateEdit(): void {
    if (!store.get().stateEditOpen || store.get().phaseBusy) return;
    store.set({ stateEditOpen: false, stateDraft: {}, phaseNote: null });
  },

  setStateField(key: string, value: string): void {
    store.set((s) => ({ stateDraft: { ...s.stateDraft, [key]: value } }));
  },

  /** Tick or untick a register row for the bulk action. */
  togglePicked(id: string): void {
    store.set((s) => ({
      registerPicked: s.registerPicked.includes(id) ? s.registerPicked.filter((x) => x !== id) : [...s.registerPicked, id],
      registerNote: null,
    }));
  },

  /** Tick every row given, or untick them all where every one is ticked already. */
  pickAll(ids: readonly string[]): void {
    store.set((s) => ({
      registerPicked: ids.every((id) => s.registerPicked.includes(id)) ? [] : [...new Set([...s.registerPicked, ...ids])],
      registerNote: null,
    }));
  },

  /**
   * The register's bulk action (LC-80): every ticked structure recorded in
   * service from its own year of completion, the ones already recorded
   * skipped, and the count of each said.
   */
  async recordPickedInService(): Promise<void> {
    const picked = store.get().registerPicked;
    if (picked.length === 0 || store.get().phaseBusy) return;
    store.set({ phaseBusy: true, registerNote: null });
    try {
      const done = await api.recordManyInService(picked);
      await actions.hydrate();
      store.set({
        phaseBusy: false,
        registerPicked: [],
        registerNote: recordedNote(done.recorded.length, done.skipped.length),
      });
    } catch (err) {
      store.set({
        phaseBusy: false,
        registerNote: err instanceof Error ? err.message : 'The structures could not be recorded in service.',
      });
    }
  },

  /**
   * Restrict, lift, close or reopen the selected structure, with a reason.
   * The register is re-read afterwards, since every chip on the site shows it.
   */
  async decidePhase(act: PhaseAct, reason: string): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().phaseBusy) return;
    if (reason.trim() === '') {
      store.set({ phaseNote: 'A decision needs a reason, recorded with it.' });
      return;
    }
    store.set({ phaseBusy: true, phaseNote: null });
    try {
      const report = await api.decidePhase(id, act, reason.trim());
      await actions.hydrate();
      if (store.get().selectedId === id) store.set({ phaseHistory: report.history });
      store.set((s) => ({ phaseBusy: false, stateDraft: { ...s.stateDraft, act: '', reason: '' } }));
    } catch (err) {
      store.set({
        phaseBusy: false,
        phaseNote: err instanceof Error ? err.message : 'The decision could not be recorded.',
      });
    }
  },

  /* --------------------------- the handover (LC-11) --------------------------- */

  /** Open the selected structure's handover as a dialog. */
  openHandover(): void {
    store.set({ handoverOpen: true, handoverError: null });
  },

  closeHandover(): void {
    if (store.get().handoverBusy) return;
    store.set({ handoverOpen: false, handoverError: null });
  },

  /** Read the selected structure's handover. A failure leaves the last one read. */
  async refreshHandover(): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().backend !== 'live') return;
    /* TEMPORARY — the FWO demonstration's handover, where the register holds
       none; delete with src/data/fwo-demo.ts. */
    const demo = demoHandoverFor(active().id, id);
    try {
      const view = await api.fetchHandover(id);
      if (store.get().selectedId === id) store.set({ handover: view.handover === null && demo ? demo : view });
    } catch {
      /* Nothing to say beyond what the page already shows: not read. */
      if (demo && store.get().selectedId === id) store.set({ handover: demo });
    }
  },

  /**
   * Save the handover as the form holds it, or prepare it. The server derives
   * where each value came from; the form sends only what it shows.
   */
  async saveHandover(body: Parameters<typeof api.saveHandover>[1]): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().handoverBusy) return;
    store.set({ handoverBusy: true, handoverError: null });
    try {
      await api.saveHandover(id, body);
      await actions.refreshHandover();
      store.set({ handoverBusy: false });
    } catch (err) {
      store.set({
        handoverBusy: false,
        handoverError: err instanceof Error ? err.message : 'The handover could not be saved.',
      });
    }
  },

  /** Accept the prepared handover, or return it. Accepting moves the bridge, so the register is read again. */
  async actOnHandover(act: 'accept' | 'return', reason = ''): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().handoverBusy) return;
    if (act === 'return' && reason.trim() === '') {
      store.set({ handoverError: 'Say why the handover is returned.' });
      return;
    }
    store.set({ handoverBusy: true, handoverError: null });
    try {
      await api.actOnHandover(id, act, reason.trim());
      if (act === 'accept') await actions.hydrate();
      await actions.refreshProgress();
      store.set({ handoverBusy: false });
    } catch (err) {
      store.set({
        handoverBusy: false,
        handoverError: err instanceof Error ? err.message : 'The handover could not be moved.',
      });
    }
  },

  /** Set the structure's underwater interval in months, or clear it (LC-60). */
  async setUnderwaterInterval(months: number | null): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().phaseBusy) return;
    if (months !== null && (!Number.isInteger(months) || months < 1 || months > 240)) {
      store.set({ phaseNote: 'An underwater interval is a whole number of months, 1 to 240.' });
      return;
    }
    store.set({ phaseBusy: true, phaseNote: null });
    try {
      await api.putUnderwaterInterval(id, months);
      await actions.hydrate();
      store.set({ phaseBusy: false });
    } catch (err) {
      store.set({
        phaseBusy: false,
        phaseNote: err instanceof Error ? err.message : 'The underwater interval could not be set.',
      });
    }
  },

  /** Make a model the selected structure's model of record (LC-12). */
  async setModelOfRecord(modelId: string): Promise<void> {
    const id = store.get().selectedId;
    if (store.get().phaseBusy) return;
    store.set({ phaseBusy: true, phaseNote: null });
    try {
      await api.putModelOfRecord(id, modelId);
      await actions.hydrate();
      store.set({ phaseBusy: false });
    } catch (err) {
      store.set({
        phaseBusy: false,
        phaseNote: err instanceof Error ? err.message : 'The model of record could not be set.',
      });
    }
  },

  /** Move one inspection through draft -> review -> accepted. */
  async setInspectionState(inspectionId: string, state: InspectionState): Promise<boolean> {
    const id = store.get().selectedId;
    try {
      adopt(await api.setInspectionState(id, inspectionId, state));
      store.set({ refused: null });
      // The transition is itself a logged change, so the panel is stale now.
      if (store.get().changesOpen) await actions.refreshChanges();
      return true;
    } catch (err) {
      /* Left as it was, and said. A state that flipped on screen without
         landing would be worse than one that did not move — this is the field
         an audit reads. */
      return refusedAt(inspectionId, err, 'That inspection could not be moved.');
    }
  },

  /* --------------------------- the offline queue --------------------------- */

  /** Re-read what is waiting. Cheap, and called after anything that changes it. */
  async refreshQueue(): Promise<void> {
    store.set({ queued: await queue.pending() });
  },

  /**
   * Send everything waiting, then re-read the register.
   *
   * Drain first and hydrate second, deliberately: the optimistic copies on
   * screen are replaced by the server's own records, and doing it the other way
   * round would blank a queued inspection off the record for as long as the
   * drain took.
   */
  async syncQueue(): Promise<void> {
    if (store.get().syncing) return;
    if (!queue.queueAvailable()) return;

    store.set({ syncing: true, syncNote: null });

    const result = await queue.drain(async (entry) => {
      try {
        if (entry.kind === 'inspection') {
          await api.addInspection(
            entry.bridgeId,
            entry.body as unknown as Inspection,
            (entry.body['photos'] as string[] | undefined) ?? [],
            entry.clientId,
          );
        } else if (entry.kind === 'progress') {
          await api.createProgress(entry.body as Parameters<typeof api.createProgress>[0]);
        } else if (entry.kind === 'site-return') {
          /* The frozen answer is a SUCCESS for the queue, not a retry. The day
             was accepted before this correction reached the register, so
             nothing will ever be written from it and sending it again would
             jam the queue behind an entry that can never land. The person who
             wrote it is told by the sheet at the time; by the time a queue
             drains, days later, there is nobody to tell. */
          await api.putSiteReturn(entry.body as Parameters<typeof api.putSiteReturn>[0]);
        } else if (entry.kind === 'incident') {
          await api.createIncident(entry.body as Parameters<typeof api.createIncident>[0]);
        } else if (entry.kind === 'site-test') {
          await api.createTest(entry.body as Parameters<typeof api.createTest>[0]);
        } else if (entry.kind === 'site-item') {
          await api.createItem(entry.body as Parameters<typeof api.createItem>[0]);
        } else {
          await api.createDefect(entry.body as Parameters<typeof api.createDefect>[0]);
        }
        return { ok: true } as const;
      } catch (err) {
        const status = err instanceof api.ApiError ? err.status : null;
        return {
          ok: false as const,
          status,
          message: err instanceof Error ? err.message : 'Failed to send.',
        };
      }
    });

    /* A refusal is dropped from the queue rather than retried for ever, so it
       has to be said out loud — an inspection that silently disappeared between
       the riverbank and the register is the worst outcome this whole item has. */
    const note =
      result.rejected.length > 0
        ? `${result.rejected.length} refused by the register and removed from the queue: ${result.rejected[0]?.lastError ?? ''}`
        : result.sent > 0
          ? `${result.sent} synced.`
          : null;

    store.set({ syncing: false, syncNote: note });
    await actions.refreshQueue();
    if (result.sent > 0) await actions.hydrate();
  },

  /** Throw the queue away. Only ever from a deliberate action, and it says what it loses. */
  async discardQueue(): Promise<void> {
    await queue.clear();
    store.set({ syncNote: null });
    await actions.refreshQueue();
  },

  /* --------------------- installing on this device --------------------- */

  /**
   * The browser has changed its mind about installing. Told, not asked.
   *
   * src/core/install.ts owns the events and calls this; nothing here polls. The
   * note is cleared on the way in, because a change of offer makes whatever the
   * last press came to stale — a phone that has since installed the app should
   * not still be reading "Not installed".
   */
  installabilityChanged(next: { canPrompt: boolean; installed: boolean }): void {
    store.set((s) => ({
      canInstall: next.canPrompt,
      installed: next.installed,
      installNote: next.installed && !s.installed ? null : s.installNote,
    }));
  },

  /**
   * Show the browser's install prompt, and say what it came to.
   *
   * Every branch writes a note, including the one nobody expects. A button that
   * did nothing visible would be indistinguishable from a broken one, and this
   * is a button pressed once, on a phone, by somebody who will not press it
   * again if the first press appeared to fail.
   *
   * `canInstall` goes false regardless: the captured event is spent by the
   * attempt, whatever the answer. Only a reload brings a fresh one, which is
   * what the dismissed note says out loud rather than leaving somebody to guess.
   */
  async promptInstall(): Promise<void> {
    if (!store.get().canInstall) return;

    const outcome = await showInstallPrompt();
    store.set({
      canInstall: false,
      installNote:
        outcome === 'accepted'
          ? 'Installed. Open it from the home-screen icon from now on — it runs full screen, and opens with no signal.'
          : outcome === 'dismissed'
            ? 'Not installed. The browser offers this once per visit: reload the page to be asked again.'
            : 'This browser did not show the prompt. Use its own menu instead — the steps are below.',
    });
  },

  /* --------------------------- bulk import --------------------------- */

  openImport(): void {
    store.set({ importOpen: true, importCsv: '', importPlan: null, importError: null, importBusy: false });
  },

  closeImport(): void {
    if (!store.get().importOpen) return;
    store.set({ importOpen: false, importBusy: false });
  },

  /** New text means the plan on screen is about a different file. */
  setImportCsv(csv: string): void {
    store.set({ importCsv: csv, importPlan: null, importError: null });
  },

  /**
   * Ask what would happen, or make it happen.
   *
   * Dry run and apply are the same call with one flag, so the report cannot
   * describe something other than what the apply does.
   */
  async runImport(apply: boolean): Promise<void> {
    const csv = store.get().importCsv.trim();
    if (csv === '') {
      store.set({ importError: 'Paste a CSV, or choose a file.' });
      return;
    }
    store.set({ importBusy: true, importError: null });
    try {
      const plan = await api.importCsv(csv, apply);
      store.set({ importPlan: plan, importBusy: false });
      if (apply) {
        // The register has changed underneath every screen, so re-read it
        // rather than patching rows in: an import can touch hundreds at once.
        await actions.hydrate();
      }
    } catch (err) {
      store.set({
        importBusy: false,
        importError: err instanceof Error ? err.message : 'The import could not be read.',
      });
    }
  },

  /* --------------------------- logging a defect --------------------------- */

  openLogDefect(bridgeId: string, seed: Partial<DefectDraft> = {}): void {
    const draft = { ...emptyDefectDraft(), ...seed };
    const roots = store.get().heroComponentTree ?? [];
    store.set({
      defectBridgeId: bridgeId,
      /* Opened to the top level, and down to whatever the draft already names.
         A picker that hides the current answer invites somebody to pick a
         different one by accident. */
      defectTreeOpen: pathsToReveal(roots, draft.component),
      defectTreeFilter: '',
      // A menu left open behind the dialog would take its Escape — see src/main.ts.
      menu: null,
      defectDraft: draft,
      defectSaving: false,
      defectError: null,
    });
  },

  closeLogDefect(): void {
    if (store.get().defectBridgeId === null) return;
    store.set({ defectBridgeId: null, defectSaving: false, defectError: null });
  },

  /**
   * Fold or unfold one node of the picker.
   *
   * Not persisted and reset every time the dialog opens, unlike the card's own
   * folding: where you left a tree an hour ago says nothing about the defect you
   * are logging now, and the seed above is a better starting point than a stale
   * one.
   */
  toggleDefectTreeNode(path: string): void {
    store.set((s) => ({
      defectTreeOpen: s.defectTreeOpen.includes(path)
        ? s.defectTreeOpen.filter((p) => p !== path)
        : [...s.defectTreeOpen, path],
    }));
  },

  setDefectTreeFilter(query: string): void {
    store.set({ defectTreeFilter: query });
  },

  setDefectField(key: keyof DefectDraft, value: string): void {
    store.set((s) => {
      /* A point clicked on one component is not a point on another. Choosing a
         different component in the dialog after placing on the model keeps the
         choice and drops the pin, rather than filing a marker on the pier under
         the name of the deck. */
      const moved = key === 'component' && value !== s.defectDraft.component;
      return {
        defectDraft: {
          ...s.defectDraft,
          [key]: value,
          /* A different label is a different part, so the id it had goes too. */
          ...(moved ? { pin: null, normal: null, componentId: '' } : {}),
        },
        /* The find box has done its job once something is picked, and "Change"
           should come back to the tree rather than to the last search. */
        ...(moved ? { defectTreeFilter: '' } : {}),
      };
    });
  },

  /**
   * Choose the component from the picker: its label and the model's id for it
   * together (LC-14), so two parts of one name stay two answers.
   */
  setDefectComponent(component: string, componentId: string | null): void {
    const was = store.get().defectDraft;
    const id = component ? (componentId ?? '') : '';
    actions.setDefectField('component', component);
    /* The same name on another part is another part: its pin goes, as a moved
       label's does in `setDefectField`. */
    const other = was.component === component && was.componentId !== id;
    store.set((s) => ({
      defectDraft: { ...s.defectDraft, componentId: id, ...(other ? { pin: null, normal: null } : {}) },
    }));
  },

  /** Forget where on the model the draft was placed, keeping everything else. */
  clearDefectPin(): void {
    store.set((s) => ({ defectDraft: { ...s.defectDraft, pin: null, normal: null } }));
  },

  /**
   * Attach a photograph to the draft, after the ones already on it.
   *
   * Separate from setDefectField because the value is a list, not a string.
   * Ignored past MAX_DEFECT_PHOTOS rather than refused: the dialog stops offering
   * the Add tile at the cap, so reaching here past it is a double-tap.
   *
   * The downscale happens in the dialog, before this is called, for the same
   * reason it does on the inspection form — that is where a file the browser
   * cannot decode can be reported against the file the person just chose.
   */
  addDefectPhoto(dataUrl: string): void {
    store.set((s) => {
      if (s.defectDraft.photos.length >= MAX_DEFECT_PHOTOS) return {};
      return { defectDraft: { ...s.defectDraft, photos: [...s.defectDraft.photos, dataUrl] } };
    });
  },

  removeDefectPhoto(index: number): void {
    store.set((s) => ({
      defectDraft: { ...s.defectDraft, photos: s.defectDraft.photos.filter((_, i) => i !== index) },
    }));
  },

  /**
   * Type one measurement into the draft. An empty string clears that box.
   *
   * Keyed by `QuantityKey`, so the box, the column and the cost engine all name
   * the dimension the same way. Switching category deliberately does NOT clear
   * the map — see the note on `DefectDraft.quantity`.
   */
  setDefectQuantity(key: string, value: string): void {
    store.set((s) => ({
      defectDraft: { ...s.defectDraft, quantity: { ...s.defectDraft.quantity, [key]: value } },
    }));
  },

  /**
   * Report a problem with something the defect dialog did on its own — today, a
   * picked file the browser could not decode. Shares the banner the save path
   * uses, because to the person looking at it there is one place errors appear.
   */
  setDefectError(message: string): void {
    store.set({ defectError: message });
  },

  async saveDefect(): Promise<void> {
    const bridgeId = store.get().defectBridgeId;
    if (bridgeId === null || store.get().defectSaving) return;

    const d = store.get().defectDraft;
    if (!ISO_DATE.test(d.loggedAt.trim())) {
      store.set({ defectError: '"Seen on" is required.' });
      return;
    }

    /* Built and refused BEFORE anything is sent or queued, so a measurement the
       register would reject is a message in this dialog rather than a queue
       entry that fails silently on a riverbank tomorrow. */
    let quantity: DefectQuantity | null;
    try {
      quantity = buildDefectQuantity(d);
    } catch (err) {
      store.set({ defectError: err instanceof Error ? err.message : 'Invalid measurement.' });
      return;
    }

    store.set({ defectSaving: true, defectError: null });
    /* Minted before the attempt so the same id is used whether this lands now or
       from the queue later — which is what stops a retry making a second defect. */
    const clientId = crypto.randomUUID();
    const payload = {
      bridgeId,
      clientId,
      code: d.code,
      severity: d.severity,
      loggedAt: d.loggedAt.trim(),
      component: d.component.trim() || null,
      ...(d.component.trim() && d.componentId ? { componentId: d.componentId } : {}),
      note: d.note.trim(),
      ...(d.inspectionId ? { inspectionId: d.inspectionId } : {}),
      /* Only when there are any, so an ordinary defect's POST — and the queue
         entry behind it — stays the few hundred bytes it has always been. */
      ...(d.photos.length ? { images: d.photos } : {}),
      /* Same rule for the measurement. A defect nobody measured goes up exactly
         as it did before this form had the boxes. */
      ...(quantity ? { quantity } : {}),
      /* The point, when it was placed on the 3D view. Omitted otherwise, so a
         defect chosen from the list is not given a location nobody clicked. */
      ...(d.pin
        ? {
            pin: [d.pin.x, d.pin.y, d.pin.z] as const,
            ...(d.normal ? { normal: [d.normal.x, d.normal.y, d.normal.z] as const } : {}),
          }
        : {}),
    };

    try {
      await api.createDefect(payload);
      await actions.refreshAllDefects();
      if (store.get().defectBridgeId === bridgeId) {
        store.set({ defectBridgeId: null, defectSaving: false, defectError: null });
      }
    } catch (err) {
      const unreachable = err instanceof api.ApiError && err.status === null;

      if (unreachable && queue.queueAvailable()) {
        await queue.enqueue({ kind: 'defect', bridgeId, clientId, body: payload });

        // On screen immediately, as an ordinary defect on this structure. It
        // will be replaced by the register's own copy when the queue drains.
        store.set((st) => ({
          registerDefects: [
            {
              id: `pending-${clientId}`,
              clientId,
              bridgeId,
              element: null,
              component: payload.component,
              /* No handle yet — the server allocates one when the queue drains.
                 Zero is never a real handle: minted ones ascend from 1 and the
                 modeller's own are negative. */
              modelDefectId: 0,
              code: payload.code,
              severity: payload.severity,
              status: 'Open',
              note: payload.note,
              loggedAt: payload.loggedAt,
              author: null,
              /* Pinned where it was placed on the 3D view, so the marker is on the
                 model while the entry waits in the queue; null for a defect
                 typed into the dialog rather than placed on the geometry. */
              pin: d.pin,
              normal: d.pin ? d.normal : null,
              /* FALSE EVEN WHEN ONE WAS ATTACHED, because `hasPhoto` means "the
                 register is holding a photograph this row can be asked for", and
                 it is not — the bytes are in the queue on this device. Saying true
                 would draw a tile pointing at /api/modeler/defect-photo for a
                 defect the register has never heard of, which is a 404 dressed up
                 as evidence. It becomes true when the queue drains and the
                 register's own copy replaces this row. */
              hasPhoto: false,
              /* Unlike the photograph, the measurement IS on this row: it is a
                 few numbers already in hand, not bytes sitting in a queue, and
                 the record showing "2.4 m² · estimated" against a defect logged
                 out of signal is true the moment it is typed. */
              quantity,
            },
            ...st.registerDefects,
          ],
        }));

        await actions.refreshQueue();
        if (store.get().defectBridgeId === bridgeId) {
          store.set({ defectBridgeId: null, defectSaving: false, defectError: null });
        }
        return;
      }

      if (store.get().defectBridgeId === bridgeId) {
        store.set({
          defectSaving: false,
          defectError: err instanceof Error ? err.message : 'The issue could not be recorded.',
        });
      }
    }
  },

  /** Move a defect along. Re-reads rather than patching in place, so the list stays server-ordered. */
  async setDefectStatus(id: string, status: string): Promise<boolean> {
    try {
      await api.updateDefectStatus(id, status);
      store.set({ refused: null });
      await actions.refreshAllDefects();
      return true;
    } catch (err) {
      /* Left as it was, and said: a status that silently flipped on screen
         without landing would be worse than one that did not move. */
      return refusedAt(id, err, 'That issue could not be moved.');
    }
  },

  /* --------------------------- removing a model --------------------------- */

  /**
   * Ask before removing what is saved against a structure — see
   * src/modeler/deleteDialog.ts.
   *
   * Confirmed rather than done on the click, because there is nothing behind it:
   * the geometry, the defect pins logged on it and the photographs attached to
   * those pins all go, and no other copy is kept.
   */
  openModelDelete(bridgeId: string): void {
    store.set({ modelDeleteBridgeId: bridgeId, modelDeleteError: null });
  },

  /** Dismiss it — refused mid-delete, when the dialog is the only progress there is. */
  closeModelDelete(): void {
    if (store.get().modelDeleteBusy) return;
    store.set({ modelDeleteBridgeId: null, modelDeleteError: null });
  },

  /** Delete one saved model, then re-read the index. */
  deleteModel(modelId: string): Promise<void> {
    return removeModels(store.get().selectedId, [modelId]);
  },

  /**
   * Clear a structure's models.
   *
   * It used to take a `thenNew` flag and open the template picker afterwards, on
   * the reasoning that an engineer replacing a model wants to be asked which
   * bridge type next. There is no next: a model is only ever imported, so the
   * record's own "Import 3D model" is the whole of what follows a delete, and it
   * creates the model it lands in.
   */
  deleteAllModels(bridgeId: string): Promise<void> {
    return removeModels(
      bridgeId,
      store.get().models.models.map((m) => m.id),
    );
  },
};

/**
 * Remove models one at a time, then re-read the index and report what is left.
 *
 * In series rather than in parallel: each delete is several D1 and R2 writes
 * (state, still, rates, carbon, and a rewrite of the list that names them all),
 * and two of them rewriting `bim_modeler_saved_models` at once would race — the
 * second read of the list would not see the first's edit, and one of the two
 * models would come back.
 *
 * A failure stops nothing that follows it and closes nothing: the dialog stays
 * up, the index says what actually went, and the message names how many did not.
 * The dialog closes only when the structure is genuinely clear.
 */
async function removeModels(bridgeId: string, ids: string[]): Promise<void> {
  if (ids.length === 0 || store.get().modelDeleteBusy) return;
  store.set({ modelDeleteBusy: true, modelDeleteError: null });

  let failed = 0;
  let lastError = 'The model could not be deleted.';
  for (const id of ids) {
    try {
      await api.deleteModel(bridgeId, id);
    } catch (err) {
      failed++;
      if (err instanceof Error) lastError = err.message;
    }
  }

  await actions.refreshModels();
  store.set({
    modelDeleteBusy: false,
    modelDeleteError:
      failed === 0 ? null : `${failed} of ${ids.length} could not be deleted — ${lastError}`,
  });
  if (failed > 0) return;

  // Nothing left to confirm. Deleting one model of three leaves the dialog open
  // on the other two, which is where the engineer already was.
  if (store.get().models.models.length > 0) return;
  store.set({ modelDeleteBridgeId: null });
}

/* --------------------------- the session, watched --------------------------- */

/**
 * The one subscriber in the register app.
 *
 * AT MODULE SCOPE, AND NOT IN src/main.ts, which is where it was written first.
 * That file is an entry point: no check can import it, so the wiring was
 * unverifiable — and it was wrong. The check that was supposed to cover it drove
 * a refused request, watched `session` stay `ok`, and passed anyway, because it
 * had been written as an unawaited `checkAsync` whose assertions resolved after
 * the summary had printed. Two mistakes that hid each other, and the same pair
 * the 2026-08-20 entry in ROADMAP's Decisions describes.
 *
 * The layering allows it: this module already imports src/core/api.ts, and that
 * module deliberately imports nothing from here — which is why the announcement
 * is a hook in the first place. See onSessionLost there.
 */
api.onSessionLost((lost) => actions.sessionLost(lost));
